import { Excel } from "antd-table-saveas-excel";
import moment from "moment";
import {
  calculatePercentages,
  getDefaultDuration,
  getPaneIndex,
  getWindowLocationPath,
  kFormatter,
} from "../../../../redux/constants";
import { setHighchartWordCloudShape } from "./highcharts.options";
import { useDispatch as dispatch } from "react-redux";
import { set_WM_DeepDive } from "../../../../redux/actions/WidgetMaker/WidgetMaker";
import { getCustomizedDate } from "../../../../redux/constants";
import { SocialMedialogo } from "../../../layout/DeepDive/DeepDivetab/SocialMediaPlatforms";
import checkMark from "../../../../assets/WidgetMaker/checkmark_filled_icon.svg";
import closeMark from "../../../../assets/WidgetMaker/close_filled_icon.svg";
import aiStars from "../../../../assets/WidgetMaker/ai_stars-icon.svg";
import { isArray } from "highcharts";
import { getCountryLogo, getGenderLogo } from "../../Logos";
import { HeatMapEnum } from "../../../../redux/constants/enums";

// Helper function to format percent change for treemap
const formatPercentChange = (percentChange) => {
  if (percentChange === null || percentChange === undefined) {
    return null;
  }

  if (percentChange === 0) {
    return {
      value: "NA",
      className: "percenter__change-na",
      icon: "",
    };
  }

  const formattedValue = Math.abs(percentChange).toLocaleString() + "%";

  if (percentChange > 0) {
    return {
      value: formattedValue,
      className: "percenter__change-positive",
      icon: "▲",
    };
  } else {
    return {
      value: formattedValue,
      className: "percenter__change-negative",
      icon: "▼",
    };
  }
};

// If magnitude value in positive then it will calculate lighter color
// else calculate darker color
const calculateNewShadeOfColor = (hexColor, magnitude) => {
  hexColor = hexColor?.replace(`#`, ``);
  if (hexColor?.length < 6) {
    for (let i = hexColor?.length; i < 6; i++) {
      hexColor += "0";
    }
  }
  if (hexColor?.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
  } else {
    return "#" + hexColor;
  }
};
const newColorShadeInHex6 = (hexColor, magnitude) => {
  let newColor = calculateNewShadeOfColor(hexColor, magnitude);
  newColor = newColor?.replace(`#`, ``);
  if (newColor?.length < 6) {
    for (let i = newColor?.length; i < 6; i++) {
      newColor += "0";
    }
  }
  return "#" + newColor;
};

// Enhanced color contrast utilities for better treemap visibility
// Inspired by KPICards contrast approach but improved for treemap use cases
// These functions ensure optimal readability for text overlays and percent change values
//
// KEY IMPROVEMENT: Uses point background colors instead of dataLabel backgrounds
// This approach is more reliable as dataLabels may not render properly in some cases
// or during initial rendering. Point colors are always rendered by Highcharts.

const hexToRgb = (hex) => {
  const cleanHex = hex?.replace("#", "");
  if (cleanHex?.length !== 6) return { r: 0, g: 0, b: 0 };

  return {
    r: parseInt(cleanHex.substr(0, 2), 16),
    g: parseInt(cleanHex.substr(2, 2), 16),
    b: parseInt(cleanHex.substr(4, 2), 16),
  };
};

const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

// Calculate luminance for WCAG contrast ratio
const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Get contrast ratio between two colors
const getContrastRatio = (color1, color2) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

// Generate optimal background color for treemap cards with better contrast
// This improves upon the simple newColorShadeInHex6 approach by considering luminance
// Returns color that will be used as the point's background color (not dataLabel)
//
// 🎯 ENHANCED COLOR STRATEGY: Stronger, more visible colors for better sentiment visibility
// - Parent nodes: Medium-intensity variation of original color (not too light)
// - Child nodes: Lighter but still visible variation
// - Maintains brand identity while ensuring visibility
const getOptimalTreemapBackground = (
  baseColor,
  isParent = false,
  isDatalabel = false
) => {
  if (!baseColor || baseColor === "#000000") {
    return undefined; // No background for black or undefined colors
  }

  const rgb = hexToRgb(baseColor);
  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);

  // For parent nodes, create a more prominent but readable background
  if (isParent) {
    // STRONGER APPROACH: Less aggressive lightening to maintain color visibility
    const lightnessFactor =
      luminance > 0.5 ? (isDatalabel ? 0.8 : 0.6) : isDatalabel ? 0.6 : 0.4; // Reduced from 0.9/0.15 for stronger colors

    return rgbToHex(
      Math.min(
        255,
        Math.max(0, Math.round(rgb.r + (255 - rgb.r) * lightnessFactor))
      ),
      Math.min(
        255,
        Math.max(0, Math.round(rgb.g + (255 - rgb.g) * lightnessFactor))
      ),
      Math.min(
        255,
        Math.max(0, Math.round(rgb.b + (255 - rgb.b) * lightnessFactor))
      )
    );
  } else {
    // For child nodes, create a lighter but still visible background
    const alpha = 0.4; // Increased from 0.08 for better visibility
    return rgbToHex(
      Math.round(255 - (255 - rgb.r) * alpha),
      Math.round(255 - (255 - rgb.g) * alpha),
      Math.round(255 - (255 - rgb.b) * alpha)
    );
  }
};

// Get optimal text color for maximum contrast
// Ensures percent changes and other text overlays are always readable
// This works with the point background color, not dataLabel background
//
// 🎯 NOTE: User requested NOT to change dataLabel text color
// This function is for reference but text color should remain as user-defined
const getOptimalTextColor = (backgroundColor) => {
  if (!backgroundColor) return "#333333";

  const rgb = hexToRgb(backgroundColor);
  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);

  // Use white text for dark backgrounds, dark text for light backgrounds
  // Added additional contrast checking for edge cases
  const contrastWithWhite = getContrastRatio(backgroundColor, "#ffffff");
  const contrastWithDark = getContrastRatio(backgroundColor, "#333333");

  // Choose the color with better contrast ratio
  if (contrastWithWhite >= contrastWithDark) {
    return "#ffffff";
  } else {
    return "#333333";
  }
};

// Get optimal border color that provides subtle but visible definition
// Creates clear boundaries without overwhelming the design
//
// 🎯 ENHANCED BORDER STRATEGY: More prominent borders for better definition
const getOptimalBorderColor = (baseColor, isParent = false) => {
  if (!baseColor || baseColor === "#000000") {
    return baseColor;
  }

  const rgb = hexToRgb(baseColor);
  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);

  if (isParent) {
    // STRONGER BORDERS: More prominent borders for parent nodes
    const factor = luminance > 0.5 ? 0.5 : 0.2; // Increased contrast from 0.7/0.3
    return rgbToHex(
      Math.round(rgb.r * factor),
      Math.round(rgb.g * factor),
      Math.round(rgb.b * factor)
    );
  } else {
    // Visible borders for child nodes
    const factor = luminance > 0.5 ? 0.75 : 0.5; // Increased contrast from 0.85/0.6
    return rgbToHex(
      Math.round(rgb.r * factor),
      Math.round(rgb.g * factor),
      Math.round(rgb.b * factor)
    );
  }
};
const globalChartColor = [
  "#6060FF",
  "#9D48FF",
  "#FF00FF",
  "#DFAAAA",
  "#29CFCF",
  "#FF7D6C",
  "#FF7D6C",
  "#CD97FF",
  "#FFD58B",
  "#75FF75",
  "#FFC0CB",
  "#C66060",
  "#A5A5DC",
  "#00A669",
  "#00A669",
  "#9898FF",
  "#EEEE52",
  "#FF83F7",
  "#3FE7AC",
  "#B600AA",
  "#39A7E4",
  "#6600DA",
  "#FFB940",
  "#FF4040",
  "#24E224",
  "#FFDB4D",
  "#CE9ECE",
  "#1C9F00",
  "#FFC1B9",
  "#FF9C73",
  "#5656B2",
  "#FF6E31",
  "#9FE9E9",
];
let selectedColorArray = globalChartColor;
const getPointCategoryName = (point, dimension) => {
  var series = point?.series,
    isY = dimension === "y",
    axis = series[isY ? "yAxis" : "xAxis"];
  return axis?.categories[point[isY ? "y" : "x"]];
};
const getTotalCount = (array) => {
  let totalCount = 0;
  array?.forEach((data) => {
    totalCount = totalCount + data;
  });
  return totalCount;
};
function formatInCommaSeprated(num) {
  return ("" + num)?.replace(
    /(\d)(?=(?:\d{3})+(?:\.|$))|(\.\d\d?)\d*$/g,
    function (m, s1, s2) {
      return s2 || s1 + ",";
    }
  );
}
const handleDeepDive = (event) => {
  dispatch(set_WM_DeepDive(true));
  alert("hello");
};

const getNumericFormatterLabel = (value) => {
  let numeric = ["K", "M", "G", "T", "P", "E"],
    i = numeric?.length,
    undefined,
    ret;
  while (i-- && ret === undefined) {
    let multi = Math.pow(1000, i + 1);
    if (value >= multi && numeric[i] !== null) {
      //ret = Highcharts.numberFormat(this.y / multi, -1) + numeric[i];
      ret = Math.floor(value / multi) + numeric[i];
    }
  }
  if (ret == undefined) ret = value;
  return ret;
};
const dynamicHighchartTextColor = (
  authParams,
  isDeepDiveView = false,
  isPdfView = false,
  isDeepDiveDashboardView = false
) => {
  let cssVar = "";
  if (isPdfView) {
    cssVar = "var(--locobuzz-primary-text-color)";
  } else if (authParams?.screen_id && !isPdfView && isDeepDiveDashboardView) {
    cssVar = "var(--locobuzz-secondary-text-color)";
  } else if (authParams?.screen_id && !isPdfView && !isDeepDiveDashboardView) {
    if (isDeepDiveView) {
      cssVar = "var(--locobuzz-secondary-text-color)";
    } else {
      cssVar = "var(--locobuzz-secondary-text-color)";
    }
  } else if (authParams?.theme_type == 1 && !isPdfView) {
    cssVar = "var(--locobuzz-wm-chart-text--color-dark)";
  } else {
    cssVar = "var(--locobuzz-wm-chart-text--color)";
  }
  return cssVar;
};
const dynamicHighchartOutlineColor = (
  authParams,
  isDeepDiveView = false,
  isPdfView = false,
  isDeepDiveDashboardView = false
) => {
  let cssVar = "";
  if (isPdfView) {
    cssVar = "var(--locobuzz-primary-bg-color)";
  } else if (authParams?.screen_id && !isPdfView && isDeepDiveDashboardView) {
    cssVar = "var(--locobuzz-cc-widget-content-bg-color)";
  } else if (authParams?.screen_id && !isPdfView && !isDeepDiveDashboardView) {
    if (isDeepDiveView) {
      cssVar = "var(--locobuzz-cc-widget-content-bg-color)";
    } else {
      cssVar = "var(--locobuzz-cc-widget-content-bg-color)";
    }
  } else if (authParams?.theme_type == 1 && !isPdfView) {
    cssVar = "var(--locobuzz-primary-bg-color)";
  } else {
    cssVar = "var(--locobuzz-primary-bg-color)";
  }
  return cssVar;
};
export const SecondstoHumanReadable = (seconds, inShortForm, level) => {
  if (typeof seconds !== "number") {
    const cleanedSeconds = seconds.toString().replace(/[^0-9.]/g, "");
    seconds = Number(cleanedSeconds);
  }
  if (
    seconds == null ||
    seconds == undefined ||
    seconds == "" ||
    seconds == "0"
  ) {
    seconds = 0;
  }
  var mobject = moment.duration(seconds, "seconds");
  let asYears = parseInt(mobject?.years());
  let asMonth = parseInt(mobject?.months());
  let asDays = parseInt(mobject?.asDays());
  let final_duration;
  let duration_array = [];
  //var asHours = parseInt(mobject.asHours());
  //var asMinutes = parseInt(mobject.asMinutes());
  //var asSeconds = parseInt(mobject.asSeconds());
  if (!inShortForm) {
    if (asYears > 0) {
      final_duration =
        asYears +
        (asYears > 1 ? " Years, " : " Year, ") +
        mobject?.months() +
        (mobject?.months() > 1 ? " months, " : " month, ") +
        mobject?.days() +
        (mobject?.days() > 1 ? " days, " : " day, ") +
        mobject?.hours() +
        (mobject?.hours() > 1 ? " hrs, " : " hr, ") +
        mobject?.minutes() +
        (mobject?.minutes() > 1 ? " mins, " : " min, ") +
        mobject?.seconds() +
        (mobject?.seconds() > 1 ? " secs" : " sec");
    } else if (asMonth > 0) {
      final_duration =
        asMonth +
        (asMonth > 1 ? " Months, " : " Month, ") +
        mobject?.days() +
        (mobject?.days() > 1 ? " days, " : " day, ") +
        mobject?.hours() +
        (mobject?.hours() > 1 ? " hrs, " : " hr, ") +
        mobject?.minutes() +
        (mobject?.minutes() > 1 ? " mins, " : " min, ") +
        mobject?.seconds() +
        (mobject?.seconds() > 1 ? " secs" : " sec");
    } else if (asDays > 0) {
      final_duration =
        asDays +
        (asDays > 1 ? " Days, " : " Day, ") +
        mobject?.hours() +
        (mobject?.hours() > 1 ? " hrs, " : " hr, ") +
        mobject?.minutes() +
        (mobject?.minutes() > 1 ? " mins, " : " min, ") +
        mobject?.seconds() +
        (mobject?.seconds() > 1 ? " secs" : " sec");
    } else if (mobject.hours() > 0) {
      final_duration =
        mobject?.hours() +
        (mobject?.hours() > 1 ? " hrs, " : " hr, ") +
        mobject?.minutes() +
        (mobject?.minutes() > 1 ? " mins, " : " min, ") +
        mobject?.seconds() +
        (mobject?.seconds() > 1 ? " secs" : " sec");

      //mobject.hours + " hrs, " + mobject.minutes + " mins, " + mobject.seconds + " sec";
    } else if (mobject?.minutes() > 0) {
      final_duration =
        mobject?.minutes() +
        (mobject?.minutes() > 1 ? " mins, " : " min, ") +
        mobject?.seconds() +
        (mobject?.seconds() > 1 ? " secs" : " sec");

      //mobject.minutes + " mins, " + mobject.seconds + " sec";
    } else if (mobject?.seconds() > 0) {
      if (mobject?.seconds() > 1) {
        final_duration = mobject?.seconds() + " secs";
      } else {
        final_duration = mobject?.seconds() + " sec";
      }
    } else {
      final_duration = "0 sec";
    }
    if (level) duration_array = final_duration?.split(",");
    if (duration_array?.length > level) {
      duration_array.length = level;
      final_duration = duration_array?.join();
    }

    return final_duration;
  } else {
    if (asYears > 0) {
      final_duration =
        asYears + "Y, " + mobject?.months() + "M, " + mobject?.days() + "D";
      // return (
      //   asYears + "Y, " + mobject?.months() + "M, " + mobject?.days() + "D"
      // );
    } else if (asMonth > 0) {
      final_duration =
        asMonth + "M, " + mobject?.days() + "D, " + mobject?.hours() + "H";
      // return asMonth + "M, " + mobject?.days() + "D, " + mobject?.hours() + "H";
    } else if (asDays > 0) {
      final_duration =
        asDays + "D, " + mobject?.hours() + "H, " + mobject?.minutes() + "m";
      // return (
      //   asDays + "D, " + mobject?.hours() + "H, " + mobject?.minutes() + "m"
      // );
    } else if (mobject?.hours() > 0) {
      final_duration =
        mobject?.hours() +
        "H, " +
        mobject?.minutes() +
        "m, " +
        mobject?.seconds() +
        "s";
      // return (
      //   mobject?.hours() +
      //   "H, " +
      //   mobject?.minutes() +
      //   "m, " +
      //   mobject?.seconds() +
      //   "s"
      // ); //mobject.hours + " hrs, " + mobject.minutes + " mins, " + mobject.seconds + " sec";
    } else if (mobject?.minutes() > 0) {
      final_duration = mobject?.minutes() + "m, " + mobject?.seconds() + "s";
      //return mobject?.minutes() + "m, " + mobject?.seconds() + "s"; //mobject.minutes + " mins, " + mobject.seconds + " sec";
    } else if (mobject?.seconds() > 0) {
      if (mobject?.seconds() > 1) {
        final_duration = mobject?.seconds() + " s";
        //return mobject?.seconds() + " s";
      } else {
        final_duration = mobject?.seconds() + " s";
        //return mobject?.seconds() + " s";
      }
    } else {
      final_duration = "0 s";
      //return "0 s";
    }
    if (level) duration_array = final_duration?.split(",");
    if (duration_array?.length > level) {
      duration_array.length = level;
      final_duration = duration_array?.join();
    }
    return final_duration;
  }
};

const handleDateFormat = (
  panes,
  index,
  section_index,
  widget_index,
  metadata,
  metadata_xaxis,
  xaxis,
  metadata_date_part,
  single,
  section,
  template_widget,
  shareTemplateData,
  pdfStatus,
  isScheduleReportOpen,
  screen,
  templateDetails,
  isDeepDiveOpen,
  dateAggregationOfThisGraph,
  deepdiveObject,
  yAxesDateAttr = null,
  isDeepDiveDashboardView,
  deepdiveDashboardData,
  deepdiveDashboardTemplateData
) => {
  let dateFormat;
  if (isDeepDiveDashboardView) {
    dateFormat = yAxesDateAttr
      ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
        ? yAxesDateAttr?.date_part
        : yAxesDateAttr?.date_aggregation
      : metadata?.x_axis?.date_part && metadata?.x_axis?.date_part !== "D"
      ? metadata?.x_axis?.date_part
      : metadata?.x_axis?.date_aggregation;
  } else if (panes?.[index]?.is_widget_maker_open_obj?.open_flag) {
    dateFormat = yAxesDateAttr
      ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
        ? yAxesDateAttr?.date_part
        : yAxesDateAttr?.date_aggregation
      : metadata?.x_axis?.date_part && metadata?.x_axis?.date_part !== "D"
      ? metadata?.x_axis?.date_part
      : metadata?.x_axis?.date_aggregation;
  } else if (
    getWindowLocationPath()?.toLowerCase() === "widgetmaker"?.toLowerCase() ||
    getWindowLocationPath()?.toLowerCase() === "edit-widget"?.toLowerCase()
  ) {
    dateFormat = yAxesDateAttr
      ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
        ? yAxesDateAttr?.date_part
        : yAxesDateAttr?.date_aggregation
      : metadata?.x_axis?.date_part && metadata?.x_axis?.date_part !== "D"
      ? metadata?.x_axis?.date_part
      : metadata?.x_axis?.date_aggregation;
  } else if (
    isDeepDiveOpen &&
    getWindowLocationPath()?.toLowerCase() === "CommandCenter"?.toLowerCase()
  ) {
    dateFormat = dateAggregationOfThisGraph;
  } else if (isScheduleReportOpen) {
    dateFormat = yAxesDateAttr
      ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
        ? yAxesDateAttr?.date_part
        : yAxesDateAttr?.date_aggregation
      : metadata?.x_axis?.date_part && metadata?.x_axis?.date_part !== "D"
      ? metadata?.x_axis?.date_part
      : metadata?.x_axis?.date_aggregation;
  } else if (pdfStatus) {
    dateFormat = yAxesDateAttr
      ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
        ? yAxesDateAttr?.date_part
        : yAxesDateAttr?.date_aggregation
      : metadata?.x_axis?.date_part && metadata?.x_axis?.date_part !== "D"
      ? metadata?.x_axis?.date_part
      : metadata?.x_axis?.date_aggregation;
  } else if (template_widget) {
    if (section_index >= 0) {
      dateFormat = yAxesDateAttr
        ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
          ? yAxesDateAttr?.date_part
          : yAxesDateAttr?.date_aggregation
        : section &&
          section[section_index]?.data &&
          section[section_index]?.data[widget_index]?.x_axis?.date_aggregation;
    } else {
      dateFormat = yAxesDateAttr
        ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
          ? yAxesDateAttr?.date_part
          : yAxesDateAttr?.date_aggregation
        : section &&
          section[0]?.data &&
          section[0]?.data[widget_index]?.x_axis?.date_aggregation;
    }
  } else if (shareTemplateData) {
    if (section_index >= 0) {
      dateFormat = yAxesDateAttr
        ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
          ? yAxesDateAttr?.date_part
          : yAxesDateAttr?.date_aggregation
        : shareTemplateData?.sections &&
          shareTemplateData?.sections[section_index]?.widgets &&
          shareTemplateData?.sections[section_index]?.widgets[widget_index]
            ?.x_axis?.date_aggregation;
    } else {
      dateFormat = yAxesDateAttr
        ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
          ? yAxesDateAttr?.date_part
          : yAxesDateAttr?.date_aggregation
        : shareTemplateData?.section &&
          shareTemplateData?.section[0]?.widgets &&
          shareTemplateData?.section[0]?.widgets[widget_index]?.x_axis
            ?.date_aggregation;
    }
  } else {
    if (
      (window.location.pathname.split("/")?.[1] === "dashboard" ||
        window.location.pathname.split("/")?.[1] === "pin-dashboard") &&
      section_index >= 0
    ) {
      dateFormat = yAxesDateAttr
        ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
          ? yAxesDateAttr?.date_part
          : yAxesDateAttr?.date_aggregation
        : panes[index]?.sections &&
          panes[index]?.sections[section_index]?.widgets &&
          panes[index]?.sections[section_index]?.widgets[widget_index]?.x_axis
            ?.date_aggregation;
    } else if (
      (window.location.pathname.split("/")?.[1] === "dashboard" ||
        window.location.pathname.split("/")?.[1] === "pin-dashboard") &&
      panes?.[index]?.section
    ) {
      dateFormat = yAxesDateAttr
        ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
          ? yAxesDateAttr?.date_part
          : yAxesDateAttr?.date_aggregation
        : panes[index]?.section &&
          panes[index]?.section[0]?.widgets &&
          panes[index]?.section[0]?.widgets[widget_index]?.x_axis
            ?.date_aggregation;
    } else if (
      getWindowLocationPath()?.toLowerCase() === "CommandCenter"?.toLowerCase()
    ) {
      dateFormat = yAxesDateAttr
        ? yAxesDateAttr?.date_part && yAxesDateAttr?.date_part !== "D"
          ? yAxesDateAttr?.date_part
          : yAxesDateAttr?.date_aggregation
        : screen &&
          screen?.widgets &&
          screen?.widgets[widget_index]?.x_axis?.date_aggregation;
    }
  }
  let finalData;
  if (
    dateFormat === "1h" ||
    dateFormat === "2h" ||
    dateFormat === "3h" ||
    dateFormat === "4h" ||
    dateFormat === "6h" ||
    dateFormat === "8h" ||
    dateFormat === "12h"
  ) {
    let defaultDuration = getDefaultDuration();
    let todayDuration = isDeepDiveDashboardView
      ? deepdiveDashboardData?.duration
        ? deepdiveDashboardData?.duration
        : deepdiveDashboardData?.start_date && deepdiveDashboardData?.end_date
        ? {
            from: deepdiveDashboardData?.start_date,
            to: deepdiveDashboardData?.end_date,
          }
        : null
      : getWindowLocationPath()?.toLowerCase() ===
        "CommandCenter"?.toLowerCase()
      ? isDeepDiveOpen &&
        dateAggregationOfThisGraph &&
        deepdiveObject?.start_date
        ? {
            from: deepdiveObject?.start_date,
            to: deepdiveObject?.end_date,
          }
        : screen?.duration
      : shareTemplateData
      ? shareTemplateData?.duration
        ? shareTemplateData?.duration
        : shareTemplateData?.start_date && shareTemplateData?.end_date
        ? {
            from: shareTemplateData?.start_date,
            to: shareTemplateData?.end_date,
          }
        : null
      : template_widget
      ? templateDetails?.templateDuration
        ? templateDetails?.templateDuration
        : defaultDuration
      : panes?.[index]?.duration;
    let todaysDate = moment(todayDuration?.from).isSame(
      todayDuration?.to,
      "day"
    );
    if (todaysDate) {
      finalData = single?.map((e) => moment(e)?.format("HH:mm"));
    } else {
      finalData = single?.map((e) => moment(e)?.format("MMM DD YYYY H:mm"));
    }
  } else if (dateFormat === "D") {
    finalData = single?.map((e) => moment(e)?.format("MMM DD YYYY"));
  } else if (
    dateFormat == "ww" ||
    dateFormat == "M" ||
    dateFormat == "hh" ||
    dateFormat == "dd" ||
    dateFormat == "dw" ||
    dateFormat == "mm" ||
    dateFormat == "qq" ||
    dateFormat == "Y" ||
    [
      5,
      10,
      15,
      30,
      60,
      2 * 60,
      4 * 60,
      8 * 60,
      16 * 60,
      24 * 60,
      48 * 60,
    ]?.includes(dateFormat)
  ) {
    finalData = single?.map((e) => e);
  } else {
    finalData = single?.map((e) => moment(e).format("MMM DD YYYY"));
  }
  return finalData;
};

const getNameWithChannelIconIfExists = (orignalName, classes) => {
  // check if  |$| and channel exits replace channel name to channel icon
  if (orignalName?.includes("|$|")) {
    let channelImage = orignalName?.split("|$|")?.[0]?.trim();
    let ProfileName = orignalName?.split("|$|")?.[1]?.trim();
    if (SocialMedialogo?.[channelImage]) {
      return (
        "<span class ='" +
        (classes?.length
          ? classes
          : "d-flex align-items-center w-100  wm-chart-card-title") +
        "' ><img  style='width:" +
        "1.5rem" +
        ";height:" +
        "1.5rem" +
        ";margin-right:" +
        "0.5rem" +
        ";' src='" +
        SocialMedialogo?.[channelImage] +
        "'> <span class ='text-ellipsis text-Darkgray' >" +
        ProfileName +
        "</span> </span>"
      );
    }
  } else if (orignalName?.includes("|G|")) {
    let genderName = orignalName?.split("|G|")?.[1]?.trim();
    if (getGenderLogo(genderName)) {
      return (
        "<span class ='" +
        (classes?.length
          ? classes
          : "d-flex align-items-center w-100  wm-chart-card-title") +
        "' ><img  style='width:" +
        "1.5rem" +
        ";height:" +
        "1.5rem" +
        ";margin-right:" +
        "0.5rem" +
        ";' src='" +
        getGenderLogo(genderName) +
        "'> <span class ='text-ellipsis text-Darkgray' >" +
        genderName +
        "</span> </span>"
      );
    }
  }
  // else if (orignalName?.includes("|C|")) {
  //   let countryName = orignalName?.split("|C|")?.[1]?.trim();
  //   if (getCountryLogo(countryName)) {
  //     return (
  //       "<span class ='" +
  //       (classes?.length
  //         ? classes
  //         : "d-flex align-items-center w-100  wm-chart-card-title") +
  //       "' >" +
  //       getCountryLogo(countryName) +
  //       " <span class ='text-ellipsis text-Darkgray' >" +
  //       countryName +
  //       "</span> </span>"
  //     );
  //   }
  // }
  return orignalName;
};
export const getHeat = (graphObject, authParams) => {
  const xaxisDataList = [];
  graphObject?.single?.[0]?.x?.map((el, i) => {
    xaxisDataList.push(`${el} ⭐`);
  });
  let widgetData = graphObject?.metadata;
  // This is for x-axis
  const durationType =
    (getWindowLocationPath()?.toLowerCase() === "widgetmaker"?.toLowerCase() ||
      getWindowLocationPath()?.toLowerCase() === "edit-widget"?.toLowerCase() ||
      graphObject?.dashboard ||
      graphObject?.screen ||
      graphObject?.panes?.[graphObject?.index]?.is_widget_maker_open_obj
        ?.open_flag) &&
    handleDateFormat(
      graphObject?.panes,
      graphObject?.index,
      graphObject?.section_index,
      graphObject?.widget_index,
      widgetData,
      widgetData?.x_axis?.attribute,
      graphObject?.xaxis,
      widgetData?.x_axis?.date_part,
      graphObject?.single[0]?.x,
      graphObject?.section,
      graphObject?.template_widget,
      graphObject?.shareTemplateData,
      graphObject?.pdfDownloadStatus,
      graphObject?.isScheduleReportOpen,
      graphObject?.screen,
      graphObject?.template_details,
      false, // graphObject?.isDeepDive,
      null, //graphObject?.dateAggregationOfThisGraph,
      null, //graphObject?.deepdiveObject,
      null, //graphObject?.yAxesDateAttr,
      graphObject?.isDeepDiveDashboardView,
      graphObject?.deepdiveDashboardData,
      graphObject?.deepdiveDashboardTemplateData
    );
  let x_date_part = widgetData?.x_axis?.date_part;
  let x_date_agg = widgetData?.x_axis?.date_aggregation;
  // This is for y-axis
  let yAxesDateAttr =
    widgetData?.y_axes && Array.isArray(widgetData?.y_axes)
      ? widgetData?.y_axes?.some((y_axis) =>
          graphObject?.graphConditionConfig?.["date-attributes"]?.includes(
            y_axis?.attribute
          )
        )
        ? widgetData?.y_axes?.find((y_axis) =>
            graphObject?.graphConditionConfig?.["date-attributes"]?.includes(
              y_axis?.attribute
            )
          )
        : null
      : widgetData?.breakdown_by && Array.isArray(widgetData?.breakdown_by)
      ? widgetData?.breakdown_by?.some((y_axis) =>
          graphObject?.graphConditionConfig?.["date-attributes"]?.includes(
            y_axis?.attribute
          )
        )
        ? widgetData?.breakdown_by?.find((y_axis) =>
            graphObject?.graphConditionConfig?.["date-attributes"]?.includes(
              y_axis?.attribute
            )
          )
        : null
      : null;
  const yAxisName = yAxesDateAttr?.trace_name || graphObject?.yaxis || "Y-axis";

  const durationTypeForYAxis =
    (getWindowLocationPath()?.toLowerCase() === "widgetmaker"?.toLowerCase() ||
      getWindowLocationPath()?.toLowerCase() === "edit-widget"?.toLowerCase() ||
      graphObject?.dashboard ||
      graphObject?.screen ||
      graphObject?.panes?.[graphObject?.index]?.is_widget_maker_open_obj
        ?.open_flag) &&
    handleDateFormat(
      graphObject?.panes,
      graphObject?.index,
      graphObject?.section_index,
      graphObject?.widget_index,
      graphObject?.metadata,
      yAxesDateAttr?.attribute,
      yAxesDateAttr,
      yAxesDateAttr?.date_part,
      graphObject?.single[0]?.y,
      graphObject?.section,
      graphObject?.template_widget,
      graphObject?.shareTemplateData,
      graphObject?.pdfDownloadStatus,
      graphObject?.isScheduleReportOpen,
      graphObject?.screen,
      graphObject?.template_details,
      false, // graphObject?.isDeepDive,
      null, //graphObject?.dateAggregationOfThisGraph,
      null, //graphObject?.deepdiveObject,
      yAxesDateAttr,
      graphObject?.isDeepDiveDashboardView,
      graphObject?.deepdiveDashboardData,
      graphObject?.deepdiveDashboardTemplateData
    );
  let y_date_part = yAxesDateAttr?.date_part;
  let y_date_agg = yAxesDateAttr?.date_aggregation;

  //Calculating cateories values here because name need to be pass in series data for deepdive
  const xCategoriesValues =
    graphObject?.metadata &&
    (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
      graphObject?.metadata?.x_axis?.attribute
    ) ||
      graphObject?.xaxis === "Date") &&
    ((graphObject?.pdfDownloadStatus &&
      graphObject?.metadata?.x_axis?.date_part !== null &&
      graphObject?.metadata?.x_axis?.date_part !== "D") ||
      ((!graphObject?.pdfDownloadStatus || graphObject?.isScheduleReportOpen) &&
        (graphObject?.metadata?.x_axis?.date_part == null ||
          graphObject?.metadata?.x_axis?.date_part == "D"))) &&
    durationType
      ? durationType
      : graphObject?.metadata &&
        (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
          graphObject?.metadata?.x_axis?.attribute
        ) ||
          graphObject?.xaxis === "Date") &&
        graphObject?.metadata?.x_axis?.date_part !== null &&
        graphObject?.metadata?.x_axis?.date_part !== "D"
      ? graphObject?.single[0]?.x?.map((e) => e)
      : graphObject?.xaxis === "Date" &&
        (graphObject?.widgetGraphConfig?.x_axis?.date_part == null ||
          graphObject?.widgetGraphConfig?.x_axis?.date_part == "D")
      ? graphObject?.single[0]?.x?.map((e) => moment(e).format("MMM DD YYYY"))
      : graphObject?.xaxis === "Review Ratings" && xaxisDataList
      ? xaxisDataList
      : graphObject?.single[0]?.x;
  const yCategoriesValues =
    graphObject?.metadata &&
    (yAxesDateAttr || graphObject?.yaxis === "Date") &&
    ((graphObject?.pdfDownloadStatus &&
      yAxesDateAttr?.date_part !== null &&
      yAxesDateAttr?.date_part !== "D") ||
      ((!graphObject?.pdfDownloadStatus || graphObject?.isScheduleReportOpen) &&
        (yAxesDateAttr?.date_part == null ||
          yAxesDateAttr?.date_part == "D"))) &&
    durationTypeForYAxis
      ? durationTypeForYAxis
      : graphObject?.metadata &&
        (yAxesDateAttr || graphObject?.yaxis === "Date") &&
        yAxesDateAttr?.date_part !== null &&
        yAxesDateAttr?.date_part !== "D"
      ? graphObject?.single[0]?.y?.map((e) => e)
      : graphObject?.yaxis === "Date" &&
        (yAxesDateAttr?.date_part == null || yAxesDateAttr?.date_part == "D")
      ? graphObject?.single[0]?.y?.map((e) => moment(e).format("MMM DD YYYY"))
      : graphObject?.single[0]?.y;

  let updateZData = graphObject?.single[0]?.z;
  if (updateZData && Array.isArray(updateZData)) {
    updateZData = updateZData?.map((zData) => {
      let cellData = {
        x: zData?.[0],
        y: zData?.[1],
        value: zData?.[2],
        xCategoryValue: xCategoriesValues?.[zData?.[0]],
        yCategoryValue: yCategoriesValues?.[zData?.[1]],
      };
      if (cellData?.value === 0) {
        cellData.color = HeatMapEnum?.backgroundColor?.gray;
        // cellData.dataLabels = { color: "#E0E0E0" };
      }
      return cellData;
    });
  }
  let date_check = moment(
    graphObject?.single && graphObject?.single[0]?.x[0]
  ).isValid();
  const obj = {
    chart: {
      height: graphObject?.style.height,
      type: "heatmap",
      // marginTop: 40,
      // marginBottom: 80,
      plotBorderWidth: 1,
      reflow: true,
      style: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        fontSize: "1.6rem",
      },
      events: {
        load: function () {
          try {
            let chart = this;
            setTimeout(() => {
              requestAnimationFrame(function () {
                let container = chart?.renderTo;
                // Use current container dimensions to update the chart size
                chart?.setSize(null, container?.clientHeight, false);
                chart?.reflow();
              });
            }, 500);
          } catch (e) {
            console.error("Error in load event of heatmap", e);
          }
        },
      },
    },

    title: {
      text: null,
    },

    xAxis: {
      title: {
        text:
          graphObject?.metadata?.x_axis?.attribute &&
          graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
            graphObject?.metadata?.x_axis?.attribute
          )
            ? !x_date_part && !x_date_agg
              ? "Date"
              : !x_date_part && x_date_agg === "1h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "2h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "3h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "4h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "6h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "8h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "12h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "ww"
              ? "Week of Year"
              : !x_date_part && x_date_agg === "M"
              ? "Month of Year"
              : !x_date_part && x_date_agg === "Y"
              ? "Years"
              : graphObject?.metadata?.x_axis?.date_part === null
              ? "Date"
              : graphObject?.metadata?.x_axis?.date_part === "1h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "2h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "3h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "4h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "6h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "8h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "12h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "M"
              ? "Month of Year"
              : graphObject?.metadata?.x_axis?.date_part === "D"
              ? "Date"
              : graphObject?.metadata?.x_axis?.date_part === "hh"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "dd"
              ? "Day of Month"
              : graphObject?.metadata?.x_axis?.date_part === "dw"
              ? "Day of Week"
              : graphObject?.metadata?.x_axis?.date_part === "mm"
              ? "Month of Year"
              : graphObject?.metadata?.x_axis?.date_part === "ww"
              ? "Week of Year"
              : graphObject?.metadata?.x_axis?.date_part === "qq"
              ? "Quarter of Year"
              : graphObject?.metadata?.x_axis?.date_part
            : !graphObject?.metadata?.x_axis?.attribute &&
              (graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                "RecordDate" ||
                graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                  "CreatedDate")
            ? graphObject?.widgetGraphConfig?.x_axis?.date_part === null
              ? "Date"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "hh"
              ? "Hour of Day"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dd"
              ? "Day of Month"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dw"
              ? "Day of Week"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "mm"
              ? "Month of Year"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "ww"
              ? "Week of Year"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "qq"
              ? "Quarter of Year"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part
            : graphObject?.xaxis,
        enabled: true,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      categories: xCategoriesValues,
      labels: {
        formatter: function () {
          return date_check || graphObject?.pdfDownloadStatus
            ? this.value
            : this.value.length > 11 && this.axis?.max + 1 > 5
            ? this.value?.slice(0, 11) + (this.value?.length > 11 ? "..." : "")
            : this.value;
        },
        rotation: graphObject?.single[0]?.x?.length > 5 ? -45 : 0,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      tickWidth: 1,
      tickmarkPlacement: "on",
    },

    yAxis: {
      min: undefined,
      title: {
        text: yAxesDateAttr
          ? !y_date_part && !y_date_agg
            ? "Date"
            : !y_date_part && y_date_agg === "1h"
            ? "Hour of Day"
            : !y_date_part && y_date_agg === "2h"
            ? "Hour of Day"
            : !y_date_part && y_date_agg === "3h"
            ? "Hour of Day"
            : !y_date_part && y_date_agg === "4h"
            ? "Hour of Day"
            : !y_date_part && y_date_agg === "6h"
            ? "Hour of Day"
            : !y_date_part && y_date_agg === "8h"
            ? "Hour of Day"
            : !y_date_part && y_date_agg === "12h"
            ? "Hour of Day"
            : !y_date_part && y_date_agg === "ww"
            ? "Week of Year"
            : !y_date_part && y_date_agg === "M"
            ? "Month of Year"
            : !y_date_part && y_date_agg === "Y"
            ? "Years"
            : yAxesDateAttr?.date_part === null
            ? "Date"
            : yAxesDateAttr?.date_part === "1h"
            ? "Hour of Day"
            : yAxesDateAttr?.date_part === "2h"
            ? "Hour of Day"
            : yAxesDateAttr?.date_part === "3h"
            ? "Hour of Day"
            : yAxesDateAttr?.date_part === "4h"
            ? "Hour of Day"
            : yAxesDateAttr?.date_part === "6h"
            ? "Hour of Day"
            : yAxesDateAttr?.date_part === "8h"
            ? "Hour of Day"
            : yAxesDateAttr?.date_part === "12h"
            ? "Hour of Day"
            : yAxesDateAttr?.date_part === "M"
            ? "Month of Year"
            : yAxesDateAttr?.date_part === "D"
            ? "Date"
            : yAxesDateAttr?.date_part === "hh"
            ? "Hour of Day"
            : yAxesDateAttr?.date_part === "dd"
            ? "Day of Month"
            : yAxesDateAttr?.date_part === "dw"
            ? "Day of Week"
            : yAxesDateAttr?.date_part === "mm"
            ? "Month of Year"
            : yAxesDateAttr?.date_part === "ww"
            ? "Week of Year"
            : yAxesDateAttr?.date_part === "qq"
            ? "Quarter of Year"
            : yAxesDateAttr?.date_part
          : !yAxesDateAttr?.attribute &&
            (yAxesDateAttr?.attribute === "RecordDate" ||
              yAxesDateAttr?.attribute === "CreatedDate")
          ? yAxesDateAttr?.date_part === null
            ? "Date"
            : yAxesDateAttr?.date_part === "hh"
            ? "Hour of Day"
            : yAxesDateAttr?.date_part === "dd"
            ? "Day of Month"
            : yAxesDateAttr?.date_part === "dw"
            ? "Day of Week"
            : yAxesDateAttr?.date_part === "mm"
            ? "Month of Year"
            : yAxesDateAttr?.date_part === "ww"
            ? "Week of Year"
            : yAxesDateAttr?.date_part === "qq"
            ? "Quarter of Year"
            : yAxesDateAttr?.date_part
          : graphObject?.yaxis,
        enabled: true,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      categories: yCategoriesValues,
      // categories: graphObject?.single[0]?.y,
      labels: {
        formatter: function () {
          return this.value.length > 11
            ? this.value?.slice(0, 11) + (this.value?.length > 11 ? "..." : "")
            : kFormatter(this.value);
        },
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      allowDecimals: false,
    },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderWidth: 0,
      shadow: false,
      backgroundColor: "transparent",
      style: {
        fontSize: "1.4rem",
        fontFamily: '"Roboto", sans-serif',
        color: "#3b5175",
      },
      formatter: function () {
        return (
          "<div class='tooltip_chart text-Darkgray'> X : " +
          getPointCategoryName(this?.point, "x") +
          "<br> Y : " +
          getPointCategoryName(this?.point, "y") +
          "<br>Count : " +
          this?.point?.value?.toLocaleString() +
          "</div>"
        );
      },
    },
    colorAxis: {
      min: 0,
      maxColor:
        graphObject?.chartStyles?.endColor &&
        graphObject?.chartStyles?.endColor !== "#000000"
          ? graphObject?.chartStyles?.endColor
          : "#185edf",
      minColor:
        graphObject?.chartStyles?.startColor &&
        graphObject?.chartStyles?.startColor !== "#000000"
          ? graphObject?.chartStyles?.startColor
          : "#fa1919",
      allowDecimals: false,
      labels: {
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
    },

    legend: {
      align: "right",
      layout: "vertical",
      margin: 0,
      verticalAlign: "top",
      y: 25,
      symbolHeight: 280,
      itemStyle: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
      },
      itemHoverStyle: {
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
      },
    },

    credits: {
      enabled: false,
    },
    series: [
      {
        name: null,
        borderWidth: 1,
        borderColor: dynamicHighchartOutlineColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        data: updateZData ? updateZData : graphObject?.single[0]?.z,
        dataLabels: {
          enabled: true,
          formatter: function () {
            return !graphObject?.split
              ? this?.point?.value?.toLocaleString()
              : "";
          },
          style: {
            textOutline: 0,
            color:
              graphObject?.chartStyles?.dataLabelColor &&
              graphObject?.chartStyles?.dataLabelColor !== "#000000"
                ? graphObject?.chartStyles?.dataLabelColor
                : dynamicHighchartTextColor(
                    authParams,
                    graphObject?.isDeepDiveView,
                    graphObject?.pdfDownloadStatus ||
                      graphObject?.pptDownloadStatus ||
                      graphObject?.isScheduleReportOpen,
                    graphObject?.isDeepDiveDashboardView
                  ),
            fontWeight: "normal",
          },
        },
      },
    ],

    exporting: {
      filename: graphObject?.widget_name
        ? graphObject?.widget_name
        : graphObject?.type,
      enabled:
        graphObject?.pdfDownloadStatus ||
        graphObject?.pptDownloadStatus ||
        graphObject?.isScheduleReportOpen ||
        authParams?.screen_id
          ? false
          : true,
      menuItemDefinitions: {
        // Custom definition
        xsl: {
          onclick: () => {
            const excel = new Excel();
            // Get axis names from metadata
            const xAxisName =
              graphObject?.metadata?.x_axis?.trace_name ||
              graphObject?.metadata?.x_axis?.attribute ||
              graphObject?.widgetGraphConfig?.x_axis?.trace_name ||
              graphObject?.widgetGraphConfig?.x_axis?.attribute ||
              "X-axis";
            const yAxisName =
              graphObject?.metadata?.y_axes?.[0]?.trace_name ||
              graphObject?.widgetGraphConfig?.breakdown_by?.[0]?.trace_name ||
              "Y-axis";
            // Define columns: first column for Y values then one for each unique X value
            let columns = [
              { title: `${yAxisName}/${xAxisName}`, dataIndex: "Y" },
            ];
            // Get unique x and y values from the data
            const uniqueX = [...new Set(graphObject?.single?.[0]?.x)];
            const uniqueY = [...new Set(graphObject?.single?.[0]?.y)];
            uniqueX?.forEach((xVal) => {
              columns.push({ title: xVal, dataIndex: xVal });
            });
            // Build datasource: each row represents a unique Y value.
            // For each X column, search for a matching [x, y, value] entry.
            let datasource = uniqueY?.map((yVal, yValIndex) => {
              let row = { Y: yVal };
              uniqueX?.forEach((xVal, xValIndex) => {
                const match = graphObject?.single?.[0]?.z?.find(
                  (point) =>
                    point?.[0] === xValIndex && point?.[1] === yValIndex
                );
                let zValue = match?.[2];
                row[xVal] = match
                  ? isNaN(Number(zValue))
                    ? zValue?.toString().includes("NaN")
                      ? Number(0)
                      : zValue
                    : Number(zValue)
                  : "";
              });
              return row;
            });
            excel
              .addSheet("test")
              .addColumns(columns)
              .addDataSource(datasource, {
                str2Percent: true,
              })
              .saveAs(
                `${
                  graphObject?.widget_name
                    ? graphObject?.widget_name
                    : graphObject?.type
                }.xlsx`
              );
          },
          text: "Download excel sheet",
        },
      },
      buttons: {
        contextButton: {
          menuItems:
            graphObject?.xaxis && graphObject?.yaxis
              ? [
                  "downloadPNG",
                  "downloadJPEG",
                  "downloadSVG",
                  "separator",
                  "xsl",
                ]
              : ["downloadPNG", "downloadJPEG", "downloadSVG"],
          buttonSpacing: 1,
          height: 20,
          symbolSize: 12,
          symbolStrokeWidth: 2,
          symbolX: 10.5,
          symbolY: 10.5,
          width: 20,
          x: -2,
          y: -12,
        },
      },
      chartOptions: {
        chart: {
          style: {
            fontSize: "16px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        xAxis: {
          title: {
            style: {
              fontSize: "14px",
              fontWeight: "500",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          labels: {
            style: {
              fontSize: "14px",
              fontWeight: "500",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
        yAxis: {
          title: {
            style: {
              fontSize: "14px",
              fontWeight: "500",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          labels: {
            style: {
              fontSize: "14px",
              fontWeight: "500",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
        tooltip: {
          style: {
            fontSize: "14px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        plotOptions: {
          series: {
            dataLabels: {
              style: {
                fontSize: "10px",
                fontFamily: '"Roboto", sans-serif',
                fontWeight: "500",
              },
            },
          },
        },
        legend: {
          itemStyle: {
            fontSize: "13px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
      },
    },

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            yAxis: {
              labels: {
                formatter: function () {
                  return this.value
                    ? String(this.value)?.charAt(0)
                    : this.value;
                },
              },
              style: {
                color: dynamicHighchartTextColor(
                  authParams,
                  graphObject?.isDeepDiveView,
                  graphObject?.pdfDownloadStatus ||
                    graphObject?.pptDownloadStatus ||
                    graphObject?.isScheduleReportOpen,
                  graphObject?.isDeepDiveDashboardView
                ),
              },
            },
          },
        },
      ],
    },
  };
  return obj;
};

export const getPie = (graphObject, authParams) => {
  // const colorArray = [
  // 	"#57E2CF",
  // 	"#57AEE3",
  // 	"#5568E0",
  // 	"#8B56E3",
  // 	"#E256AD",
  // 	"#E35668",
  // 	"#E38957",
  // 	"#E2CF56",
  // 	"#AEE357",
  // 	"#68E357",
  // ];
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  const colorArray = selectedColorArray;
  let piedata = [];
  let j = 0;
  // let totalYValue = 0;
  // // Calculate total for percentage calculation
  // graphObject?.single?.forEach((data) => {
  //   totalYValue = totalYValue + data?.y;
  // });
  let noOfIterationInColors = 0;
  let lengthOfColors = colorArray?.length;
  graphObject?.single &&
    graphObject?.single?.map((el, i) => {
      let dataColor;
      if (
        el?.color !== undefined &&
        el?.color !== "#000000" &&
        el?.color !== null
      ) {
        dataColor = el?.color;
      } else {
        if (j >= lengthOfColors) {
          noOfIterationInColors = parseInt(j / lengthOfColors);
          let rem = parseInt(j % lengthOfColors);
          dataColor =
            noOfIterationInColors > 0
              ? newColorShadeInHex6(
                  selectedColorArray[rem],
                  parseInt(noOfIterationInColors * 5)
                )
              : selectedColorArray[j];
        } else {
          dataColor = selectedColorArray[j];
        }
        j++;
      }
      let lighterColor = newColorShadeInHex6(dataColor, 60);
      let darkerColor = dataColor;
      // // Calculate percentage for this slice
      // const percentage =
      //   totalYValue > 0 && el?.y > 0 ? (el?.y / totalYValue) * 100 : 0;
      piedata?.push({
        color: {
          radialGradient: {
            cx: 0.5,
            cy: 0.3,
            r: 0.7,
          },
          stops: [
            [0, lighterColor],
            [1, darkerColor], // darken
          ],
        },
        name:
          graphObject?.xaxis === "Review Ratings" ? `${el?.name} ⭐` : el?.name,
        y: el?.y,
        // dataLabels: {
        //   distance: percentage >= 5 ? "30%" : percentage >= 2 ? "50%" : "30%",
        // },
      });
    });
  const obj = {
    chart: {
      height: graphObject?.style?.height,
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: "pie",
      // spacingLeft: 0,
      // spacingRight: 0,
      // spacingTop: 0,
      // spacingBottom: 0,
      reflow: true,
      options3d: {
        enabled: graphObject?.chartShow3d,
        alpha: graphObject?.chart3dOption?.alpha,
        beta: graphObject?.chart3dOption?.beta,
      },
      style: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        fill: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        fontSize: "1.6rem",
      },
    },
    title: {
      text: null,
      align: "center",
      verticalAlign: "middle",
      y: -30,
      style: {
        fontWeight: 200,
      },
    },
    subtitle: {
      text: null,
      align: "center",
      verticalAlign: "middle",
      y: 0,
      style: {
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ), //"#3b5175",
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      useHTML: true,
      borderWidth: 0,
      shadow: false,
      backgroundColor: "transparent",
      // headerFormat:
      //   '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">{point.point.name}</span><div class="d-flex align-items-center">',
      // pointFormatter: function () {
      //   let clr = this?.color
      //     ? this?.color?.stops?.length
      //       ? this?.color?.stops?.[0]?.[1]
      //       : this?.color
      //     : this?.series?.color?.stops?.length
      //     ? this?.series?.color?.stops?.[0]?.[1]
      //     : this?.series?.color;
      //   return (
      //     '<span class="stackbar__tooltip" style="background-color:' +
      //     clr +
      //     ';">' +
      //     this?.y +
      //     (this?.percentage
      //       ? " | " + this?.percentage?.toFixed(1)?.replace(/\.0$/, "") + "%"
      //       : "") +
      //     "</span></div></div>"
      //   );
      // },
      formatter: function () {
        let clr = this?.color
          ? this?.color?.stops?.length
            ? this?.color?.stops?.[0]?.[1]
            : this?.color
          : this?.series?.color?.stops?.length
          ? this?.series?.color?.stops?.[0]?.[1]
          : this?.series?.color;
        let card_percent =
          graphObject?.metadata?.chart?.chart_settings?.card_percent === true
            ? true
            : graphObject?.metadata?.chart?.chart_settings?.card_percent ===
              false
            ? false
            : true;
        return (
          '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1"> ' +
          getNameWithChannelIconIfExists(
            this?.key ? this?.key : this?.point?.name
          ) +
          '</span><div class="d-flex align-items-center">' +
          '<span class="stackbar__tooltip" style="background-color:' +
          clr +
          ';">' +
          this?.y?.toLocaleString() +
          (card_percent && this?.percentage
            ? " | " + this?.percentage?.toFixed(2)?.replace(/\.0$/, "") + "%"
            : "") +
          "</span></div></div>"
        );
      },
      //borderRadius: 0,
      //itemMarginBottom: 3,
      //borderWidth: 1,
      //backgroundColor: '#00000',
      style: { fontSize: "1.4rem", fontFamily: '"Roboto", sans-serif' },
      //shadow: true
    },
    plotOptions: {
      series: {
        cursor: "pointer",

        states: {
          hover: {
            enabled: true,
          },
        },
      },
      pie: {
        size:
          graphObject?.metadata?.span === "half"
            ? "75%"
            : graphObject?.metadata?.span === "two_third"
            ? "50%"
            : graphObject?.metadata?.span === "one_third"
            ? "50%"
            : "80%",
        allowPointSelect: true,
        depth: graphObject?.chart3dOption?.depth,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          useHTML: true,
          crop: false,
          overflow: "allow",
          allowOverlap: true,
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
            fontSize: "1.1rem",
            fontFamily: "Roboto",
            fontWeight: "600",
            textOutline: "none",
            wordWrap: "break-word",
          },
          //format: "{point.name} : {point.y} | {point.percentage:.1f}%",
          //format: "{point.name}<br>{point.y:,.2f} | {point.percentage:.1f}%",
          formatter: function () {
            let formattedNumber = formatInCommaSeprated(
              this?.y?.toFixed(2)?.replace(/\.00$/, "")
            );
            let card_percent =
              graphObject?.metadata?.chart?.chart_settings?.card_percent ===
              true
                ? true
                : graphObject?.metadata?.chart?.chart_settings?.card_percent ===
                  false
                ? false
                : true;

            let keyText = String(this?.key);
            const maxLengthPerLine = 24;
            if (keyText?.length <= maxLengthPerLine) {
              keyText = keyText;
            } else {
              // Find the last space before maxLengthPerLine for a natural word break
              let firstLineEnd = keyText?.lastIndexOf(" ", maxLengthPerLine);
              if (firstLineEnd === -1) firstLineEnd = maxLengthPerLine; // If no space found, force split

              const firstLine = keyText?.slice(0, firstLineEnd);
              let secondLine = keyText?.slice(firstLineEnd + 1);

              // Truncate the second line if it exceeds maxLengthPerLine
              if (secondLine?.length > maxLengthPerLine) {
                secondLine = secondLine?.slice(0, maxLengthPerLine - 3) + "...";
              }
              keyText = firstLine + "<br>" + secondLine;
            }
            return (
              keyText +
              "<br>" +
              formattedNumber +
              (card_percent && this?.percentage
                ? " | " +
                  this?.percentage?.toFixed(2)?.replace(/\.0$/, "") +
                  "%"
                : "")
            );
          },
          distance: "30%",
        },

        point: {
          events: {
            legendItemClick: () => false,
          },
        },
        showInLegend: graphObject?.metadata?.chart
          ? graphObject?.metadata?.chart?.chart_settings?.chart_property
              ?.show_legend
          : graphObject?.metadata?.chart_property?.show_legend,
        borderWidth: 2,
        borderColor: dynamicHighchartOutlineColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        //colors: ["#a5c559", "#edf3de"],
      },
    },
    series:
      graphObject?.type === "pie"
        ? [
            {
              innerSize: "0%",
              borderRadius: "4px",
              data: piedata,
            },
          ]
        : [
            {
              innerSize: "50%",
              borderRadius: "4px",
              data: piedata,
            },
          ],
    exporting: {
      sourceWidth: 1200,
      sourceHeight: 900,
      scale: 1,
      allowHTML: true,
      filename: graphObject?.widget_name
        ? graphObject?.widget_name
        : graphObject?.type,
      enabled:
        graphObject?.pdfDownloadStatus ||
        graphObject?.pptDownloadStatus ||
        graphObject?.isScheduleReportOpen ||
        authParams?.screen_id
          ? false
          : true,
      menuItemDefinitions: {
        // Custom definition
        xsl: {
          onclick: () => {
            const excel = new Excel();
            let columns = [
              {
                title: graphObject?.xaxis,
                dataIndex: graphObject?.xaxis,
              },
              {
                title: graphObject?.yaxis,
                dataIndex: graphObject?.yaxis,
              },
            ];

            let datasource = graphObject?.single?.map((el, i) => {
              return {
                [columns[0]?.title]: el?.name,
                [columns[1]?.title]: isNaN(Number(el?.y))
                  ? el?.y?.toString().includes("NaN")
                    ? Number(0)
                    : el?.y
                  : Number(el?.y), // el?.y
              };
            });
            excel
              .addSheet("test")
              .addColumns(columns)
              .addDataSource(datasource, {
                str2Percent: true,
              })
              .saveAs(
                `${
                  graphObject?.widget_name
                    ? graphObject?.widget_name
                    : graphObject?.type
                }.xlsx`
              );
          },
          text: "Download excel sheet",
        },
      },
      buttons: {
        contextButton: {
          menuItems:
            graphObject?.xaxis && graphObject?.yaxis
              ? [
                  "downloadPNG",
                  "downloadJPEG",
                  "downloadSVG",
                  "separator",
                  "xsl",
                ]
              : ["downloadPNG", "downloadJPEG", "downloadSVG"],
          buttonSpacing: 1,
          height: 20,
          symbolSize: 12,
          symbolStrokeWidth: 2,
          symbolX: 10.5,
          symbolY: 10.5,
          width: 20,
          x: -2,
          y: -12,
        },
      },
      chartOptions: {
        chart: {
          width: 1000,
          height: 800,
          spacingTop: 10,
          spacingRight: 20,
          spacingBottom: 10,
          spacingLeft: 20,
          marginTop: 10,
          marginRight: 20,
          marginBottom: 10,
          marginLeft: 20,
        },
        tooltip: {
          style: {
            fontSize: "14px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        plotOptions: {
          pie: {
            size:
              graphObject?.metadata?.span === "half"
                ? "75%"
                : graphObject?.metadata?.span === "two_third"
                ? "50%"
                : graphObject?.metadata?.span === "one_third"
                ? "50%"
                : "80%",
            dataLabels: {
              enabled: true,
              crop: false,
              overflow: "allow",
              distance: "30%",
              style: {
                fontSize: "11px",
                fontFamily: "Roboto",
                fontWeight: "bold",
                textOutline: "none",
                wordWrap: "break-word",
              },
            },
          },
        },
        legend: {
          itemStyle: {
            fontSize: "13px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    // legend: {
    // 	itemStyle: {
    // 		color: "#787878",
    // 	},
    // 	symbolRadius: 0,
    // },
    legend: graphObject?.legend_position
      ? {
          itemWidth:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? undefined
              : authParams?.screen_id === "000-000-556"
              ? 100
              : 200,
          layout:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? "horizontal"
              : "vertical",
          align:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? "center"
              : graphObject?.legend_position,
          verticalAlign:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? graphObject?.legend_position
              : "middle",
          itemStyle: {
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          itemHoverStyle: {
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          useHTML: true,
          labelFormatter: function () {
            let formattedName = getNameWithChannelIconIfExists(this?.name);
            return formattedName;
          },
        }
      : {
          itemWidth: undefined,
          enabled: true,
          layout: "horizontal",
          align: "center",
          verticalAlign: "bottom",
          itemStyle: {
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          itemHoverStyle: {
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          useHTML: true,
          labelFormatter: function () {
            let formattedName = getNameWithChannelIconIfExists(this?.name);
            return formattedName;
          },
        },
  };
  if (authParams?.screen_id) {
    obj.plotOptions.pie.size = "85%";
    obj.plotOptions.pie.dataLabels.style.fontSize = "1.3rem";
    obj.legend.itemStyle.fontSize = "1.3rem";
    obj.legend.itemStyle.fontWeight = "500";
    obj.plotOptions.pie.dataLabels.distance = 20; // Increase this value to move the labels further
  }
  return obj;
};

export const getMultiplePie = (graphObject, authParams) => {
  const colorArray = graphObject?.accentColor
    ? graphObject?.accentColor
    : globalChartColor;
  let piedata = [];
  let j = 0;
  let noOfIterationInColors = 0;
  let lengthOfColors = colorArray?.length;

  let widgetSize;
  let updatedSingleData;
  if (graphObject?.single?.length > 8) {
    updatedSingleData = graphObject?.single.slice(0, 8);
  } else {
    updatedSingleData = graphObject?.single;
  }

  // console.log(
  //   "graphObject",
  //   graphObject?.single,
  //   "updatedSingleData",
  //   updatedSingleData
  // );
  let divideNum = updatedSingleData?.length > 4 ? 2 : 1;
  widgetSize = Math.floor(
    100 / Math.ceil(updatedSingleData?.length / divideNum)
  );
  // console.log("widgetSize", widgetSize);

  // console.log("updatedSingleData", updatedSingleData);
  let xIncrementValue = 0;
  updatedSingleData &&
    updatedSingleData?.map((el, index) => {
      // let dataColor;
      // if (
      //   el?.color !== undefined &&
      //   el?.color !== "#000000" &&
      //   el?.color !== null
      // ) {
      //   dataColor = el?.color;
      // } else {
      //   if (j >= lengthOfColors) {
      //     noOfIterationInColors = parseInt(j / lengthOfColors);
      //     let rem = parseInt(j % lengthOfColors);
      //     dataColor =
      //       noOfIterationInColors > 0
      //         ? newColorShadeInHex6(
      //             d[rem],
      //             parseInt(noOfIterationInColors * 5)
      //           )
      //         : d[j];
      //   } else {
      //     dataColor = d[j];
      //   }
      //   j++;
      // }
      // let lighterColor = newColorShadeInHex6(dataColor, 60);
      // let darkerColor = dataColor;

      let remender = index % 4;
      // console.log("widgetSize", widgetSize, "remender", remender);
      let xValue = 0;
      if (remender === 0) {
        if (updatedSingleData?.length === 1) {
          xIncrementValue = 0;
          xValue = 50;
          xIncrementValue = xIncrementValue + xValue;
        } else if (updatedSingleData?.length === 2) {
          xIncrementValue = 0;
          xValue = 25;
          xIncrementValue = xIncrementValue + xValue;
        } else if (updatedSingleData?.length === 3) {
          xIncrementValue = 0;
          xValue = 15;
          xIncrementValue = xIncrementValue + xValue;
        } else {
          xIncrementValue = 0;
          xValue = 10;
          xIncrementValue = xIncrementValue + xValue;
        }
      } else {
        xValue = xIncrementValue + widgetSize;
        xIncrementValue = xValue;
      }

      // console.log(
      //   "el data",
      //   el,
      //   "xValue",
      //   xValue,
      //   "xIncrementValue",
      //   xIncrementValue
      // );
      // console.log("el data", el, "index", index);

      let newData = [];
      el?.data?.map((d) => {
        newData = [
          ...newData,
          {
            colorByPoint: true,
            title: {
              text: "TEST",
              verticalAlign: "middle",
              align: "center",
              // y: -30,
              style: {
                color: "#fff",
                fontWeight: 200,
              },
            },

            name: el?.x,
            y: d?.y,
            color:
              d?.name === "Positive"
                ? "#5ED142"
                : d?.name === "Negative"
                ? "#FF5D48"
                : d?.name === "Neutral"
                ? "#F0C52F"
                : "black",
            // dataLabels: {
            //   enabled: true,
            //   format: `<b style=margin-top: 5rem;>TEST</b>: {point.percentage:.1f}%`,
            // },
          },
        ];
      });

      piedata?.push({
        center: [
          xValue + "%",
          updatedSingleData?.length <= 4 ? "50%" : index > 3 ? "70%" : "15%",
        ],
        name: el?.x,
        data: newData,
        type: "pie",
        colorByPoint: true,
        title: {
          text: "TEST",
          verticalAlign: "middle",
          align: "center",
          // y: -30,
          style: {
            color: "#fff",
            fontWeight: 200,
          },
        },
      });
    });
  // console.log("piedata", piedata);

  const obj = {
    title: {
      text: null,
      verticalAlign: "middle",
      align: "center",
      // y: -30,
      style: {
        color: "#fff",
        fontWeight: 200,
      },
    },
    exporting: {
      enabled: false,
      allowHTML: true,
      sourceWidth: 800,
      sourceHeight: 600,
    },
    plotOptions: {
      pie: {
        showInLegend: graphObject?.metadata?.chart
          ? graphObject?.metadata?.chart?.chart_settings?.chart_property
              ?.show_legend
          : graphObject?.metadata?.chart_property?.show_legend,
        innerSize: "70%",
        dataLabels: {
          enabled: false,
          distance: -50,
          style: {
            fontWeight: "bold",
            color: "white",
          },
        },
        size: widgetSize + "%",
      },
    },
    // series: multipleDonutData,
    series: piedata,
    credits: {
      enabled: false,
    },
  };
  return obj;
};
// const onLegendItemClicked=(targetEvent)=>{
// 	targetEvent?.target && targetEvent?.target?.series?.data.map((el,i)=>{
// 		if(el?.visible){
// 			getPercentage(el?.y,el?.total) >=6 ? el.labelDistance='-30%' :el.labelDistance='30%';
// 			return true;
// 		}else{
// 			return false;
// 		}
// 	});
// };
// const getPercentage=(value,total)=>{
// 	return value && total ? (value/total)*100 : 0;
// }

export const getLine = (graphObject, authParams) => {
  selectedColorArray = graphObject?.accentColors // adding accent color suuport
    ? graphObject?.accentColors
    : globalChartColor;
  if (graphObject?.widgetGraphConfig?.chart_settings?.settings_3d) {
    graphObject.widgetGraphConfig.chart_settings.settings_3d = null;
  }
  if (graphObject?.single[0]?.y_axis) {
    let obj = { ...graphObject };
    obj.single = graphObject.single[0];
    return getCombination(obj, authParams);
  } else {
    let ySeriesFromConfig = graphObject?.metadata?.y_series
      ? graphObject?.metadata?.y_series
      : Object?.entries(graphObject?.widgetGraphConfig?.y_axis);
    let flrSeriesExists = false;

    let graphConditionConfig = [
      ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
      ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
    ];
    ySeriesFromConfig?.forEach((yseries, index) => {
      if (yseries?.attribute) {
        if (yseries && graphConditionConfig?.includes(yseries?.attribute))
          flrSeriesExists = true;
      } else if (
        yseries &&
        graphConditionConfig?.includes(yseries[1]?.attribute)
      )
        flrSeriesExists = true;
    });
    let seriesData = [];
    let seriesColorsFlag = false;
    let date_check = moment(
      graphObject?.single && graphObject?.single[0]?.x[0]
    ).isValid();

    if (
      graphObject?.single[0]?.colors != undefined &&
      graphObject?.single[0]?.colors?.length > 0
    ) {
      seriesColorsFlag = true;
      seriesData = graphObject?.single[0]?.y?.map((yElem, i) => {
        return {
          y: yElem,
          color:
            graphObject?.single[0]?.colors != undefined
              ? graphObject?.single[0]?.colors[i] != "#000000"
                ? graphObject?.single[0]?.colors[i]
                : ""
              : "",
        };
      });
    }
    const xaxisDataList = [];
    graphObject?.single[0]?.x?.map((el, i) => {
      xaxisDataList.push(`${el} ⭐`);
    });

    const durationType =
      (getWindowLocationPath()?.toLowerCase() ===
        "widgetmaker"?.toLowerCase() ||
        getWindowLocationPath()?.toLowerCase() ===
          "edit-widget"?.toLowerCase() ||
        graphObject?.dashboard ||
        graphObject?.screen ||
        graphObject?.panes?.[graphObject?.index]?.is_widget_maker_open_obj
          ?.open_flag) &&
      handleDateFormat(
        graphObject?.panes,
        graphObject?.index,
        graphObject?.section_index,
        graphObject?.widget_index,
        graphObject?.metadata,
        graphObject?.metadata?.x_axis?.attribute,
        graphObject?.xaxis,
        graphObject?.metadata?.x_axis?.date_part,
        graphObject?.single[0]?.x,
        graphObject?.section,
        graphObject?.template_widget,
        graphObject?.shareTemplateData,
        graphObject?.pdfDownloadStatus,
        graphObject?.isScheduleReportOpen,
        graphObject?.screen,
        graphObject?.template_details,
        false, // graphObject?.isDeepDive,
        null, //graphObject?.dateAggregationOfThisGraph,
        null, //graphObject?.deepdiveObject,
        null, //graphObject?.yAxesDateAttr,
        graphObject?.isDeepDiveDashboardView,
        graphObject?.deepdiveDashboardData,
        graphObject?.deepdiveDashboardTemplateData
      );
    let x_date_part = graphObject?.metadata?.x_axis?.date_part;
    let x_date_agg = graphObject?.metadata?.x_axis?.date_aggregation;

    let lineWidth;
    if (
      graphObject?.metadata?.chart?.chart_settings?.y_axes_settings[0]
        ?.lineWidth
    ) {
      lineWidth =
        graphObject.metadata.chart.chart_settings.y_axes_settings[0].lineWidth;
    } else if (graphObject?.widgetGraphConfig?.y_axis_settings[0]?.lineWidth) {
      lineWidth = graphObject.widgetGraphConfig.y_axis_settings[0].lineWidth;
    } else {
      lineWidth = 3;
    }
    let lineStyle;
    if (
      graphObject?.metadata?.chart?.chart_settings?.y_axes_settings[0]
        ?.lineStyle
    ) {
      lineStyle =
        graphObject.metadata.chart.chart_settings.y_axes_settings[0].lineStyle;
    } else if (graphObject?.widgetGraphConfig?.y_axis_settings[0]?.lineStyle) {
      lineStyle = graphObject.widgetGraphConfig.y_axis_settings[0].lineStyle;
    } else {
      lineStyle = "solid";
    }
    const obj = {
      chart: {
        //type: graphObject?.type,
        type: "spline",
        reflow: true,
        height: graphObject?.style?.height,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          fontSize: "1.6rem",
        },
      },
      title: {
        text: "",
      },
      yAxis: {
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
        },
        min:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? 0.1
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? 0.1
            : flrSeriesExists
            ? 0
            : undefined, //aiLogScale:aiLogScale!==null? aiLogScale : log,
        minTickInterval: flrSeriesExists ? 60 : undefined,
        type:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? "logarithmic"
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? "logarithmic"
            : "", //aiLogScale:aiLogScale!==null? aiLogScale : log,
        title: {
          text: graphObject.yaxis,
          //enabled: chartGlobalDataLabel === false ? chartGlobalDataLabel : true,
          enabled: true,
          style: {
            fontWeight: "bold",
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
            // authParams?.screen_id
            //   ? "#fff"
            //   : authParams?.theme_type === "1"
            //   ? "#b1bdd0"
            //   : "#3B5175",
          },
        },

        labels: {
          formatter: flrSeriesExists
            ? function () {
                return SecondstoHumanReadable(this?.value, true);
              }
            : function () {
                return kFormatter(this.value);
              },

          style: {
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),

            // authParams?.screen_id
            //   ? "#fff"
            //   : authParams?.theme_type === "1"
            //   ? "#b1bdd0"
            //   : "#3B5175",
          },
        },
        allowDecimals: false,
      },
      xAxis: {
        // type:
        //   graphObject?.yAxisScale &&
        //   graphObject?.yAxisScale[0]?.y_axis_scale === "log"
        //     ? "logarithmic"
        //     : "",
        title: {
          text:
            graphObject?.metadata?.x_axis?.attribute &&
            graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
              graphObject?.metadata?.x_axis?.attribute
            )
              ? !x_date_part && !x_date_agg
                ? "Date"
                : !x_date_part && x_date_agg === "1h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "2h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "3h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "4h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "6h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "8h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "12h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "ww"
                ? "Week of Year"
                : !x_date_part && x_date_agg === "M"
                ? "Month of Year"
                : !x_date_part && x_date_agg === "Y"
                ? "Years"
                : graphObject?.metadata?.x_axis?.date_part === null ||
                  graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "1h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "2h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "3h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "4h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "6h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "8h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "12h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "M"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.metadata?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.metadata?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.metadata?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.metadata?.x_axis?.date_part
              : !graphObject?.metadata?.x_axis?.attribute &&
                (graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                  "RecordDate" ||
                  graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                    "CreatedDate")
              ? graphObject?.widgetGraphConfig?.x_axis?.date_part === null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part
              : graphObject?.xaxis,
          enabled: true,
          style: {
            fontFamily: '"Roboto", sans-serif',
            fontWeight: "bold",
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),

            // authParams?.screen_id
            //   ? "#fff"
            //   : authParams?.theme_type === "1"
            //   ? "#b1bdd0"
            //   : "#3B5175",
          },
        },
        categories:
          // (graphObject?.metadata &&
          //   graphObject?.metadata?.x_axis?.attribute === "RecordDate") ||
          // (graphObject?.xaxis === "Date" &&
          //   graphObject?.metadata?.x_axis?.date_part == null)
          //   ? graphObject?.single[0]?.x?.map((e) =>
          //       moment(e).format("MMM DD YYYY")
          //     )
          graphObject?.metadata &&
          (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
            graphObject?.metadata?.x_axis?.attribute
          ) ||
            graphObject?.xaxis === "Date") &&
          ((graphObject?.pdfDownloadStatus &&
            graphObject?.metadata?.x_axis?.date_part !== null &&
            graphObject?.metadata?.x_axis?.date_part !== "D") ||
            ((!graphObject?.pdfDownloadStatus ||
              graphObject?.isScheduleReportOpen) &&
              (graphObject?.metadata?.x_axis?.date_part == null ||
                graphObject?.metadata?.x_axis?.date_part == "D"))) &&
          durationType
            ? durationType
            : graphObject?.metadata &&
              (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) ||
                graphObject?.xaxis === "Date") &&
              graphObject?.metadata?.x_axis?.date_part !== null &&
              graphObject?.metadata?.x_axis?.date_part !== "D"
            ? graphObject?.single[0]?.x?.map((e) => e)
            : graphObject?.xaxis === "Date" &&
              (graphObject?.widgetGraphConfig?.x_axis?.date_part == null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part == "D")
            ? graphObject?.single[0]?.x?.map((e) =>
                moment(e).format("MMM DD YYYY")
              )
            : graphObject?.xaxis === "Review Ratings" && xaxisDataList
            ? xaxisDataList
            : graphObject?.single[0]?.x,
        labels: {
          useHTML: true, // Enable HTML rendering
          formatter: function () {
            if (date_check || graphObject?.pdfDownloadStatus) {
              return this.value; // No formatting needed in this case
            }

            const text = String(this.value);
            const maxLengthPerLine = 24;

            if (text?.length <= maxLengthPerLine) {
              return `<div style="text-align: right;">${text}</div>`; // Single-line, right-aligned
            }

            // Find the last space before maxLengthPerLine for a natural word break
            let firstLineEnd = text?.lastIndexOf(" ", maxLengthPerLine);
            if (firstLineEnd === -1) firstLineEnd = maxLengthPerLine; // If no space found, force split

            const firstLine = text?.slice(0, firstLineEnd);
            let secondLine = text?.slice(firstLineEnd + 1);

            // Truncate the second line if it exceeds maxLengthPerLine
            if (secondLine?.length > maxLengthPerLine) {
              secondLine = secondLine?.slice(0, maxLengthPerLine - 3) + "...";
            }

            // Return wrapped text with right alignment using div and HTML
            return `<div style="text-align: right;">${firstLine}<br>${secondLine}</div>`;
          },
          rotation: graphObject?.single[0]?.x?.length > 5 ? -90 : 0,
          style: {
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),

            // authParams?.screen_id
            //   ? "#fff"
            //   : authParams?.theme_type === "1"
            //   ? "#b1bdd0"
            //   : "#3B5175",
          },
        },
        gridLineWidth: 1,
        tickmarkPlacement: "on",
        tickWidth: 1,
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
        },
      },
      tooltip: {
        enabled: true,
        useHTML: true,
        borderWidth: 0,
        shadow: false,
        backgroundColor: "transparent",
        //pointFormat: " {point.y})",
        headerFormat:
          "<div class='tooltip_chart fontSize-14 d-flex align-items-center'><span class='text-Darkgray font-weight600 mr-1'>{point.key} </span> ",
        pointFormatter: function () {
          let clr = this?.color
            ? this?.color?.stops?.length
              ? this?.color?.stops?.[0]?.[1]
              : this?.color
            : this?.series?.color?.stops?.length
            ? this?.series?.color?.stops?.[0]?.[1]
            : this?.series?.color;
          return (
            '<span class="stackbar__tooltip" style="background-color:' +
            clr +
            ';">' +
            (flrSeriesExists
              ? SecondstoHumanReadable(this?.y)
              : this?.y?.toLocaleString()) +
            "</span></div>"
          );
        },
        // formatter: function () {
        //   let clr = this?.color
        //     ? this?.color?.stops?.length
        //       ? this?.color?.stops?.[0]?.[1]
        //       : this?.color
        //     : this?.series?.color?.stops?.length
        //     ? this?.series?.color?.stops?.[0]?.[1]
        //     : this?.series?.color;
        //   return (
        //     "<div class='tooltip_chart fontSize-14 d-flex align-items-center'><span class='text-Darkgray font-weight600 mr-1'> " +
        //     getNameWithChannelIconIfExists(
        //       this?.key ? this?.key : this?.point?.category
        //     ) +
        //     "</span>" +
        //     '<span class="stackbar__tooltip" style="background-color:' +
        //     clr +
        //     ';">' +
        //     (flrSeriesExists ? SecondstoHumanReadable(this?.y) : this?.y) +
        //     "</span></div>"
        //   );
        // },
        style: {
          fontSize: "1.4rem",
          fontFamily: '"Roboto", sans-serif',
          color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
        },
      },
      plotOptions: {
        series: {
          cursor: "pointer",
          lineWidth: lineWidth,
          events: {
            click: function (event) {
              return "hello";
            },
          },
          label: {
            connectorAllowed: false,
          },
          style: {
            fontFamily: '"Roboto", sans-serif',
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
          },
          marker: {
            enabled:
              graphObject?.chartMarkers === false
                ? graphObject?.chartMarkers
                : true,
            //radius:graphObject?.chartMarkers === true ? 4 : 0
            symbol: "circle",
            fillColor: "#FFFFFF",
            lineWidth: 3,
            lineColor: null, // inherit from series
          },
          showInLegend: false,
          dataLabels: {
            enabled: graphObject?.chartGlobalDataLabel ? true : false,
            style: {
              fontWeight: "500",
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
              fontSize: "1rem",
              fontFamily: '"Roboto", sans-serif',
            },
            formatter: flrSeriesExists
              ? function () {
                  return (this.y / 60).toFixed(0) + " mins";
                }
              : function () {
                  return kFormatter(this.y);
                },
          },
        },
        spline: {
          animation: graphObject?.pdfDownloadStatus ? false : true,
          stacking: undefined,
        },
      },
      series: [
        {
          name: graphObject?.single[0]?.name
            ? graphObject?.single[0]?.name
            : "Timeline",
          data: seriesColorsFlag ? seriesData : graphObject?.single[0]?.y,
          color: !seriesColorsFlag
            ? graphObject?.single[0]?.color != undefined &&
              graphObject?.single[0]?.color != "#000000"
              ? graphObject?.single[0]?.color
              : selectedColorArray[0]
            : undefined,
          dashStyle: lineStyle,
          //  shadow: {
          //         color: !seriesColorsFlag
          //         ? graphObject?.single[0]?.color != undefined &&
          //           graphObject?.single[0]?.color != "#000000"
          //           ? graphObject?.single[0]?.color
          //           : globalChartColor[1]
          //         : seriesColorsFlag &&
          //           seriesData?.[0]?.color!= undefined &&
          //           seriesData?.[0]?.color != "#000000"
          //         ? seriesData?.[0]?.color
          //         : '#d5d3d31a',
          //         width: 10,
          //         opacity: 1,
          //         offsetX: 0,
          //         offsetY: 0
          //     }
        },
      ],
      exporting: {
        filename: graphObject?.widget_name
          ? graphObject?.widget_name
          : graphObject?.type,
        enabled:
          graphObject?.pdfDownloadStatus ||
          graphObject?.pptDownloadStatus ||
          graphObject?.isScheduleReportOpen ||
          authParams?.screen_id
            ? false
            : true,
        menuItemDefinitions: {
          // Custom definition
          xsl: {
            onclick: () => {
              const excel = new Excel();
              let columns = [
                {
                  title: graphObject?.xaxis,
                  dataIndex: graphObject?.xaxis,
                },
                {
                  title: graphObject?.yaxis,
                  dataIndex: graphObject?.yaxis,
                },
              ];

              let datasource = graphObject?.single[0]?.y?.map((yElem, i) => {
                return {
                  [columns[0]?.title]: graphObject?.single[0]?.x[i],
                  [columns[1]?.title]: flrSeriesExists
                    ? SecondstoHumanReadable(yElem)
                    : isNaN(Number(yElem))
                    ? yElem?.toString().includes("NaN")
                      ? Number(0)
                      : yElem
                    : Number(yElem), // yElem
                };
              });
              excel
                .addSheet("test")
                .addColumns(columns)
                .addDataSource(datasource, {
                  str2Percent: true,
                })
                .saveAs(
                  `${
                    graphObject?.widget_name
                      ? graphObject?.widget_name
                      : graphObject?.type
                  }.xlsx`
                );
            },
            text: "Download excel sheet",
          },
        },
        buttons: {
          contextButton: {
            menuItems:
              graphObject?.xaxis && graphObject?.yaxis
                ? [
                    "downloadPNG",
                    "downloadJPEG",
                    "downloadSVG",
                    "separator",
                    "xsl",
                  ]
                : ["downloadPNG", "downloadJPEG", "downloadSVG"],
            buttonSpacing: 1,
            height: 20,
            symbolSize: 12,
            symbolStrokeWidth: 2,
            symbolX: 10.5,
            symbolY: 10.5,
            width: 20,
            x: -2,
            y: -12,
          },
        },
        chartOptions: {
          chart: {
            style: {
              fontSize: "16px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          xAxis: {
            title: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
            labels: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
          },
          yAxis: {
            title: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
            labels: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
          },
          tooltip: {
            style: {
              fontSize: "14px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          plotOptions: {
            series: {
              dataLabels: {
                style: {
                  fontSize: "10px",
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: "500",
                },
              },
            },
          },
          legend: {
            itemStyle: {
              fontSize: "13px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
        chartOptions: {
          chart: {
            style: {
              fontSize: "16px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          tooltip: {
            style: {
              fontSize: "14px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          plotOptions: {
            series: {
              dataLabels: {
                style: {
                  fontSize: "10px",
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: "500",
                },
              },
            },
          },
          legend: {
            itemStyle: {
              fontSize: "13px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
      },
      credits: {
        enabled: false,
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              legend: {
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
              },
            },
          },
        ],
      },
      legend: graphObject?.legend_position
        ? {
            itemWidth:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? undefined
                : 200,
            layout:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "horizontal"
                : "vertical",
            align:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "center"
                : graphObject?.legend_position,
            verticalAlign:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? graphObject?.legend_position
                : "middle",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          }
        : {
            itemWidth: undefined,
            enabled: true,
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          },
    };
    return obj;
  }
};

export const getMultiLine = (
  graphObject,
  authParams,
  Highcharts,
  isChartTypeChange = false
) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  let twoMonthsDuration = getCustomizedDate(6, "Days");
  let defaultDuration = {
    from:
      moment(twoMonthsDuration?.date?.[0]).format("YYYY/MM/DD") +
      " " +
      twoMonthsDuration?.time?.startTime,
    to:
      moment(twoMonthsDuration?.date?.[1]).format("YYYY/MM/DD") +
      " " +
      twoMonthsDuration?.time?.endTime,
  };

  let ticketOverviewId =
    graphObject?.index >= 0 &&
    graphObject?.panes[graphObject?.index]?.template_id;
  let startdate =
    graphObject?.index >= 0 &&
    graphObject?.panes?.[graphObject?.index]?.duration?.from
      ? graphObject?.index >= 0 &&
        graphObject?.panes?.[graphObject?.index]?.duration?.from
      : defaultDuration?.from;
  let endDate =
    graphObject?.index >= 0 &&
    graphObject?.panes?.[graphObject?.index]?.duration?.to
      ? graphObject?.index >= 0 &&
        graphObject?.panes?.[graphObject?.index]?.duration?.to
      : defaultDuration?.to;
  let diffDay = moment(endDate).diff(moment(startdate), "day");

  if (graphObject?.single?.[0]?.y_axis) {
    let obj = { ...graphObject };
    obj.single = graphObject.single?.[0];
    return getCombination(obj, authParams);
  } else {
    let ySeriesFromConfig = graphObject?.metadata?.y_series
      ? graphObject?.metadata?.y_series
      : Object?.entries(graphObject?.widgetGraphConfig?.y_axis);

    let arr = [];
    arr = graphObject?.metadata?.y_series
      ? graphObject?.metadata?.y_series
      : Object.keys(graphObject?.widgetGraphConfig?.y_axis).map(
          (key) => graphObject?.widgetGraphConfig?.y_axis[key]
        );
    let filteredArray = arr?.filter((d) => d.attribute);
    let updatedYAxisLabel = [];
    filteredArray?.map((d, i) => {
      if (i >= 1) {
        updatedYAxisLabel = updatedYAxisLabel + ", " + d?.trace_name;
      } else {
        updatedYAxisLabel = updatedYAxisLabel + d?.trace_name;
      }
    });

    let flrSeriesExists = false;
    let graphConditionConfig = [
      ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
      ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
    ];

    ySeriesFromConfig?.forEach((yseries, index) => {
      if (yseries?.attribute) {
        if (yseries && graphConditionConfig?.includes(yseries?.attribute))
          flrSeriesExists = true;
      } else if (
        yseries &&
        graphConditionConfig?.includes(yseries[1]?.attribute)
      )
        flrSeriesExists = true;
    });
    let multiData = [];
    let j = 0;
    let noOfIterationInColors = 0;
    let lengthOfColors = selectedColorArray?.length;
    // Assuming graphObject is already defined and accessible in your scope
    let targetAttribute = graphObject?.single?.[0]?.data?.[0]?.attribute;

    // Find the number of attributes that match the targetAttribute
    let numberOfBreakdownBy = graphObject?.single?.[0]?.data?.filter(
      (item) => item.attribute === targetAttribute
    )?.length;
    graphObject?.single?.[0]?.data?.map((d, i) => {
      let lineWidth;
      let lineStyle;
      if (
        (graphObject?.widgetGraphConfig?.breakdown_by &&
          graphObject?.widgetGraphConfig?.breakdown_by?.length >= 1 &&
          graphObject?.widgetGraphConfig?.breakdown_by?.[0]?.attribute ===
            d?.attribute) ||
        (graphObject?.metadata?.y_axes &&
          graphObject?.metadata?.y_axes?.length >= 1 &&
          graphObject?.metadata?.y_axes?.[0]?.attribute === d?.attribute)
      ) {
        lineWidth = graphObject?.metadata?.chart?.chart_settings
          ?.y_axes_settings?.[0]?.lineWidth
          ? graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[0]
              ?.lineWidth
          : graphObject?.widgetGraphConfig?.y_axis_settings?.[0]?.lineWidth
          ? graphObject?.widgetGraphConfig?.y_axis_settings?.[0]?.lineWidth
          : 3;

        lineStyle = graphObject?.metadata?.chart?.chart_settings
          ?.y_axes_settings?.[0]?.lineStyle
          ? graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[0]
              ?.lineStyle
          : graphObject?.widgetGraphConfig?.y_axis_settings?.[0]?.lineStyle
          ? graphObject?.widgetGraphConfig?.y_axis_settings?.[0]?.lineStyle
          : "Solid";
      } else if (
        (graphObject?.widgetGraphConfig?.breakdown_by &&
          graphObject?.widgetGraphConfig?.breakdown_by?.length >= 1) ||
        (graphObject?.metadata?.y_axes &&
          graphObject?.metadata?.y_axes?.length >= 1)
      ) {
        lineWidth = graphObject?.metadata?.chart?.chart_settings
          ?.y_axes_settings?.[i - numberOfBreakdownBy + 1]?.lineWidth
          ? (lineWidth =
              graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[
                i - numberOfBreakdownBy + 1
              ].lineWidth)
          : graphObject?.widgetGraphConfig?.y_axis_settings?.[
              i - numberOfBreakdownBy + 1
            ]?.lineWidth
          ? graphObject?.widgetGraphConfig?.y_axis_settings?.[
              i - numberOfBreakdownBy + 1
            ]?.lineWidth
          : 3;

        lineStyle = graphObject?.metadata?.chart?.chart_settings
          ?.y_axes_settings?.[i - numberOfBreakdownBy + 1]?.lineStyle
          ? graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[
              i - numberOfBreakdownBy + 1
            ]?.lineStyle
          : graphObject?.widgetGraphConfig?.y_axis_settings?.[
              i - numberOfBreakdownBy + 1
            ]?.lineStyle
          ? graphObject?.widgetGraphConfig?.y_axis_settings?.[
              i - numberOfBreakdownBy + 1
            ]?.lineStyle
          : "Solid";
      } else {
        lineWidth = graphObject?.metadata?.chart?.chart_settings
          ?.y_axes_settings?.[i]?.lineWidth
          ? graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[i]
              ?.lineWidth
          : graphObject?.widgetGraphConfig?.y_axis_settings?.[i]?.lineWidth
          ? graphObject?.widgetGraphConfig?.y_axis_settings?.[i]?.lineWidth
          : 3;

        lineStyle = graphObject?.metadata?.chart?.chart_settings
          ?.y_axes_settings?.[i]?.lineStyle
          ? graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[i]
              ?.lineStyle
          : graphObject?.widgetGraphConfig?.y_axis_settings?.[i]?.lineStyle
          ? graphObject?.widgetGraphConfig?.y_axis_settings?.[i]?.lineStyle
          : "Solid";
      }
      let dataColor;
      // if we are getting color from backend
      if (
        d?.color !== undefined &&
        d?.color !== "#000000" &&
        d?.color !== null
      ) {
        dataColor = d?.color;
      } else {
        // if user has palette
        // gloabl chart color replaced by user palette
        if (j >= lengthOfColors) {
          noOfIterationInColors = parseInt(j / lengthOfColors);
          let rem = parseInt(j % lengthOfColors);
          dataColor =
            noOfIterationInColors > 0
              ? newColorShadeInHex6(
                  selectedColorArray[rem],
                  parseInt(noOfIterationInColors * 5)
                )
              : selectedColorArray[j];
        } else {
          dataColor = selectedColorArray[j];
        }
        j++;
      }
      if (d?.attribute === "Rating") {
        multiData.push({
          name: `${d?.name} ⭐`,
          data: d?.y,
          color: dataColor,
          dashStyle: lineStyle,
          lineWidth: lineWidth,
          // shadow: {
          //       color: d?.color != undefined && d?.color != "#000000"
          //       ? d?.color
          //       : globalChartColor[i],
          //       width: 10,
          //       opacity: 1,
          //       offsetX: 0,
          //       offsetY: 0
          //   }
        });
      } else {
        multiData.push({
          name: d?.name,
          data: d?.y,
          color: dataColor,
          dashStyle: lineStyle,
          lineWidth: lineWidth,
          //  shadow: {
          //         color: d?.color != undefined && d?.color != "#000000"
          //         ? d?.color
          //         : globalChartColor[i],
          //         width: 10,
          //         opacity: 1,
          //         offsetX: 0,
          //         offsetY: 0
          //     }
        });
      }
    });
    let date_check = moment(
      graphObject?.single && graphObject?.single?.[0]?.x[0]
    ).isValid();
    const xaxisDataList = [];
    graphObject?.single?.[0]?.x?.map((el, i) => {
      xaxisDataList.push(`${el} ⭐`);
    });
    const durationType =
      (getWindowLocationPath()?.toLowerCase() ===
        "widgetmaker"?.toLowerCase() ||
        getWindowLocationPath()?.toLowerCase() ===
          "edit-widget"?.toLowerCase() ||
        graphObject?.dashboard ||
        graphObject?.screen ||
        graphObject?.panes?.[graphObject?.index]?.is_widget_maker_open_obj
          ?.open_flag) &&
      handleDateFormat(
        graphObject?.panes,
        graphObject?.index,
        graphObject?.section_index,
        graphObject?.widget_index,
        graphObject?.metadata,
        graphObject?.metadata?.x_axis?.attribute,
        graphObject?.xaxis,
        graphObject?.metadata?.x_axis?.date_part,
        graphObject?.single?.[0]?.x,
        graphObject?.section,
        graphObject?.template_widget,
        graphObject?.shareTemplateData,
        graphObject?.pdfDownloadStatus,
        graphObject?.isScheduleReportOpen,
        graphObject?.screen,
        graphObject?.template_details,
        graphObject?.isDeepDive,
        graphObject?.dateAggregationOfThisGraph,
        graphObject?.deepdiveObject,
        null, //graphObject?.yAxesDateAttr,
        graphObject?.isDeepDiveDashboardView,
        graphObject?.deepdiveDashboardData,
        graphObject?.deepdiveDashboardTemplateData
      );
    let x_date_part =
      getWindowLocationPath()?.toLowerCase() ===
        "CommandCenter"?.toLowerCase() &&
      graphObject?.isDeepDive &&
      graphObject?.dateAggregationOfThisGraph &&
      graphObject?.deepdiveObject?.start_date
        ? null
        : graphObject?.metadata?.x_axis?.date_part;
    let x_date_agg =
      getWindowLocationPath()?.toLowerCase() ===
        "CommandCenter"?.toLowerCase() &&
      graphObject?.isDeepDive &&
      graphObject?.dateAggregationOfThisGraph &&
      graphObject?.deepdiveObject?.start_date
        ? graphObject?.dateAggregationOfThisGraph
        : graphObject?.metadata?.x_axis?.date_aggregation;
    try {
      if (
        isChartTypeChange &&
        (graphObject?.chartGlobalDataStacked ||
          graphObject?.chartGlobalDataStacked == undefined)
      ) {
        if (multiData && multiData?.length > 0) {
          //multiData = structuredClone(multiData)?.reverse();
          multiData = multiData?.slice()?.reverse();
        }
      }
    } catch (error) {
      console.error("Error While convert dataset to reverse");
    }
    const obj = {
      isChartTypeChange: isChartTypeChange,
      chart: {
        //type: graphObject?.type,
        type: "spline",
        reflow: true,
        height: graphObject?.style?.height,
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          fontSize: "1.6rem",
        },
      },
      title: {
        text: "",
      },
      yAxis: {
        min:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? 0.1
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? 0.1
            : flrSeriesExists
            ? 0
            : undefined, //aiLogScale:aiLogScale!==null? aiLogScale : log,
        minTickInterval: flrSeriesExists ? 60 : undefined,
        type:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? "logarithmic"
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? "logarithmic"
            : "", //aiLogScale:aiLogScale!==null? aiLogScale : log,
        title: {
          text:
            updatedYAxisLabel?.length > 0
              ? updatedYAxisLabel
              : graphObject?.yaxis,
          enabled: true,
          style: {
            fontWeight: "bold",
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
        },
        labels: {
          formatter: flrSeriesExists
            ? function () {
                return SecondstoHumanReadable(this?.value, true);
              }
            : function () {
                return kFormatter(this.value);
              },
          style: {
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
        },
        allowDecimals: false,
        //endOnTick: false,
      },
      xAxis: {
        type: graphObject?.xAxisScale === "log" ? "logarithmic" : "",
        title: {
          text:
            getWindowLocationPath()?.toLowerCase() ===
              "CommandCenter"?.toLowerCase() &&
            graphObject?.isDeepDive &&
            graphObject?.dateAggregationOfThisGraph &&
            graphObject?.deepdiveObject?.start_date
              ? !x_date_part && !x_date_agg
                ? "Date"
                : !x_date_part && x_date_agg === "1h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "2h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "3h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "4h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "6h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "8h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "12h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "ww"
                ? "Week of Year"
                : !x_date_part && x_date_agg === "M"
                ? "Month of Year"
                : !x_date_part && x_date_agg === "Y"
                ? "Years"
                : x_date_part === null || x_date_part === "D"
                ? "Date"
                : x_date_part === "1h"
                ? "Hour of Day"
                : x_date_part === "2h"
                ? "Hour of Day"
                : x_date_part === "3h"
                ? "Hour of Day"
                : x_date_part === "4h"
                ? "Hour of Day"
                : x_date_part === "6h"
                ? "Hour of Day"
                : x_date_part === "8h"
                ? "Hour of Day"
                : x_date_part === "12h"
                ? "Hour of Day"
                : x_date_part === "M"
                ? "Month of Year"
                : x_date_part === "D"
                ? "Date"
                : x_date_part === "hh"
                ? "Hour of Day"
                : x_date_part === "dd"
                ? "Day of Month"
                : x_date_part === "dw"
                ? "Day of Week"
                : x_date_part === "mm"
                ? "Month of Year"
                : x_date_part === "ww"
                ? "Week of Year"
                : x_date_part === "qq"
                ? "Quarter of Year"
                : x_date_part
              : graphObject?.metadata?.x_axis?.attribute &&
                graphObject?.graphConditionConfig?.[
                  `date-attributes`
                ]?.includes(graphObject?.metadata?.x_axis?.attribute)
              ? authParams?.screen_id &&
                graphObject?.screen?.selected_duration_type === "15Minutes"
                ? "Minutes"
                : !x_date_part && !x_date_agg
                ? "Date"
                : !x_date_part && x_date_agg === "1h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "2h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "3h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "4h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "6h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "8h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "12h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "ww"
                ? "Week of Year"
                : !x_date_part && x_date_agg === "M"
                ? "Month of Year"
                : !x_date_part && x_date_agg === "Y"
                ? "Years"
                : graphObject?.metadata?.x_axis?.date_part === null ||
                  graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "1h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "2h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "3h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "4h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "6h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "8h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "12h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "M"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.metadata?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.metadata?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.metadata?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.metadata?.x_axis?.date_part
              : !graphObject?.metadata?.x_axis?.attribute &&
                (graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                  "RecordDate" ||
                  graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                    "CreatedDate")
              ? graphObject?.widgetGraphConfig?.x_axis?.date_part === null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part
              : graphObject?.xaxis,
          enabled: true,
          style: {
            fontWeight: "bold",
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
        },
        categories:
          graphObject?.dashboard &&
          ticketOverviewId === "000-000-004" &&
          // diffDay < 6 &&
          graphObject?.ticket_chart_type !== "W" &&
          graphObject?.ticket_chart_type !== "M" &&
          graphObject?.ticket_chart_type !== "Y" &&
          (graphObject?.ticket_status_duration === 60 ||
            graphObject?.ticket_status_duration === 120 ||
            graphObject?.ticket_status_duration === 180 ||
            graphObject?.ticket_status_duration === 240 ||
            graphObject?.ticket_status_duration === 360 ||
            graphObject?.ticket_status_duration === 480 ||
            graphObject?.ticket_status_duration === 720)
            ? graphObject?.single[0]?.x?.map((e) =>
                moment(e).format("D MMM H:mm")
              )
            : graphObject?.dashboard &&
              ticketOverviewId === "000-000-004" &&
              // diffDay >= 6 &&
              graphObject?.ticket_chart_type !== "W" &&
              graphObject?.ticket_chart_type !== "M" &&
              graphObject?.ticket_chart_type !== "Y" &&
              graphObject?.ticket_chart_type === "D"
            ? graphObject?.single?.[0]?.x?.map((e) => moment(e).format("D MMM"))
            : graphObject?.dashboard &&
              ticketOverviewId === "000-000-004" &&
              diffDay >= 6 &&
              graphObject?.ticket_chart_type === "M"
            ? graphObject?.single?.[0]?.x?.map((e) =>
                moment(e).format("MMM YYYY")
              )
            : graphObject?.dashboard &&
              ticketOverviewId === "000-000-004" &&
              diffDay >= 6 &&
              (graphObject?.ticket_chart_type === "W" ||
                graphObject?.ticket_chart_type === "Y")
            ? graphObject?.single?.[0]?.x?.map((e) => e)
            : // : (graphObject?.metadata &&
            //     graphObject?.metadata?.x_axis?.attribute === "RecordDate") ||
            //   (graphObject?.xaxis === "Date" &&
            //     graphObject?.metadata?.x_axis?.date_part == null)
            // ? graphObject?.single[0]?.x?.map((e) =>
            //     moment(e).format("MMM DD YYYY")
            //   )

            graphObject?.metadata &&
              (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) ||
                graphObject?.xaxis === "Date") &&
              ((graphObject?.pdfDownloadStatus &&
                graphObject?.metadata?.x_axis?.date_part !== null &&
                graphObject?.metadata?.x_axis?.date_part !== "D") ||
                ((!graphObject?.pdfDownloadStatus ||
                  graphObject?.isScheduleReportOpen) &&
                  (graphObject?.metadata?.x_axis?.date_part == null ||
                    graphObject?.metadata?.x_axis?.date_part == "D"))) &&
              durationType
            ? durationType
            : getWindowLocationPath()?.toLowerCase() ===
                "CommandCenter"?.toLowerCase() &&
              graphObject?.isDeepDive &&
              graphObject?.dateAggregationOfThisGraph &&
              graphObject?.deepdiveObject?.start_date
            ? durationType
            : graphObject?.metadata &&
              (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) ||
                graphObject?.xaxis === "Date") &&
              graphObject?.metadata?.x_axis?.date_part !== null &&
              graphObject?.metadata?.x_axis?.date_part !== "D"
            ? graphObject?.single?.[0]?.x?.map((e) => e)
            : graphObject?.xaxis === "Date" &&
              (graphObject?.widgetGraphConfig?.x_axis?.date_part == null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part == "D")
            ? graphObject?.single?.[0]?.x?.map((e) =>
                moment(e).format("MMM DD YYYY")
              )
            : graphObject?.xaxis === "Review Ratings" && xaxisDataList
            ? xaxisDataList
            : graphObject?.single?.[0]?.x,
        labels: {
          useHTML: true, // Enable HTML rendering
          formatter: function () {
            if (date_check || graphObject?.pdfDownloadStatus) {
              return this.value; // No formatting needed in this case
            }

            const text = String(this.value);
            const maxLengthPerLine = 24;

            if (text?.length <= maxLengthPerLine) {
              return `<div style="text-align: right;">${text}</div>`; // Single-line, right-aligned
            }

            // Find the last space before maxLengthPerLine for a natural word break
            let firstLineEnd = text?.lastIndexOf(" ", maxLengthPerLine);
            if (firstLineEnd === -1) firstLineEnd = maxLengthPerLine; // If no space found, force split

            const firstLine = text?.slice(0, firstLineEnd);
            let secondLine = text?.slice(firstLineEnd + 1);

            // Truncate the second line if it exceeds maxLengthPerLine
            if (secondLine?.length > maxLengthPerLine) {
              secondLine = secondLine?.slice(0, maxLengthPerLine - 3) + "...";
            }

            // Return wrapped text with right alignment using div and HTML
            return `<div style="text-align: right;">${firstLine}<br>${secondLine}</div>`;
          },
          rotation: graphObject?.single?.[0]?.x?.length > 5 ? -90 : 0,
          style: {
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
        },
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      tooltip: {
        enabled: true,
        borderWidth: 0,
        useHTML: true,
        shadow: false,
        backgroundColor: "transparent",
        // headerFormat:
        //   '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">{series.name}</span> <div class="d-flex align-items-center"><span class="text-Darkgray mr-1"> {point.key} </span> ',
        // //pointFormat: ",{point.y})"#3b5175,
        // pointFormatter: function () {
        //   let clr = this?.color
        //     ? this?.color?.stops?.length
        //       ? this?.color?.stops?.[0]?.[1]
        //       : this?.color
        //     : this?.series?.color?.stops?.length
        //     ? this?.series?.color?.stops?.[0]?.[1]
        //     : this?.series?.color;
        //   return (
        //     '<span class="stackbar__tooltip" style="background-color:' +
        //     clr +
        //     ';">' +
        //     (flrSeriesExists ? SecondstoHumanReadable(this?.y) : this?.y) +
        //     "</span></div></div>"
        //   );
        // },
        formatter: function () {
          let clr = this?.color
            ? this?.color?.stops?.length
              ? this?.color?.stops?.[0]?.[1]
              : this?.color
            : this?.series?.color?.stops?.length
            ? this?.series?.color?.stops?.[0]?.[1]
            : this?.series?.color;
          return (
            '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">' +
            getNameWithChannelIconIfExists(this?.series?.name) +
            '</span> <div class="d-flex align-items-center"><span class="text-Darkgray mr-1">' +
            (this?.key ? this?.key : this?.point?.category) +
            " </span> " +
            '<span class="stackbar__tooltip" style="background-color:' +
            clr +
            ';">' +
            (flrSeriesExists
              ? SecondstoHumanReadable(this?.y)
              : this?.y?.toLocaleString()) +
            "</span></div></div>"
          );
        },
        style: {
          fontSize: "1.4rem",
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      plotOptions: {
        series: {
          label: {
            connectorAllowed: false,
          },
          lineWidth: 3,
          marker: {
            enabled:
              graphObject?.chartMarkers === false
                ? graphObject?.chartMarkers
                : true,
            //radius:graphObject?.chartMarkers === true ? 4 : 0
            symbol: "circle",
            fillColor: "#FFFFFF",
            lineWidth: 3,
            lineColor: null, // inherit from series
          },
          showInLegend: graphObject?.metadata?.chart
            ? graphObject?.metadata?.chart?.chart_settings?.chart_property
                ?.show_legend
            : graphObject?.metadata?.chart_property?.show_legend,
          dataLabels: {
            enabled: graphObject?.chartGlobalDataLabel ? true : false,
            style: {
              fontWeight: "500",
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
              fontSize: "1rem",
              fontFamily: '"Roboto", sans-serif',
              textOutline: "none",
            },
            formatter: flrSeriesExists
              ? function () {
                  return (this.y / 60).toFixed(0) + " mins";
                }
              : function () {
                  return kFormatter(this.y);
                },
          },
        },
        spline: {
          animation: graphObject?.pdfDownloadStatus ? false : true,
          stacking:
            isChartTypeChange &&
            (graphObject?.chartGlobalDataStacked ||
              graphObject?.chartGlobalDataStacked == undefined)
              ? "normal"
              : undefined,
        },
      },
      series: multiData,
      exporting: {
        filename: graphObject?.widget_name
          ? graphObject?.widget_name
          : graphObject?.type,
        chartOptions: {
          chart: {
            style: {
              fontSize: "16px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          tooltip: {
            style: {
              fontSize: "14px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          plotOptions: {
            series: {
              dataLabels: {
                style: {
                  fontSize: "10px",
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: "500",
                },
              },
            },
          },
          legend: {
            itemStyle: {
              fontSize: "13px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
        enabled:
          graphObject?.pdfDownloadStatus ||
          graphObject?.pptDownloadStatus ||
          graphObject?.isScheduleReportOpen ||
          authParams?.screen_id
            ? false
            : true,
        menuItemDefinitions: {
          // Custom definition
          xsl: {
            onclick: () => {
              const excel = new Excel();
              let columns = [
                {
                  title: graphObject?.xaxis,
                  dataIndex: graphObject?.xaxis,
                },
              ];
              let datasource = [];
              let lengthOfMultiData = multiData?.length;
              let lengthOfColumns = lengthOfMultiData + 1;
              for (let i = 0; i < lengthOfColumns; i++) {
                if (i == 0) {
                  graphObject?.single?.[0]?.x?.forEach((xElem, x_index) => {
                    datasource[x_index] = { [columns[i]?.title]: xElem };
                  });
                } else {
                  if (multiData[i - 1]?.name?.includes("|$|")) {
                    let ProfileName = multiData[i - 1]?.name
                      ?.split("|$|")?.[1]
                      ?.trim();
                    let channelName = multiData[i - 1]?.name
                      ?.split("|$|")?.[0]
                      ?.trim();
                    columns[i] = {
                      title: channelName + " | " + ProfileName,
                      dataIndex: multiData[i - 1]?.name,
                    };
                  } else if (multiData[i - 1]?.name?.includes("|G|")) {
                    let genderName = multiData[i - 1]?.name
                      ?.split("|G|")?.[1]
                      ?.trim();

                    columns[i] = {
                      title: genderName,
                      dataIndex: multiData[i - 1]?.name,
                    };
                  } else if (multiData[i - 1]?.name?.includes("|C|")) {
                    let countryName = multiData[i - 1]?.name
                      ?.split("|C|")?.[1]
                      ?.trim();

                    columns[i] = {
                      title: countryName,
                      dataIndex: multiData[i - 1]?.name,
                    };
                  } else {
                    columns[i] = {
                      title: multiData[i - 1]?.name,
                      dataIndex: multiData[i - 1]?.name,
                    };
                  }
                  multiData[i - 1]?.data?.forEach((data, data_index) => {
                    datasource[data_index] = {
                      ...datasource[data_index],
                      [columns[i]?.dataIndex]: flrSeriesExists
                        ? SecondstoHumanReadable(data)
                        : isNaN(Number(data))
                        ? data?.toString().includes("NaN")
                          ? Number(0)
                          : data
                        : Number(data), // data
                    };
                  });
                }
              }
              excel
                .addSheet("test")
                .addColumns(columns)
                .addDataSource(datasource, {
                  str2Percent: true,
                })
                .saveAs(
                  `${
                    graphObject?.widget_name
                      ? graphObject?.widget_name
                      : graphObject?.type
                  }.xlsx`
                );
            },
            text: "Download excel sheet",
          },
        },
        buttons: {
          contextButton: {
            menuItems:
              graphObject?.xaxis && graphObject?.yaxis
                ? [
                    "downloadPNG",
                    "downloadJPEG",
                    "downloadSVG",
                    "separator",
                    "xsl",
                  ]
                : ["downloadPNG", "downloadJPEG", "downloadSVG"],
            buttonSpacing: 1,
            height: 20,
            symbolSize: 12,
            symbolStrokeWidth: 2,
            symbolX: 10.5,
            symbolY: 10.5,
            width: 20,
            x: -2,
            y: -12,
          },
        },
      },
      credits: {
        enabled: false,
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              legend: {
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
              },
            },
          },
        ],
      },
      legend: graphObject?.legend_position
        ? {
            reversed:
              isChartTypeChange &&
              (graphObject?.chartGlobalDataStacked ||
                graphObject?.chartGlobalDataStacked == undefined)
                ? true
                : false, // This will reverse the order of the legend
            itemWidth:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? undefined
                : 200,
            layout:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "horizontal"
                : "vertical",
            align:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "center"
                : graphObject?.legend_position,
            verticalAlign:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? graphObject?.legend_position
                : "middle",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
            },
            itemHoverStyle: {
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          }
        : {
            reversed:
              isChartTypeChange &&
              (graphObject?.chartGlobalDataStacked ||
                graphObject?.chartGlobalDataStacked == undefined)
                ? true
                : false, // This will reverse the order of the legend
            itemWidth: undefined,
            enabled: true,
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
            },
            itemHoverStyle: {
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          },
    };
    return obj;
  }
};

export const getArea = (graphObject, authParams) => {
  // const plotOptionsKey = graphObject?.chartShow3d ? "area" : "areaspline";
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  if (graphObject?.single[0]?.y_axis) {
    let obj = { ...graphObject };
    obj.single = graphObject.single[0];
    return getCombination(obj, authParams);
  } else {
    let ySeriesFromConfig = graphObject?.metadata?.y_series
      ? graphObject?.metadata?.y_series
      : Object?.entries(graphObject?.widgetGraphConfig?.y_axis);

    let graphConditionConfig = [
      ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
      ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
    ];

    let flrSeriesExists = false;
    ySeriesFromConfig?.forEach((yseries, index) => {
      if (yseries?.attribute) {
        if (yseries && graphConditionConfig?.includes(yseries?.attribute))
          flrSeriesExists = true;
      } else if (
        yseries &&
        graphConditionConfig?.includes(yseries[1]?.attribute)
      )
        flrSeriesExists = true;
    });
    let seriesData = [];
    let seriesColorsFlag = false;
    let zoneColors = [];
    let date_check = moment(
      graphObject?.single && graphObject?.single[0]?.x[0]
    ).isValid();
    // zoneColors.push({
    // 	value:yElem,
    // 	color:single[0]?.color != undefined ? single[0]?.color[i]:'',
    // });
    if (
      graphObject?.single[0]?.colors != undefined &&
      graphObject?.single[0]?.colors?.length > 0
    ) {
      seriesColorsFlag = true;
      seriesData = graphObject?.single[0]?.y?.map((yElem, i) => {
        return {
          y: yElem,
          color:
            graphObject?.single[0]?.colors != undefined
              ? graphObject?.single[0]?.colors[i] != "#000000"
                ? graphObject?.single[0]?.colors[i]
                : ""
              : "",
        };
      });
    }
    const xaxisDataList = [];
    graphObject?.single[0]?.x?.map((el, i) => {
      xaxisDataList.push(`${el} ⭐`);
    });

    const durationType =
      (getWindowLocationPath()?.toLowerCase() ===
        "widgetmaker"?.toLowerCase() ||
        getWindowLocationPath()?.toLowerCase() ===
          "edit-widget"?.toLowerCase() ||
        graphObject?.dashboard ||
        graphObject?.screen ||
        graphObject?.panes?.[graphObject?.index]?.is_widget_maker_open_obj
          ?.open_flag) &&
      handleDateFormat(
        graphObject?.panes,
        graphObject?.index,
        graphObject?.section_index,
        graphObject?.widget_index,
        graphObject?.metadata,
        graphObject?.metadata?.x_axis?.attribute,
        graphObject?.xaxis,
        graphObject?.metadata?.x_axis?.date_part,
        graphObject?.single[0]?.x,
        graphObject?.section,
        graphObject?.template_widget,
        graphObject?.shareTemplateData,
        graphObject?.pdfDownloadStatus,
        graphObject?.isScheduleReportOpen,
        graphObject?.screen,
        graphObject?.template_details,
        false, // graphObject?.isDeepDive,
        null, //graphObject?.dateAggregationOfThisGraph,
        null, //graphObject?.deepdiveObject,
        null, //graphObject?.yAxesDateAttr,
        graphObject?.isDeepDiveDashboardView,
        graphObject?.deepdiveDashboardData,
        graphObject?.deepdiveDashboardTemplateData
      );

    let x_date_part = graphObject?.metadata?.x_axis?.date_part;
    let x_date_agg = graphObject?.metadata?.x_axis?.date_aggregation;

    const obj = {
      chart: {
        //type: graphObject?.type,
        // type: graphObject?.chartShow3d ? "area" : "areaspline",
        // options3d: {
        //   enabled: graphObject?.chartShow3d,
        //   alpha: graphObject?.chart3dOption?.alpha,
        //   beta: graphObject?.chart3dOption?.beta,
        //   depth: graphObject?.chart3dOption?.depth,
        // },
        type: "areaspline",
        reflow: true,
        height: graphObject?.style?.height,
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          fontSize: "1.6rem",
        },
      },
      title: {
        text: "",
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      xAxis: {
        title: {
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
            fontWeight: "bold",
          },
          text:
            graphObject?.metadata?.x_axis?.attribute &&
            graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
              graphObject?.metadata?.x_axis?.attribute
            )
              ? authParams?.screen_id &&
                graphObject?.screen?.selected_duration_type === "15Minutes"
                ? "Minutes"
                : !x_date_part && !x_date_agg
                ? "Date"
                : !x_date_part && x_date_agg === "1h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "2h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "3h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "4h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "6h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "8h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "12h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "ww"
                ? "Week of Year"
                : !x_date_part && x_date_agg === "M"
                ? "Month of Year"
                : !x_date_part && x_date_agg === "Y"
                ? "Years"
                : graphObject?.metadata?.x_axis?.date_part === null ||
                  graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "1h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "2h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "3h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "4h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "6h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "8h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "12h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "M"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.metadata?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.metadata?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.metadata?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.metadata?.x_axis?.date_part
              : !graphObject?.metadata?.x_axis?.attribute &&
                (graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                  "RecordDate" ||
                  graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                    "CreatedDate")
              ? graphObject?.widgetGraphConfig?.x_axis?.date_part === null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part
              : graphObject?.xaxis,
          enabled: true,
        },
        allowDecimals: false,
        categories:
          graphObject?.metadata &&
          (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
            graphObject?.metadata?.x_axis?.attribute
          ) ||
            graphObject?.xaxis === "Date") &&
          ((graphObject?.pdfDownloadStatus &&
            graphObject?.metadata?.x_axis?.date_part !== null &&
            graphObject?.metadata?.x_axis?.date_part !== "D") ||
            ((!graphObject?.pdfDownloadStatus ||
              graphObject?.isScheduleReportOpen) &&
              (graphObject?.metadata?.x_axis?.date_part == null ||
                graphObject?.metadata?.x_axis?.date_part == "D"))) &&
          durationType
            ? durationType
            : graphObject?.metadata &&
              (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) ||
                graphObject?.xaxis === "Date") &&
              graphObject?.metadata?.x_axis?.date_part !== null &&
              graphObject?.metadata?.x_axis?.date_part !== "D"
            ? graphObject?.single[0]?.x?.map((e) => e)
            : // (graphObject?.metadata &&
            //   graphObject?.metadata?.x_axis?.attribute === "RecordDate") ||
            // (graphObject?.xaxis === "Date" &&
            //   graphObject?.metadata?.x_axis?.date_part == null)
            //   ? graphObject?.single[0]?.x?.map((e) =>
            //       moment(e).format("MMM DD YYYY")
            //     )
            graphObject?.xaxis === "Date" &&
              (graphObject?.widgetGraphConfig?.x_axis?.date_part == null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part == "D")
            ? graphObject?.single[0]?.x?.map((e) =>
                moment(e).format("MMM DD YYYY")
              )
            : graphObject?.xaxis === "Review Ratings" && xaxisDataList
            ? xaxisDataList
            : graphObject?.single[0]?.x,
        labels: {
          useHTML: true, // Enable HTML rendering
          formatter: function () {
            if (date_check || graphObject?.pdfDownloadStatus) {
              return this.value; // No formatting needed in this case
            }

            const text = String(this.value);
            const maxLengthPerLine = 24;

            if (text?.length <= maxLengthPerLine) {
              return `<div style="text-align: right;">${text}</div>`; // Single-line, right-aligned
            }

            // Find the last space before maxLengthPerLine for a natural word break
            let firstLineEnd = text?.lastIndexOf(" ", maxLengthPerLine);
            if (firstLineEnd === -1) firstLineEnd = maxLengthPerLine; // If no space found, force split

            const firstLine = text?.slice(0, firstLineEnd);
            let secondLine = text?.slice(firstLineEnd + 1);

            // Truncate the second line if it exceeds maxLengthPerLine
            if (secondLine?.length > maxLengthPerLine) {
              secondLine = secondLine?.slice(0, maxLengthPerLine - 3) + "...";
            }

            // Return wrapped text with right alignment using div and HTML
            return `<div style="text-align: right;">${firstLine}<br>${secondLine}</div>`;
          },
          rotation: graphObject?.single[0]?.x?.length > 5 ? -90 : 0,
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          //padding:0,
        },
        tickmarkPlacement: "on",
        gridLineWidth: 1,
        tickWidth: 1,
        tickColor: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
      },
      yAxis: {
        tickAmount:
          getWindowLocationPath()?.toLowerCase() ===
          "CommandCenter"?.toLowerCase()
            ? 5
            : null,
        style: {
          color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
        },
        min:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? 0.1
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? 0.1
            : flrSeriesExists
            ? 0
            : undefined, //aiLogScale:aiLogScale!==null? aiLogScale : log,
        minTickInterval: flrSeriesExists ? 60 : undefined,

        type:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? "logarithmic"
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? "logarithmic"
            : "", //aiLogScale:aiLogScale!==null? aiLogScale : log,
        title: {
          text: graphObject?.yaxis,
          style: {
            fontWeight: "bold",
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
        },
        labels: {
          formatter: flrSeriesExists
            ? function () {
                return SecondstoHumanReadable(this?.value, true);
              }
            : function () {
                return kFormatter(this.value);
              },
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
        },
        allowDecimals: false,
        tickColor: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
      },

      // tooltip: {
      // 	pointFormat: "{series.name} <b>{point.y:,.0f}</b><br/> {point.x}",
      // },
      tooltip: {
        enabled: true,
        useHTML: true,
        borderWidth: 0,
        shadow: false,
        backgroundColor: "transparent",
        // headerFormat:
        //   "<div class='tooltip_chart fontSize-14 d-flex align-items-center'><span class='text-Darkgray font-weight600 mr-1'>{point.key} </span> ",
        // pointFormatter: function () {
        //   let clr = this?.color
        //     ? this?.color?.stops?.length
        //       ? this?.color?.stops?.[0]?.[1]
        //       : this?.color
        //     : this?.series?.color?.stops?.length
        //     ? this?.series?.color?.stops?.[0]?.[1]
        //     : this?.series?.color;
        //   return (
        //     '<span class="stackbar__tooltip" style="background-color:' +
        //     clr +
        //     ';">' +
        //     (flrSeriesExists ? SecondstoHumanReadable(this?.y) : this?.y) +
        //     "</span></div>"
        //   );
        // },
        formatter: function () {
          let clr = this?.color
            ? this?.color?.stops?.length
              ? this?.color?.stops?.[0]?.[1]
              : this?.color
            : this?.series?.color?.stops?.length
            ? this?.series?.color?.stops?.[0]?.[1]
            : this?.series?.color;
          return (
            "<div class='tooltip_chart fontSize-14 d-flex align-items-center'><span class='text-Darkgray font-weight600 mr-1'> " +
            getNameWithChannelIconIfExists(
              this?.key ? this?.key : this?.point?.category
            ) +
            "</span>" +
            '<span class="stackbar__tooltip" style="background-color:' +
            clr +
            ';">' +
            (flrSeriesExists
              ? SecondstoHumanReadable(this?.y)
              : this?.y?.toLocaleString()) +
            "</span></div>"
          );
        },
        style: {
          fontSize: "1.4rem",
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ), //"#3b5175",
        },
      },
      plotOptions: {
        // [plotOptionsKey]: {
        // pointStart: 1940,
        // depth: 100,
        areaspline: {
          animation: graphObject?.pdfDownloadStatus ? false : true,
          lineWidth: 3,
          marker: {
            enabled:
              graphObject?.chartMarkers === false
                ? graphObject?.chartMarkers
                : true,
            //radius:graphObject?.chartMarkers === true ? 4 : 0
            symbol: "circle",
            fillColor: "#FFFFFF",
            lineWidth: 3,
            lineColor: null, // inherit from series
          },
          showInLegend: false,
          dataLabels: {
            enabled: graphObject?.chartGlobalDataLabel ? true : false,
            style: {
              fontWeight: "500",
              // color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
              fontSize: "1rem",
              fontFamily: '"Roboto", sans-serif',
              textOutline: `0.2rem ${dynamicHighchartOutlineColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              )}`,
            },
            formatter: flrSeriesExists
              ? function () {
                  return (this.y / 60).toFixed(0) + " mins";
                }
              : function () {
                  return kFormatter(this.y);
                },
          },
        },
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          name: graphObject?.single[0]?.name
            ? graphObject?.single[0]?.name
            : "Timeline",
          data: seriesColorsFlag ? seriesData : graphObject?.single[0]?.y,
          dashStyle: "solid",
          lineWidth: 3,
          color: !seriesColorsFlag
            ? graphObject?.single[0]?.color != undefined &&
              graphObject?.single[0]?.color != "#000000"
              ? `${graphObject?.single[0]?.color}99`
              : `${selectedColorArray[0]}99`
            : `${selectedColorArray[0]}99`,
          //pointIntervalUnit: 1,
          //zoneAxis:seriesColorsFlag?'x':'y',
          //zones:seriesColorsFlag ? zoneColors:[{}],
          fillColor: {
            linearGradient: [0, 0, 0, 300],
            stops: [
              [
                0,
                graphObject?.single[0]?.color != undefined &&
                graphObject?.single[0]?.color != "#000000"
                  ? `${graphObject?.single[0]?.color}99`
                  : `${selectedColorArray[0]}99`,
              ],
              [
                1,
                graphObject?.single[0]?.color != undefined &&
                graphObject?.single[0]?.color != "#000000"
                  ? `${graphObject?.single[0]?.color}1a`
                  : `${selectedColorArray[0]}1a`,
              ],
            ],
          },
        },
      ],
      exporting: {
        filename: graphObject?.widget_name
          ? graphObject?.widget_name
          : graphObject?.type,
        enabled:
          graphObject?.pdfDownloadStatus ||
          graphObject?.pptDownloadStatus ||
          graphObject?.isScheduleReportOpen ||
          authParams?.screen_id
            ? false
            : true,
        menuItemDefinitions: {
          // Custom definition
          xsl: {
            onclick: () => {
              const excel = new Excel();
              let columns = [
                {
                  title: graphObject?.xaxis,
                  dataIndex: graphObject?.xaxis,
                },
                {
                  title: graphObject?.yaxis,
                  dataIndex: graphObject?.yaxis,
                },
              ];

              let datasource = graphObject?.single[0]?.y?.map((yElem, i) => {
                return {
                  [columns[0]?.title]: graphObject?.single[0]?.x[i],
                  [columns[1]?.title]: flrSeriesExists
                    ? SecondstoHumanReadable(yElem)
                    : isNaN(Number(yElem))
                    ? yElem?.toString().includes("NaN")
                      ? Number(0)
                      : yElem
                    : Number(yElem), // yElem
                };
              });
              excel
                .addSheet("test")
                .addColumns(columns)
                .addDataSource(datasource, {
                  str2Percent: true,
                })
                .saveAs(
                  `${
                    graphObject?.widget_name
                      ? graphObject?.widget_name
                      : graphObject?.type
                  }.xlsx`
                );
            },
            text: "Download excel sheet",
          },
        },
        buttons: {
          contextButton: {
            menuItems:
              graphObject?.xaxis && graphObject?.yaxis
                ? [
                    "downloadPNG",
                    "downloadJPEG",
                    "downloadSVG",
                    "separator",
                    "xsl",
                  ]
                : ["downloadPNG", "downloadJPEG", "downloadSVG"],
            buttonSpacing: 1,
            height: 20,
            symbolSize: 12,
            symbolStrokeWidth: 2,
            symbolX: 10.5,
            symbolY: 10.5,
            width: 20,
            x: -2,
            y: -12,
          },
        },

        chartOptions: {
          chart: {
            style: {
              fontSize: "16px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          tooltip: {
            style: {
              fontSize: "14px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          plotOptions: {
            areaspline: {
              dataLabels: {
                style: {
                  fontSize: "10px",
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: "500",
                },
              },
            },
          },
          legend: {
            itemStyle: {
              fontSize: "13px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
      },
      legend: graphObject?.legend_position
        ? {
            itemWidth:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? undefined
                : 200,
            layout:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "horizontal"
                : "vertical",
            align:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "center"
                : graphObject?.legend_position,
            verticalAlign:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? graphObject?.legend_position
                : "middle",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            },
            itemHoverStyle: {
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          }
        : {
            itemWidth: undefined,
            enabled: true,
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: "#3b5175",
            },
            itemHoverStyle: {
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          },
    };
    return obj;
  }
};

export const getMultiArea = (graphObject, authParams) => {
  // const plotOptionsKey = graphObject?.chartShow3d ? "area" : "areaspline";
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  if (graphObject?.single[0]?.y_axis) {
    let obj = { ...graphObject };
    obj.single = graphObject.single[0];
    return getCombination(obj, authParams);
  } else {
    let arr = [];
    arr = graphObject?.metadata?.y_series
      ? graphObject?.metadata?.y_series
      : Object.keys(graphObject?.widgetGraphConfig?.y_axis).map(
          (key) => graphObject?.widgetGraphConfig?.y_axis[key]
        );
    let filteredArray = arr?.filter((d) => d.attribute);
    let updatedYAxisLabel = [];
    filteredArray?.map((d, i) => {
      if (i >= 1) {
        updatedYAxisLabel = updatedYAxisLabel + ", " + d.trace_name;
      } else {
        updatedYAxisLabel = updatedYAxisLabel + d.trace_name;
      }
    });

    let ySeriesFromConfig = graphObject?.metadata?.y_series
      ? graphObject?.metadata?.y_series
      : Object?.entries(graphObject?.widgetGraphConfig?.y_axis);
    let flrSeriesExists = false;
    let graphConditionConfig = [
      ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
      ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
    ];

    ySeriesFromConfig?.forEach((yseries, index) => {
      if (yseries?.attribute) {
        if (yseries && graphConditionConfig?.includes(yseries?.attribute))
          flrSeriesExists = true;
      } else if (
        yseries &&
        graphConditionConfig?.includes(yseries[1]?.attribute)
      )
        flrSeriesExists = true;
    });
    let multiData = [];
    let j = 0;
    let noOfIterationInColors = 0;
    let lengthOfColors = selectedColorArray?.length;
    graphObject?.single[0]?.data?.map((d, i) => {
      let dataColor;
      if (
        d?.color !== undefined &&
        d?.color !== "#000000" &&
        d?.color !== null
      ) {
        dataColor = d?.color;
      } else {
        if (j >= lengthOfColors) {
          noOfIterationInColors = parseInt(j / lengthOfColors);
          let rem = parseInt(j % lengthOfColors);
          dataColor =
            noOfIterationInColors > 0
              ? newColorShadeInHex6(
                  selectedColorArray[rem],
                  parseInt(noOfIterationInColors * 5)
                )
              : selectedColorArray[j];
        } else {
          dataColor = selectedColorArray[j];
        }
        j++;
      }
      if (d?.attribute === "Rating") {
        multiData.push({
          name: `${d?.name} ⭐`,
          data: d?.y,
          color: `${dataColor}99`,
          dashStyle: "Solid",
          lineWidth: 3,
          fillColor: {
            linearGradient: [0, 0, 0, 300],
            stops: [
              [0, `${dataColor}99`],
              [1, `${dataColor}1a`],
            ],
          },
        });
      } else {
        multiData.push({
          name: d?.name,
          data: d?.y,
          dashStyle: "Solid",
          lineWidth: 3,
          fillColor: {
            linearGradient: [0, 0, 0, 300],
            stops: [
              [0, `${dataColor}99`],
              [1, `${dataColor}1a`],
            ],
          },
          color: `${dataColor}99`,
        });
      }
    });
    let date_check = moment(
      graphObject?.single && graphObject?.single[0]?.x[0]
    ).isValid();
    const xaxisDataList = [];
    graphObject?.single[0]?.x?.map((el, i) => {
      xaxisDataList.push(`${el} ⭐`);
    });

    let x_date_part = graphObject?.metadata?.x_axis?.date_part;
    let x_date_agg = graphObject?.metadata?.x_axis?.date_aggregation;

    const durationType =
      (getWindowLocationPath()?.toLowerCase() ===
        "widgetmaker"?.toLowerCase() ||
        getWindowLocationPath()?.toLowerCase() ===
          "edit-widget"?.toLowerCase() ||
        graphObject?.dashboard ||
        graphObject?.panes?.[graphObject?.index]?.is_widget_maker_open_obj
          ?.open_flag) &&
      handleDateFormat(
        graphObject?.panes,
        graphObject?.index,
        graphObject?.section_index,
        graphObject?.widget_index,
        graphObject?.metadata,
        graphObject?.metadata?.x_axis?.attribute,
        graphObject?.xaxis,
        graphObject?.metadata?.x_axis?.date_part,
        graphObject?.single[0]?.x,
        graphObject?.section,
        graphObject?.template_widget,
        graphObject?.shareTemplateData,
        graphObject?.pdfDownloadStatus,
        graphObject?.isScheduleReportOpen,
        graphObject?.screen,
        graphObject?.template_details,
        false, // graphObject?.isDeepDive,
        null, //graphObject?.dateAggregationOfThisGraph,
        null, //graphObject?.deepdiveObject,
        null, //graphObject?.yAxesDateAttr,
        graphObject?.isDeepDiveDashboardView,
        graphObject?.deepdiveDashboardData,
        graphObject?.deepdiveDashboardTemplateData
      );
    try {
      if (
        graphObject?.chartGlobalDataStacked ||
        graphObject?.chartGlobalDataStacked == undefined
      ) {
        if (multiData && multiData?.length > 0) {
          //multiData = structuredClone(multiData)?.reverse();
          multiData = multiData?.slice()?.reverse();
        }
      }
    } catch (error) {
      console.error("Error While convert dataset to reverse");
    }
    const obj = {
      chart: {
        //type: graphObject?.type,
        // type: graphObject?.chartShow3d ? "area" : "areaspline",
        // options3d: {
        //   enabled: graphObject?.chartShow3d,
        //   alpha: graphObject?.chart3dOption?.alpha,
        //   beta: graphObject?.chart3dOption?.beta,
        //   depth: graphObject?.chart3dOption?.depth,
        // },
        type: "areaspline",
        reflow: true,
        height: graphObject?.style?.height,
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          fontSize: "1.6rem",
        },
      },
      title: {
        text: "",
      },
      xAxis: {
        style: {
          color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
        },
        title: {
          style: {
            fontWeight: "bold",
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          text:
            graphObject?.metadata?.x_axis?.attribute &&
            graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
              graphObject?.metadata?.x_axis?.attribute
            )
              ? !x_date_part && !x_date_agg
                ? "Date"
                : !x_date_part && x_date_agg === "1h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "2h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "3h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "4h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "6h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "8h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "12h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "ww"
                ? "Week of Year"
                : !x_date_part && x_date_agg === "M"
                ? "Month of Year"
                : !x_date_part && x_date_agg === "Y"
                ? "Years"
                : graphObject?.metadata?.x_axis?.date_part === null ||
                  graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "1h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "2h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "3h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "4h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "6h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "8h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "12h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "M"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.metadata?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.metadata?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.metadata?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.metadata?.x_axis?.date_part
              : !graphObject?.metadata?.x_axis?.attribute &&
                (graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                  "RecordDate" ||
                  graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                    "CreatedDate")
              ? graphObject?.widgetGraphConfig?.x_axis?.date_part === null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part
              : graphObject?.xaxis,
          enabled: true,
        },
        allowDecimals: false,
        categories:
          graphObject?.metadata &&
          (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
            graphObject?.metadata?.x_axis?.attribute
          ) ||
            graphObject?.xaxis === "Date") &&
          ((graphObject?.pdfDownloadStatus &&
            graphObject?.metadata?.x_axis?.date_part !== null &&
            graphObject?.metadata?.x_axis?.date_part !== "D") ||
            ((!graphObject?.pdfDownloadStatus ||
              graphObject?.isScheduleReportOpen) &&
              (graphObject?.metadata?.x_axis?.date_part == null ||
                graphObject?.metadata?.x_axis?.date_part == "D"))) &&
          durationType
            ? durationType
            : graphObject?.metadata &&
              (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) ||
                graphObject?.xaxis === "Date") &&
              graphObject?.metadata?.x_axis?.date_part !== null &&
              graphObject?.metadata?.x_axis?.date_part !== "D"
            ? graphObject?.single[0]?.x?.map((e) => e)
            : // (graphObject?.metadata &&
            //   graphObject?.metadata?.x_axis?.attribute === "RecordDate") ||
            // (graphObject?.xaxis === "Date" &&
            //   graphObject?.metadata?.x_axis?.date_part == null)
            //   ? graphObject?.single[0]?.x?.map((e) =>
            //       moment(e).format("MMM DD YYYY")
            //     )
            graphObject?.xaxis === "Date" &&
              (graphObject?.widgetGraphConfig?.x_axis?.date_part == null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part == "D")
            ? graphObject?.single[0]?.x?.map((e) =>
                moment(e).format("MMM DD YYYY")
              )
            : graphObject?.xaxis === "Review Ratings" && xaxisDataList
            ? xaxisDataList
            : graphObject?.single[0]?.x,
        labels: {
          useHTML: true, // Enable HTML rendering
          formatter: function () {
            if (date_check || graphObject?.pdfDownloadStatus) {
              return this.value; // No formatting needed in this case
            }

            const text = String(this.value);
            const maxLengthPerLine = 24;

            if (text?.length <= maxLengthPerLine) {
              return `<div style="text-align: right;">${text}</div>`; // Single-line, right-aligned
            }

            // Find the last space before maxLengthPerLine for a natural word break
            let firstLineEnd = text?.lastIndexOf(" ", maxLengthPerLine);
            if (firstLineEnd === -1) firstLineEnd = maxLengthPerLine; // If no space found, force split

            const firstLine = text?.slice(0, firstLineEnd);
            let secondLine = text?.slice(firstLineEnd + 1);

            // Truncate the second line if it exceeds maxLengthPerLine
            if (secondLine?.length > maxLengthPerLine) {
              secondLine = secondLine?.slice(0, maxLengthPerLine - 3) + "...";
            }

            // Return wrapped text with right alignment using div and HTML
            return `<div style="text-align: right;">${firstLine}<br>${secondLine}</div>`;
          },
          rotation: graphObject?.single[0]?.x?.length > 5 ? -90 : 0,
          style: {
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
        },
      },
      yAxis: {
        style: {
          color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
        },
        min:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? 0.1
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? 0.1
            : flrSeriesExists
            ? 0
            : undefined, //aiLogScale:aiLogScale!==null? aiLogScale : log,
        minTickInterval: flrSeriesExists ? 60 : undefined,
        type:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? "logarithmic"
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? "logarithmic"
            : "", //aiLogScale:aiLogScale!==null? aiLogScale : log,
        title: {
          text:
            updatedYAxisLabel?.length > 0
              ? updatedYAxisLabel
              : graphObject?.yaxis,
          style: {
            fontWeight: "bold",
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
        },
        labels: {
          formatter: flrSeriesExists
            ? function () {
                return SecondstoHumanReadable(this?.value, true);
              }
            : function () {
                return kFormatter(this.value);
              },
          style: {
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
        },
        allowDecimals: false,
        //endOnTick: false,
      },

      tooltip: {
        enabled: true,
        useHTML: true,
        borderWidth: 0,
        shadow: false,
        backgroundColor: "transparent",
        // headerFormat:
        //   '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">{series.name}</span> <div class="d-flex align-items-center"><span class="text-Darkgray mr-1"> {point.key} </span> ',
        // //pointFormat: ",{point.y})"#3b5175,
        // pointFormatter: function () {
        //   let clr = this?.color
        //     ? this?.color?.stops?.length
        //       ? this?.color?.stops?.[0]?.[1]
        //       : this?.color
        //     : this?.series?.color?.stops?.length
        //     ? this?.series?.color?.stops?.[0]?.[1]
        //     : this?.series?.color;
        //   return (
        //     '<span class="stackbar__tooltip" style="background-color:' +
        //     clr +
        //     ';">' +
        //     (flrSeriesExists ? SecondstoHumanReadable(this?.y) : this?.y) +
        //     "</span></div></div>"
        //   );
        // },
        formatter: function () {
          let clr = this?.color
            ? this?.color?.stops?.length
              ? this?.color?.stops?.[0]?.[1]
              : this?.color
            : this?.series?.color?.stops?.length
            ? this?.series?.color?.stops?.[0]?.[1]
            : this?.series?.color;
          return (
            '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">' +
            getNameWithChannelIconIfExists(this?.series?.name) +
            '</span> <div class="d-flex align-items-center"><span class="text-Darkgray mr-1">' +
            (this?.key ? this?.key : this?.point?.category) +
            " </span> " +
            '<span class="stackbar__tooltip" style="background-color:' +
            clr +
            ';">' +
            (flrSeriesExists
              ? SecondstoHumanReadable(this?.y)
              : this?.y?.toLocaleString()) +
            "</span></div></div>"
          );
        },
        style: {
          fontSize: "1.4rem",
          fontFamily: '"Roboto", sans-serif',
          color: "#3b5175",
        },
      },
      plotOptions: {
        lineWidth: 3,
        // [plotOptionsKey]: {
        // depth: 100,
        areaspline: {
          animation: graphObject?.pdfDownloadStatus ? false : true,
          stacking: graphObject?.chartGlobalDataStacked
            ? "normal"
            : graphObject?.chartGlobalDataStacked == undefined
            ? "normal"
            : undefined,
          lineWidth: 3,
          marker: {
            enabled:
              graphObject?.chartMarkers === false
                ? graphObject?.chartMarkers
                : true,
            //radius:graphObject?.chartMarkers === true ? 4 : 0
            symbol: "circle",
            fillColor: "#FFFFFF",
            lineWidth: 3,
            lineColor: null, // inherit from series
          },
          showInLegend: graphObject?.metadata?.chart
            ? graphObject?.metadata?.chart?.chart_settings?.chart_property
                ?.show_legend
            : graphObject?.metadata?.chart_property?.show_legend,
        },
        series: {
          dataLabels: {
            enabled: graphObject?.chartGlobalDataLabel ? true : false,
            style: {
              fontWeight: "500",
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
              fontSize: "1rem",
              fontFamily: '"Roboto", sans-serif',
              textOutline: "none",
            },
            formatter: flrSeriesExists
              ? function () {
                  return (this.y / 60).toFixed(0) + " mins";
                }
              : function () {
                  return kFormatter(this.y);
                },
          },
        },
      },
      exporting: {
        filename: graphObject?.widget_name
          ? graphObject?.widget_name
          : graphObject?.type,
        enabled:
          graphObject?.pdfDownloadStatus ||
          graphObject?.pptDownloadStatus ||
          graphObject?.isScheduleReportOpen ||
          authParams?.screen_id
            ? false
            : true,
        menuItemDefinitions: {
          // Custom definition
          xsl: {
            onclick: () => {
              const excel = new Excel();
              let columns = [
                {
                  title: graphObject?.xaxis,
                  dataIndex: graphObject?.xaxis,
                },
              ];
              let datasource = [];
              let lengthOfMultiData = multiData?.length;
              let lengthOfColumns = lengthOfMultiData + 1;
              for (let i = 0; i < lengthOfColumns; i++) {
                if (i == 0) {
                  graphObject?.single[0]?.x?.forEach((xElem, x_index) => {
                    datasource[x_index] = { [columns[i]?.title]: xElem };
                  });
                } else {
                  columns[i] = {
                    title: multiData[i - 1]?.name,
                    dataIndex: multiData[i - 1]?.name,
                  };
                  multiData[i - 1]?.data?.forEach((data, data_index) => {
                    datasource[data_index] = {
                      ...datasource[data_index],
                      [columns[i]?.title]: flrSeriesExists
                        ? SecondstoHumanReadable(data)
                        : isNaN(Number(data))
                        ? data?.toString().includes("NaN")
                          ? Number(0)
                          : data
                        : Number(data), // data
                    };
                  });
                }
              }
              excel
                .addSheet("test")
                .addColumns(columns)
                .addDataSource(datasource, {
                  str2Percent: true,
                })
                .saveAs(
                  `${
                    graphObject?.widget_name
                      ? graphObject?.widget_name
                      : graphObject?.type
                  }.xlsx`
                );
            },
            text: "Download excel sheet",
          },
        },
        buttons: {
          contextButton: {
            menuItems:
              graphObject?.xaxis && graphObject?.yaxis
                ? [
                    "downloadPNG",
                    "downloadJPEG",
                    "downloadSVG",
                    "separator",
                    "xsl",
                  ]
                : ["downloadPNG", "downloadJPEG", "downloadSVG"],
            buttonSpacing: 1,
            height: 20,
            symbolSize: 12,
            symbolStrokeWidth: 2,
            symbolX: 10.5,
            symbolY: 10.5,
            width: 20,
            x: -2,
            y: -12,
          },
        },

        chartOptions: {
          chart: {
            style: {
              fontSize: "16px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          tooltip: {
            style: {
              fontSize: "14px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          plotOptions: {
            series: {
              dataLabels: {
                style: {
                  fontSize: "10px",
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: "500",
                },
              },
            },
          },
          legend: {
            itemStyle: {
              fontSize: "13px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
      },
      credits: {
        enabled: false,
      },
      series: multiData,
      legend: graphObject?.legend_position
        ? {
            reversed:
              graphObject?.chartGlobalDataStacked ||
              graphObject?.chartGlobalDataStacked == undefined
                ? true
                : false, // This will reverse the order of the legend
            itemWidth:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? undefined
                : 200,
            layout:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "horizontal"
                : "vertical",
            align:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "center"
                : graphObject?.legend_position,
            verticalAlign:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? graphObject?.legend_position
                : "middle",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            },
            itemHoverStyle: {
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          }
        : {
            reversed:
              graphObject?.chartGlobalDataStacked ||
              graphObject?.chartGlobalDataStacked == undefined
                ? true
                : false, // This will reverse the order of the legend
            itemWidth: undefined,
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            },
            itemHoverStyle: {
              color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          },
    };
    return obj;
  }
};

export const getSunburst = (graphObject, authParams) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  let j = 0;
  let noOfIterationInColors = 0;
  let lengthOfColors = selectedColorArray?.length;
  let multiData = graphObject?.single?.map((el, i) => {
    let dataColor;
    if (
      el?.color !== undefined &&
      el?.color !== "#000000" &&
      el?.color !== null
    ) {
      dataColor = el?.color;
    } else {
      if (j >= lengthOfColors) {
        noOfIterationInColors = parseInt(j / lengthOfColors);
        let rem = parseInt(j % lengthOfColors);
        dataColor =
          noOfIterationInColors > 0
            ? newColorShadeInHex6(
                selectedColorArray[rem],
                parseInt(noOfIterationInColors * 5)
              )
            : selectedColorArray[j];
      } else {
        dataColor = selectedColorArray[j];
      }
      j++;
    }
    return {
      id: el?.id,
      parent: el?.parent,
      name: el?.name,
      value: el?.value,
      color: authParams?.screen_id
        ? dataColor
        : {
            radialGradient: {
              cx: 0.5,
              cy: 0.3,
              r: 0.7,
            },
            stops: [
              [0, `${dataColor}99`],
              [1, dataColor],
            ],
          },
    };
  });

  if (
    authParams?.screen_id === "000-000-552" &&
    graphObject?.screen &&
    multiData?.length
  ) {
    let categoryFilterName = null;
    let categoryFilterValue = null;

    let subCategoryFilterName = null;
    let subCategoryFilterValue = null;

    let userLocationFilterValue = false;
    graphObject?.screen?.category_screen_filters?.map((d) => {
      if (d?.attribute === "Category") {
        categoryFilterValue = d?.chart_value;
        categoryFilterName = d?.columns?.[0].name;
      }
      if (d?.attribute === "SubCategory") {
        subCategoryFilterValue = d?.chart_value;
        subCategoryFilterName = d?.columns?.[0].name;
      }
      if (d?.attribute === "UserLocation") {
        userLocationFilterValue = true;
      }
    });
    multiData?.map((d) => {
      if (categoryFilterName && !subCategoryFilterName) {
        if (d?.name === categoryFilterName) {
          d.color = "#185edf";
          d.dataLabels = {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          };
        } else {
          d.color = "#2d416f";
          d.dataLabels = {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          };
        }
      } else if (categoryFilterName && subCategoryFilterName) {
        if (userLocationFilterValue && d?.name === subCategoryFilterName) {
          d.color = "#185edf";
          d.dataLabels = {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          };
        } else if (
          !userLocationFilterValue &&
          d?.name === subCategoryFilterName &&
          d?.value === subCategoryFilterValue
        ) {
          d.color = "#185edf";
          d.dataLabels = {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          };
        } else {
          d.color = "#2d416f";
          d.dataLabels = {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          };
        }
      }
    });
  }

  const obj = {
    chart: {
      type: graphObject?.type,
      height: graphObject?.style?.height,
      reflow: true,
      style: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        fontSize: "1.6rem",
      },
    },
    colors: ["transparent"].concat(selectedColorArray),
    title: {
      text: "",
    },
    series: [
      {
        type: "sunburst",
        size: graphObject?.pdfDownloadStatus ? "70%" : null,
        data: multiData,
        name: "Root",
        allowDrillToNode: !graphObject?.metadata?.chart?.chart_settings
          ?.allow_deepdive
          ? true
          : false,
        cursor: "pointer",
        borderWidth: 2,
        borderColor: authParams?.theme_type === "1" ? "#1b2f50" : "#fff",
        borderRadius: 4,
        dataLabels: {
          format: "{point.name}",
          filter: {
            property: "innerArcLength",
            operator: ">",
            value: 1,
          },
          rotationMode: "circular",
          // style: {
          // 	fontSize: "1.2rem",
          // 	//textOutline: 0,
          // 	//fontWeight:"normal",
          // },
          style:
            graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            authParams?.screen_id
              ? {
                  //color: '#869DAD',
                  fontFamily: '"Roboto", sans-serif',
                  //fill: '#869DAD',
                  fontWeight: "bold",
                  textOutline: "none",
                }
              : {
                  //color: '#869DAD',
                  fontSize: "1.2rem",
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: "bold",
                  textOutline: "none",
                  color: dynamicHighchartTextColor(
                    authParams,
                    graphObject?.isDeepDiveView,
                    graphObject?.pdfDownloadStatus ||
                      graphObject?.pptDownloadStatus ||
                      graphObject?.isScheduleReportOpen,
                    graphObject?.isDeepDiveDashboardView
                  ),
                },
        },
        levels: [
          {
            level: 1,
            levelIsConstant: false,
            dataLabels: {
              filter: {
                property: "outerArcLength",
                operator: ">",
                value: 1,
              },
              style: authParams?.screen_id
                ? { color: "#f5f6f8" }
                : graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus
                ? { color: "#3b5175" }
                : {
                    fontSize: "1.2rem",
                    color: dynamicHighchartTextColor(
                      authParams,
                      graphObject?.isDeepDiveView,
                      graphObject?.pdfDownloadStatus ||
                        graphObject?.pptDownloadStatus ||
                        graphObject?.isScheduleReportOpen,
                      graphObject?.isDeepDiveDashboardView
                    ),
                    textOutline: "none",
                  },
            },
          },
          {
            level: 2,
            colorByPoint: true,
            dataLabels:
              graphObject?.pdfDownloadStatus || graphObject?.pptDownloadStatus
                ? {}
                : {
                    style: authParams?.screen_id
                      ? { color: "#f5f6f8" }
                      : {
                          fontSize: "0.8rem",
                          color: dynamicHighchartTextColor(
                            authParams,
                            graphObject?.isDeepDiveView,
                            graphObject?.pdfDownloadStatus ||
                              graphObject?.pptDownloadStatus ||
                              graphObject?.isScheduleReportOpen,
                            graphObject?.isDeepDiveDashboardView
                          ),
                          textOutline: "none",
                        },
                  },
          },
          {
            level: 3,
            colorVariation: {
              key: "brightness",
              to: 0.3,
            },
            dataLabels:
              graphObject?.pdfDownloadStatus || graphObject?.pptDownloadStatus
                ? {}
                : {
                    style: authParams?.screen_id
                      ? { color: "#f5f6f8" }
                      : {
                          fontSize: "0.6rem",
                          color: dynamicHighchartTextColor(
                            authParams,
                            graphObject?.isDeepDiveView,
                            graphObject?.pdfDownloadStatus ||
                              graphObject?.pptDownloadStatus ||
                              graphObject?.isScheduleReportOpen,
                            graphObject?.isDeepDiveDashboardView
                          ),
                          textOutline: "none",
                        },
                  },
          },
          {
            level: 4,
            colorVariation: {
              key: "brightness",
              to: 0.8,
            },
            dataLabels: {
              style:
                graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                authParams?.screen_id
                  ? {}
                  : {
                      fontSize: "0.6rem",
                      color: dynamicHighchartTextColor(
                        authParams,
                        graphObject?.isDeepDiveView,
                        graphObject?.pdfDownloadStatus ||
                          graphObject?.pptDownloadStatus ||
                          graphObject?.isScheduleReportOpen,
                        graphObject?.isDeepDiveDashboardView
                      ),
                      textOutline: "none",
                    },
            },
          },
        ],
      },
    ],
    exporting: {
      filename: graphObject?.widget_name
        ? graphObject?.widget_name
        : graphObject?.type,
      enabled:
        graphObject?.pdfDownloadStatus ||
        graphObject?.pptDownloadStatus ||
        graphObject?.isScheduleReportOpen ||
        authParams?.screen_id
          ? false
          : true,
      buttons: {
        contextButton: {
          menuItems: ["downloadPNG", "downloadJPEG", "downloadSVG"],
          buttonSpacing: 1,
          height: 20,
          symbolSize: 12,
          symbolStrokeWidth: 2,
          symbolX: 10.5,
          symbolY: 10.5,
          width: 20,
          x: -2,
          y: -12,
        },
      },

      chartOptions: {
        chart: {
          style: {
            fontSize: "16px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        tooltip: {
          style: {
            fontSize: "14px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        plotOptions: {
          series: {
            dataLabels: {
              style: {
                fontSize: "10px",
                fontFamily: '"Roboto", sans-serif',
                fontWeight: "bold",
              },
            },
          },
        },
        legend: {
          itemStyle: {
            fontSize: "13px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderWidth: 0,
      shadow: false,
      backgroundColor: "transparent",
      headerFormat: "",
      pointFormatter: function () {
        let clr = this?.color
          ? this?.color?.stops?.length
            ? this?.color?.stops?.[0]?.[1]
            : this?.color
          : this?.series?.color?.stops?.length
          ? this?.series?.color?.stops?.[0]?.[1]
          : this?.series?.color;
        return (
          '<div class="tooltip_chart d-flex align-items-center"><span class="text-Darkgray fontSize-14 font-weight600 mr-1">' +
          getNameWithChannelIconIfExists(this?.name) +
          "</span>" +
          '<span class="stackbar__tooltip" style="background-color:' +
          clr +
          ';">' +
          this?.value?.toLocaleString() +
          "</span></div>"
        );
      },
    },
  };
  return obj;
};

export const getSankey = (graphObject, authParams) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  let j = 0;
  let noOfIterationInColors = 0;
  let lengthOfColors = selectedColorArray?.length;
  let multiData = graphObject?.single?.nodes?.map((el, i) => {
    let dataColor;
    if (el?.id === "start_Positive" || el?.id === "end_Positive") {
      dataColor = "#35B819";
    } else if (el?.id === "start_Negative" || el?.id === "end_Negative") {
      dataColor = "#F33737";
    } else if (el?.id === "start_Neutral" || el?.id === "end_Neutral") {
      dataColor = "#DEAA0C";
    } else if (
      el?.color !== undefined &&
      el?.color !== "#000000" &&
      el?.color !== null
    ) {
      dataColor = el?.color;
    } else {
      if (j >= lengthOfColors) {
        noOfIterationInColors = parseInt(j / lengthOfColors);
        let rem = parseInt(j % lengthOfColors);
        dataColor =
          noOfIterationInColors > 0
            ? newColorShadeInHex6(
                selectedColorArray[rem],
                parseInt(noOfIterationInColors * 5)
              )
            : selectedColorArray[j];
      } else {
        dataColor = selectedColorArray[j];
      }
      j++;
    }
    return {
      id: el?.id,
      color: dataColor,
      name: el?.display_name,
    };
  });
  function convertToSankeyData(inputData) {
    let sankeyData = inputData?.map((item) => {
      const [from, to, weight, colors] = item;
      return [
        from,
        to,
        weight,
        {
          linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
          stops: [
            [0, colors?.[0]],
            [1, colors?.[1]],
          ],
        },
      ];
    });

    return sankeyData;
  }
  let sankeyData = convertToSankeyData(graphObject?.single?.data);
  const obj = {
    title: {
      text: "",
    },
    accessibility: {
      point: {
        valueDescriptionFormat:
          "{index}. {point.from} to {point.to}, " + "{point.weight}.",
      },
    },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderWidth: 0,
      headerFormat: null,
      shadow: false,
      backgroundColor: "transparent",
      pointFormatter: function () {
        let clr = this?.color;
        return (
          '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">' +
          this.from +
          "\u2192" +
          this?.to +
          " " +
          '<span class="stackbar__tooltip" style="background-color:' +
          this?.options?.color?.stops?.[1]?.[1] +
          ';">' +
          this?.weight +
          "</span></span></div>"
        );
      },
      nodeFormatter: function () {
        let clr = this?.color;
        return (
          '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">' +
          this?.name +
          " " +
          '<span class="stackbar__tooltip" style="background-color:' +
          clr +
          ';">' +
          this?.sum +
          "</span></span></div>"
        );
      },
    },
    style: {
      height: graphObject?.style?.height,
      width: graphObject?.style?.width,
    },
    series: [
      {
        keys: ["from", "to", "weight", "color"],
        nodes: multiData,
        data: sankeyData,
        type: "sankey",
      },
    ],
    exporting: {
      filename: graphObject?.widget_name
        ? graphObject?.widget_name
        : graphObject?.type,
      enabled:
        graphObject?.pdfDownloadStatus ||
        graphObject?.pptDownloadStatus ||
        graphObject?.isScheduleReportOpen ||
        authParams?.screen_id
          ? false
          : true,

      buttons: {
        contextButton: {
          menuItems:
            graphObject?.xaxis && graphObject?.yaxis
              ? ["downloadPNG", "downloadJPEG", "downloadSVG"]
              : ["downloadPNG", "downloadJPEG", "downloadSVG"],
          buttonSpacing: 1,
          height: 20,
          symbolSize: 12,
          symbolStrokeWidth: 2,
          symbolX: 10.5,
          symbolY: 10.5,
          width: 20,
          x: -2,
          y: -12,
        },
      },
    },
  };
  return obj;
};

// FOR BAR CHART
export const getBarChart = (graphObject, authParams, deepDive) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  const xaxisDataList = [];
  let fontSize = !authParams?.screen_id ? "1.6rem" : "";
  graphObject?.single[0]?.x?.map((el, i) => {
    xaxisDataList.push(`${el} ⭐`);
  });
  let j = 0;
  let noOfIterationInColors = 0;
  let lengthOfColors = selectedColorArray?.length;
  if (graphObject?.single[0]?.y_axis) {
    let obj = { ...graphObject };
    obj.single = graphObject.single[0];
    return getCombination(obj, authParams);
  } else {
    let ySeriesFromConfig = graphObject?.metadata?.y_series
      ? graphObject?.metadata?.y_series
      : Object?.entries(graphObject?.widgetGraphConfig?.y_axis);
    let flrSeriesExists = false;
    let graphConditionConfig = [
      ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
      ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
    ];

    ySeriesFromConfig?.forEach((yseries, index) => {
      if (yseries?.attribute) {
        if (yseries && graphConditionConfig?.includes(yseries?.attribute))
          flrSeriesExists = true;
      } else if (
        yseries &&
        graphConditionConfig?.includes(yseries[1]?.attribute)
      )
        flrSeriesExists = true;
    });
    let seriesData = [];
    let seriesColorsFlag = false;
    let date_check = moment(
      graphObject?.single && graphObject?.single[0]?.x[0]
    ).isValid();
    if (
      graphObject?.single[0]?.colors != undefined &&
      graphObject?.single[0]?.colors?.length > 0
    ) {
      seriesColorsFlag = true;
      seriesData = graphObject?.single[0]?.y?.map((yElem, i) => {
        let dataColor;
        if (
          graphObject?.single[0]?.colors != undefined &&
          graphObject?.single[0]?.colors[i] !== undefined &&
          graphObject?.single[0]?.colors[i] !== "#000000" &&
          graphObject?.single[0]?.colors[i] !== null
        ) {
          dataColor = graphObject?.single[0]?.colors[i];
        } else {
          if (j >= lengthOfColors) {
            noOfIterationInColors = parseInt(j / lengthOfColors);
            let rem = parseInt(j % lengthOfColors);
            dataColor =
              noOfIterationInColors > 0
                ? newColorShadeInHex6(
                    selectedColorArray[rem],
                    parseInt(noOfIterationInColors * 5)
                  )
                : selectedColorArray[j];
          } else {
            dataColor = selectedColorArray[j];
          }
          j++;
        }
        let lighterColor = newColorShadeInHex6(dataColor, 60);
        let darkerColor = dataColor;
        return {
          y: yElem,
          color: {
            linearGradient: {
              x1: 0,
              x2: 0,
              y1: 0,
              y2: 1,
            },
            stops: [
              [0, darkerColor],
              [1, lighterColor],
            ],
          },
          connectorColor: darkerColor,
        };
      });
    }

    const totalCount = getTotalCount(graphObject?.single[0]?.y);

    const durationType =
      (getWindowLocationPath()?.toLowerCase() ===
        "widgetmaker"?.toLowerCase() ||
        getWindowLocationPath()?.toLowerCase() ===
          "edit-widget"?.toLowerCase() ||
        graphObject?.dashboard ||
        graphObject?.panes?.[graphObject?.index]?.is_widget_maker_open_obj
          ?.open_flag) &&
      handleDateFormat(
        graphObject?.panes,
        graphObject?.index,
        graphObject?.section_index,
        graphObject?.widget_index,
        graphObject?.metadata,
        graphObject?.metadata?.x_axis?.attribute,
        graphObject?.xaxis,
        graphObject?.metadata?.x_axis?.date_part,
        graphObject?.single[0]?.x,
        graphObject?.section,
        graphObject?.template_widget,
        graphObject?.shareTemplateData,
        graphObject?.pdfDownloadStatus,
        graphObject?.isScheduleReportOpen,
        graphObject?.screen,
        graphObject?.template_details,
        false, // graphObject?.isDeepDive,
        null, //graphObject?.dateAggregationOfThisGraph,
        null, //graphObject?.deepdiveObject,
        null, //graphObject?.yAxesDateAttr,
        graphObject?.isDeepDiveDashboardView,
        graphObject?.deepdiveDashboardData,
        graphObject?.deepdiveDashboardTemplateData
      );

    let x_date_part = graphObject?.metadata?.x_axis?.date_part;
    let x_date_agg = graphObject?.metadata?.x_axis?.date_aggregation;
    let bandColor = 0;
    let bandColorCount = 0;
    if (
      graphObject?.metadata?.x_axis?.attribute === "FLRBenchmark" ||
      graphObject?.metadata?.x_axis?.attribute === "ResolutionBenchmark"
    ) {
      for (let i = 0; i < graphObject?.single[0]?.colors?.length; i++) {
        if (!bandColor) {
          bandColor = graphObject?.single[0]?.colors[i];
        }
        if (bandColor && bandColor !== graphObject?.single[0]?.colors[i]) {
          bandColorCount = i;
          break;
        }
      }
    }

    const configObj = {
      chart: {
        inverted:
          graphObject.type === "lollipop"
            ? graphObject?.metadata?.chart?.chart_settings?.show_horizontal_view
              ? true
              : graphObject?.widgetGraphConfig?.chart_settings
                  ?.show_horizontal_view
              ? true
              : false
            : null,
        height: graphObject?.style?.height,
        type:
          graphObject.type === "lollipop"
            ? "lollipop"
            : graphObject.type === "horizontal-bar"
            ? "bar"
            : "column",
        animation:
          graphObject.type === "horizontal-bar" && graphObject?.chart3dOption
            ? false
            : null,
        options3d:
          graphObject.type !== "lollipop"
            ? {
                enabled: graphObject?.chartShow3d,
                alpha: graphObject?.chart3dOption?.alpha,
                beta: graphObject?.chart3dOption?.beta,
                depth: graphObject?.chart3dOption?.depth,
                viewDistance: 25,
              }
            : {},
        //marginTop: 50,
        marginRight: graphObject?.type === "horizontal-bar" ? 30 : undefined,
        reflow: true,
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          fontSize: fontSize,
        },
      },
      title: {
        text: "",
        style: {
          fontWeight: "bold",
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },

      xAxis: {
        scrollbar: {
          enabled:
            graphObject?.single?.[0]?.x?.length > 7 && deepDive ? true : false,
        },
        min:
          graphObject?.single?.[0]?.x?.length > 7 && deepDive ? 0 : undefined,
        max:
          graphObject?.single?.[0]?.x?.length > 7 && deepDive ? 6 : undefined,
        plotBands:
          graphObject?.metadata?.x_axis?.attribute === "FLRBenchmark" ||
          graphObject?.metadata?.x_axis?.attribute === "ResolutionBenchmark"
            ? [
                {
                  color: "#5ED14230",
                  from: -1,
                  to: bandColorCount - 0.5,
                  id: "green_breach",
                  zIndex: 1,
                },
                {
                  color: "#FF5D4830",
                  from: bandColorCount - 0.5,
                  to: graphObject?.single?.[0]?.x?.length,
                  id: "red_breach",
                  zIndex: 1,
                },
              ]
            : undefined,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
        title: {
          style: {
            fontWeight: "bold",
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          text:
            graphObject?.metadata?.x_axis?.attribute &&
            graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
              graphObject?.metadata?.x_axis?.attribute
            )
              ? graphObject?.metadata?.x_axis?.attribute === "FLRBenchmark" ||
                graphObject?.metadata?.x_axis?.attribute ===
                  "ResolutionBenchmark"
                ? ""
                : !x_date_part && !x_date_agg
                ? "Date"
                : !x_date_part && x_date_agg === "1h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "2h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "3h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "4h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "6h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "8h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "12h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "ww"
                ? "Week of Year"
                : !x_date_part && x_date_agg === "M"
                ? "Month of Year"
                : !x_date_part && x_date_agg === "Y"
                ? "Years"
                : graphObject?.metadata?.x_axis?.date_part === null ||
                  graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "1h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "2h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "3h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "4h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "6h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "8h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "12h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "M"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.metadata?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.metadata?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.metadata?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.metadata?.x_axis?.date_part
              : !graphObject?.metadata?.x_axis?.attribute &&
                (graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                  "RecordDate" ||
                  graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                    "CreatedDate")
              ? graphObject?.widgetGraphConfig?.x_axis?.date_part === null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part
              : graphObject?.xaxis,
          enabled:
            typeof graphObject?.chartAxisLabel === "boolean"
              ? graphObject?.chartAxisLabel
                ? true
                : false
              : true,
        },
        categories:
          graphObject?.metadata &&
          (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
            graphObject?.metadata?.x_axis?.attribute
          ) ||
            graphObject?.xaxis === "Date") &&
          ((graphObject?.pdfDownloadStatus &&
            graphObject?.metadata?.x_axis?.date_part !== null &&
            graphObject?.metadata?.x_axis?.date_part !== "D") ||
            ((!graphObject?.pdfDownloadStatus ||
              graphObject?.isScheduleReportOpen) &&
              (graphObject?.metadata?.x_axis?.date_part == null ||
                graphObject?.metadata?.x_axis?.date_part == "D"))) &&
          durationType
            ? durationType
            : graphObject?.metadata &&
              (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) ||
                graphObject?.xaxis === "Date") &&
              graphObject?.metadata?.x_axis?.date_part !== null &&
              graphObject?.metadata?.x_axis?.date_part !== "D"
            ? graphObject?.single[0]?.x?.map((e) => e)
            : // (graphObject?.metadata &&
            //   graphObject?.metadata?.x_axis?.attribute === "RecordDate") ||
            // (graphObject?.xaxis === "Date" &&
            //   graphObject?.metadata?.x_axis?.date_part == null)
            //   ? graphObject?.single[0]?.x?.map((e) =>
            //       moment(e).format("MMM DD YYYY")
            //     )
            (graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus) &&
              graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) &&
              ["FLRBenchmark", "ResolutionBenchmark"]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) &&
              (graphObject?.widgetGraphConfig?.x_axis?.date_part == null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part == "D")
            ? graphObject?.single[0]?.x?.map((e) => e)
            : graphObject?.xaxis === "Date" &&
              (graphObject?.widgetGraphConfig?.x_axis?.date_part == null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part == "D")
            ? graphObject?.single[0]?.x?.map((e) =>
                moment(e).format("MMM DD YYYY")
              )
            : graphObject?.xaxis === "Review Ratings" && xaxisDataList
            ? xaxisDataList
            : graphObject?.single[0]?.x,
        // categories: graphObject?.single[0]?.x,
        // categories: single[0]?.x?.map(
        // 	(e) => e?.slice(0, 15) + (e?.length > 15 ? "..." : "")
        // ),
        labels: {
          useHTML: true, // Enable HTML rendering
          formatter:
            graphObject?.type === "horizontal-bar" // formatting for horizontal bar
              ? function () {
                  return date_check || graphObject?.pdfDownloadStatus
                    ? this.value
                    : this.value.length > 12 &&
                      graphObject?.single[0]?.x?.length > 5
                    ? this.value?.slice(0, 12) +
                      (this.value?.length > 12 ? "..." : "")
                    : this.value;
                }
              : function () {
                  // formatting for all other cases
                  if (date_check || graphObject?.pdfDownloadStatus) {
                    return this.value; // No formatting needed in this case
                  }

                  const text = String(this.value);
                  const maxLengthPerLine = 24;

                  if (text?.length <= maxLengthPerLine) {
                    return `<div style="text-align: right;">${text}</div>`; // Single-line, right-aligned
                  }

                  // Find the last space before maxLengthPerLine for a natural word break
                  let firstLineEnd = text?.lastIndexOf(" ", maxLengthPerLine);
                  if (firstLineEnd === -1) firstLineEnd = maxLengthPerLine; // If no space found, force split

                  const firstLine = text?.slice(0, firstLineEnd);
                  let secondLine = text?.slice(firstLineEnd + 1);

                  // Truncate the second line if it exceeds maxLengthPerLine
                  if (secondLine?.length > maxLengthPerLine) {
                    secondLine =
                      secondLine?.slice(0, maxLengthPerLine - 3) + "...";
                  }

                  // Return wrapped text with right alignment using div and HTML
                  return `<div style="text-align: right;">${firstLine}<br>${secondLine}</div>`;
                },
          rotation:
            graphObject?.type === "horizontal-bar"
              ? 0
              : graphObject?.single[0]?.x?.length > 5
              ? -90
              : 0,
          //style: { fontWeight: "normal",color:"#869DAD" },
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          skew3d: graphObject?.chartShow3d,
        },
      },
      yAxis: {
        min:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? 0.1
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? 0.1
            : flrSeriesExists
            ? 0
            : undefined, //aiLogScale:aiLogScale!==null? aiLogScale : log,
        minTickInterval: flrSeriesExists
          ? 60
          : graphObject?.scaleFromZeroToOne
          ? 1
          : undefined,
        type:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? "logarithmic"
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? "logarithmic"
            : "", //aiLogScale:aiLogScale!==null? aiLogScale : log,
        // min: 0,
        title: {
          text: graphObject?.yaxis,
          //style: { fontWeight: "normal",color:"#869DAD" },
          style: {
            fontWeight: "bold",
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          enabled:
            typeof graphObject?.chartAxisLabel === "boolean"
              ? graphObject?.chartAxisLabel
                ? true
                : false
              : true,
        },
        // labels: {
        //   //style: { fontWeight: "normal",color:"#869DAD" },
        // },
        labels: {
          formatter: flrSeriesExists
            ? function () {
                return SecondstoHumanReadable(this?.value, true);
              }
            : function () {
                return graphObject?.scaleFromZeroToOne &&
                  !Number?.isInteger(this?.value)
                  ? kFormatter(Math?.floor(this?.value))
                  : kFormatter(this?.value);
              },
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          enabled:
            typeof graphObject?.chartAxisLabel === "boolean"
              ? graphObject?.chartAxisLabel
                ? true
                : false
              : true,
          skew3d: graphObject?.chartShow3d,
        },
        allowDecimals: false,
        endOnTick:
          typeof graphObject?.chartAxisLabel === "boolean"
            ? graphObject?.chartAxisLabel
              ? true
              : false
            : true,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },

      exporting: {
        filename: graphObject?.widget_name
          ? graphObject?.widget_name
          : graphObject?.type,
        enabled:
          graphObject?.pdfDownloadStatus ||
          graphObject?.pptDownloadStatus ||
          graphObject?.isScheduleReportOpen ||
          authParams?.screen_id
            ? false
            : true,
        menuItemDefinitions: {
          // Custom definition
          xsl: {
            onclick: () => {
              const excel = new Excel();
              let columns = [
                {
                  title: graphObject?.xaxis,
                  dataIndex: graphObject?.xaxis,
                },
                {
                  title: graphObject?.yaxis,
                  dataIndex: graphObject?.yaxis,
                },
                {
                  title: graphObject?.yaxis + " Percent",
                  dataIndex: graphObject?.yaxis + " Percent",
                },
              ];

              let percentNums = graphObject?.single[0]?.y;
              let percentages = calculatePercentages(percentNums);

              let datasource = graphObject?.single[0]?.y?.map((yElem, i) => {
                return {
                  [columns[0]?.title]: graphObject?.single[0]?.x[i],
                  [columns[1]?.title]: flrSeriesExists
                    ? SecondstoHumanReadable(yElem)
                    : isNaN(Number(yElem))
                    ? yElem?.toString().includes("NaN")
                      ? Number(0)
                      : yElem
                    : Number(yElem), // yElem
                  [columns[2]?.title]: percentages[i],
                };
              });
              excel
                .addSheet("chart")
                .addColumns(columns)
                .addDataSource(datasource, {
                  str2Percent: true,
                })
                .saveAs(
                  `${
                    graphObject?.widget_name
                      ? graphObject?.widget_name
                      : graphObject?.type
                  }.xlsx`
                );
            },
            text: "Download excel sheet",
          },
        },
        buttons: {
          contextButton: {
            menuItems:
              graphObject?.xaxis && graphObject?.yaxis
                ? [
                    "downloadPNG",
                    "downloadJPEG",
                    "downloadSVG",
                    "separator",
                    "xsl",
                  ]
                : ["downloadPNG", "downloadJPEG", "downloadSVG"],
            buttonSpacing: 1,
            height: 20,
            symbolSize: 12,
            symbolStrokeWidth: 2,
            symbolX: 10.5,
            symbolY: 10.5,
            width: 20,
            x: -2,
            y: -12,
          },
        },
        chartOptions: {
          chart: {
            style: {
              fontSize: "16px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          xAxis: {
            title: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
            labels: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
          },
          yAxis: {
            title: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
            labels: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
          },
          tooltip: {
            style: {
              fontSize: "14px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          plotOptions: {
            series: {
              dataLabels: {
                style: {
                  fontSize: "10px",
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: "500",
                },
              },
            },
          },
          legend: {
            itemStyle: {
              fontSize: "13px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
      },
      credits: {
        enabled: false,
      },
      // legend: {
      // 	reversed: true,
      // },
      tooltip: {
        enabled: true,
        useHTML: true,
        //headerFormat: "X: {point.key}<br/>",
        //pointFormat: "Y :{point.y}",
        shadow: {
          color: "#0000001A",
        },
        style: {
          fontSize: "1.4rem",
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
        className: "custom_background",
        formatter: function () {
          let clr = this?.color
            ? this?.color?.stops?.length
              ? this?.color?.stops?.[0]?.[1]
              : this?.color
            : this?.series?.color?.stops?.length
            ? this?.series?.color?.stops?.[0]?.[1]
            : this?.points?.[0]?.color?.stops?.length
            ? this?.points?.[0]?.color?.stops?.[0]?.[1]
            : this?.series?.color;
          let valueOfY =
            this?.point?.y >= 0 ? this?.point?.y : this?.points?.[0]?.y;
          let yTooltipValue = flrSeriesExists
            ? SecondstoHumanReadable(valueOfY)
            : this?.point?.y >= 0
            ? this?.point?.y?.toLocaleString()
            : this?.points?.[0]?.y?.toLocaleString();
          let xTooltipValue = this.point?.category
            ? this.point?.category
            : this?.points?.[0]?.x;
          return (
            "<div class='tooltip_chart--dark d-flex align-items-center fontSize-14 font-weight600'><span class='text-Darkgray  d-block mb-1'> <span class='text-Darkgray mr-1'> X: </span>" +
            xTooltipValue +
            " </span></div><div class='tooltip_chart--dark d-flex align-items-center fontSize-14 '><span class='text-Darkgray font-weight600 mr-1'> Y : </span><span class='stackbar__tooltip' style='background-color:" +
            clr +
            ";'>" +
            yTooltipValue +
            " | " +
            ((valueOfY / totalCount) * 100).toFixed(1)?.replace(/\.0$/, "") +
            "%" +
            "</span></div>"
          );
        },
      },
      plotOptions: {
        series: {
          connectorColor: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ), // "#b1bdd0",
          dataLabels: {
            enabled: graphObject?.chartGlobalDataLabel ? true : false,
            style: {
              fontWeight: "500",
              fontSize: "1rem",
              fontFamily: '"Roboto", sans-serif',
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
              // authParams?.theme_type === "1"
              //   ? typeof graphObject?.chartAxisLabel === "boolean" &&
              //     !graphObject?.chartAxisLabel
              //     ? "#fffff"
              //     : "#b1bdd0"
              //   : "#3B5175",
              textOutline: "none",
              // fill: "#ffffff",
            },
            crop:
              typeof graphObject?.chartAxisLabel === "boolean"
                ? graphObject?.chartAxisLabel
                  ? true
                  : false
                : true,
            overflow:
              typeof graphObject?.chartAxisLabel === "boolean"
                ? graphObject?.chartAxisLabel
                  ? "justify"
                  : "allow"
                : "justify",
            formatter: flrSeriesExists
              ? function () {
                  return (this.y / 60).toFixed(0) + " mins";
                }
              : graphObject?.agentActivit
              ? function () {
                  return (
                    kFormatter(this.y) +
                    " | " +
                    ((this?.y / totalCount) * 100)
                      .toFixed(1)
                      ?.replace(/\.0$/, "") +
                    "%"
                  );
                }
              : function () {
                  return kFormatter(this.y);
                },
          },
          animation: graphObject?.pdfDownloadStatus ? false : true,
          // : {
          //     defer: 1000,
          //   },
          pointPadding: 0.1,
          groupPadding: 0,
          borderWidth: 0,
          shadow: false,
          //pointWidth: 28,
          showInLegend: false,
          borderRadius: {
            radius: 10,
          },

          borderColor: authParams?.theme_type === "1" ? "#1b2f50" : "#fff",
        },
      },

      series: [
        {
          name: "",
          data: seriesColorsFlag ? seriesData : graphObject?.single[0]?.y,
          color: seriesColorsFlag
            ? undefined
            : {
                linearGradient: {
                  x1: 0,
                  x2: 0,
                  y1: 0,
                  y2: 1,
                },
                stops: !seriesColorsFlag
                  ? graphObject?.single[0]?.color != undefined &&
                    graphObject?.single[0]?.color != "#000000"
                    ? [
                        [0, graphObject?.single[0]?.color],
                        [1, `${graphObject?.single[0]?.color}80`],
                      ]
                    : [
                        [0, selectedColorArray[0]],
                        [1, `${selectedColorArray[0]}80`],
                      ]
                  : [],
              },
        },
      ],
      legend: graphObject?.legend_position
        ? {
            itemWidth:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? undefined
                : 200,
            layout:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "horizontal"
                : "vertical",
            align:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "center"
                : graphObject?.legend_position,
            verticalAlign:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? graphObject?.legend_position
                : "middle",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
            },
            itemHoverStyle: {
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          }
        : {
            itemWidth: undefined,
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            itemStyle: {
              fontFamily: '"Roboto", sans-serif',
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
            },
            itemHoverStyle: {
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          },
    };
    configObj["customLegend"] =
      graphObject?.single && graphObject?.single?.[0]?.legends ? true : false;
    if (authParams?.screen_id) {
      configObj.xAxis.title.style.fontSize = "1.4rem";
      configObj.xAxis.title.style.fontWeight = "500";
      configObj.xAxis.labels.style.fontSize = "1.4rem";
      configObj.xAxis.labels.style.fontWeight = "500";
      configObj.yAxis.title.style.fontSize = "1.4rem";
      configObj.yAxis.title.style.fontWeight = "500";
      configObj.yAxis.labels.style.fontSize = "1.4rem";
      configObj.yAxis.labels.style.fontWeight = "500";
    }
    return configObj;
  }
};

export const getStackBarChart = (graphObject, authParams) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  let isTicketOverview1 =
    graphObject?.ticket_widget_id === "tickets_overview_1" ? true : false;
  const xaxisDataList = [];
  graphObject?.single[0]?.x?.map((el, i) => {
    xaxisDataList.push(`${el} ⭐`);
  });
  let ySeriesFromConfig = graphObject?.metadata?.y_series
    ? graphObject?.metadata?.y_series
    : Object?.entries(graphObject?.widgetGraphConfig?.y_axis);
  let flrSeriesExists = false;
  let graphConditionConfig = [
    ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
    ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
  ];

  ySeriesFromConfig?.forEach((yseries, index) => {
    if (yseries?.attribute) {
      if (yseries && graphConditionConfig?.includes(yseries?.attribute))
        flrSeriesExists = true;
    } else if (yseries && graphConditionConfig?.includes(yseries[1]?.attribute))
      flrSeriesExists = true;
  });
  let twoMonthsDuration = getCustomizedDate(6, "Days");
  let defaultDuration = {
    from:
      moment(twoMonthsDuration?.date[0]).format("YYYY/MM/DD") +
      " " +
      twoMonthsDuration?.time?.startTime,
    to:
      moment(twoMonthsDuration?.date[1]).format("YYYY/MM/DD") +
      " " +
      twoMonthsDuration?.time?.endTime,
  };

  let ticketOverviewId =
    graphObject?.index >= 0 &&
    graphObject?.panes[graphObject?.index]?.template_id;
  let startdate =
    graphObject?.index >= 0 &&
    graphObject?.panes[graphObject?.index]?.duration?.from
      ? graphObject?.index >= 0 &&
        graphObject?.panes[graphObject?.index]?.duration?.from
      : defaultDuration?.from;
  let endDate =
    graphObject?.index >= 0 &&
    graphObject?.panes[graphObject?.index]?.duration?.to
      ? graphObject?.index >= 0 &&
        graphObject?.panes[graphObject?.index]?.duration?.to
      : defaultDuration?.to;
  let diffDay = moment(endDate).diff(moment(startdate), "day");

  let date_check = moment(
    graphObject?.single && graphObject?.single[0]?.x[0]
  ).isValid();
  if (graphObject?.single[0]?.y_axis) {
    let obj = { ...graphObject };
    obj.single = graphObject.single[0];
    return getCombination(obj, authParams);
  } else {
    let arr = [];
    arr = graphObject?.metadata?.y_series
      ? graphObject?.metadata?.y_series
      : Object.keys(graphObject?.widgetGraphConfig?.y_axis).map(
          (key) => graphObject?.widgetGraphConfig?.y_axis[key]
        );
    let filteredArray = arr?.filter((d) => d.attribute);
    let updatedYAxisLabel = [];
    filteredArray?.map((d, i) => {
      if (i >= 1) {
        updatedYAxisLabel = updatedYAxisLabel + ", " + d.trace_name;
      } else {
        updatedYAxisLabel = updatedYAxisLabel + d.trace_name;
      }
    });
    let j = 0;
    let noOfIterationInColors = 0;
    let lengthOfColors = selectedColorArray?.length;
    let yAxisData = graphObject?.single?.map((e) => {
      return e?.data?.map((el, i) => {
        let dataColor;
        if (
          el?.color !== undefined &&
          el?.color !== "#000000" &&
          el?.color !== null
        ) {
          dataColor = el?.color;
        } else {
          if (j >= lengthOfColors) {
            noOfIterationInColors = parseInt(j / lengthOfColors);
            let rem = parseInt(j % lengthOfColors);
            dataColor =
              noOfIterationInColors > 0
                ? newColorShadeInHex6(
                    selectedColorArray[rem],
                    parseInt(noOfIterationInColors * 5)
                  )
                : selectedColorArray[j];
          } else {
            dataColor = selectedColorArray[j];
          }
          j++;
        }
        let lighterColor = graphObject?.chartGradientColor
          ? newColorShadeInHex6(dataColor, 60)
          : null;
        let darkerColor = dataColor;
        return {
          name: el?.name,
          data: el?.y,
          color: graphObject?.chartGradientColor
            ? {
                linearGradient: {
                  x1: 0,
                  x2: 0,
                  y1: 0,
                  y2: 1,
                },
                stops: [
                  [0, darkerColor],
                  [1, lighterColor],
                ],
              }
            : dataColor,
          attribute: el?.attribute,
          connectorColor: darkerColor,
        };
      });
    });

    let xAxisData = [];
    graphObject?.single?.forEach((e) => {
      xAxisData = [...xAxisData, ...e?.x];
    });

    let yAxisdataRating = [];
    yAxisData?.[0] &&
      yAxisData?.[0]?.map((el, i) => {
        if (el.attribute === "Rating") {
          yAxisdataRating.push({ ...el, name: `${el.name} ⭐` });
        } else {
          yAxisdataRating.push({ ...el, name: el.name });
        }
      });

    const durationType =
      (getWindowLocationPath()?.toLowerCase() ===
        "widgetmaker"?.toLowerCase() ||
        getWindowLocationPath()?.toLowerCase() ===
          "edit-widget"?.toLowerCase() ||
        graphObject?.dashboard ||
        graphObject?.panes?.[graphObject?.index]?.is_widget_maker_open_obj
          ?.open_flag) &&
      handleDateFormat(
        graphObject?.panes,
        graphObject?.index,
        graphObject?.section_index,
        graphObject?.widget_index,
        graphObject?.metadata,
        graphObject?.metadata?.x_axis?.attribute,
        graphObject?.xaxis,
        graphObject?.metadata?.x_axis?.date_part,
        graphObject?.single[0]?.x,
        graphObject?.section,
        graphObject?.template_widget,
        graphObject?.shareTemplateData,
        graphObject?.pdfDownloadStatus,
        graphObject?.isScheduleReportOpen,
        graphObject?.screen,
        graphObject?.template_details,
        false, // graphObject?.isDeepDive,
        null, //graphObject?.dateAggregationOfThisGraph,
        null, //graphObject?.deepdiveObject,
        null, //graphObject?.yAxesDateAttr,
        graphObject?.isDeepDiveDashboardView,
        graphObject?.deepdiveDashboardData,
        graphObject?.deepdiveDashboardTemplateData
      );

    let x_date_part = graphObject?.metadata?.x_axis?.date_part;
    let x_date_agg = graphObject?.metadata?.x_axis?.date_aggregation;

    // graphObject?.ticket_chart_type,
    // graphObject?.ticket_status_duration
    // );
    try {
      if (graphObject?.chartGlobalDataStacked) {
        if (yAxisdataRating && yAxisdataRating?.length > 0) {
          // yAxisdataRating = structuredClone(yAxisdataRating)?.reverse();
          yAxisdataRating = yAxisdataRating?.slice()?.reverse();
        } else if (yAxisData?.[0] && yAxisData?.[0]?.length > 0) {
          // yAxisData[0] = structuredClone(yAxisData?.[0])?.reverse();
          yAxisData[0] = yAxisData[0]?.slice()?.reverse();
        }
      }
    } catch (error) {
      console.error("Error While convert dataset to reverse");
    }
    const configObj = {
      chart: {
        inverted:
          graphObject.type === "lollipop"
            ? graphObject?.metadata?.chart?.chart_settings?.show_horizontal_view
              ? true
              : graphObject?.widgetGraphConfig?.chart_settings
                  ?.show_horizontal_view
              ? true
              : false
            : null,
        height: graphObject?.style.height,
        type:
          graphObject.type === "horizontal-bar"
            ? "bar"
            : graphObject.type === "lollipop"
            ? "lollipop"
            : "column",
        animation:
          graphObject.type === "horizontal-bar" && graphObject?.chart3dOption
            ? false
            : null,
        options3d:
          graphObject.type !== "lollipop"
            ? {
                enabled: graphObject?.chartShow3d,
                alpha: graphObject?.chart3dOption?.alpha,
                beta: graphObject?.chart3dOption?.beta,
                depth: graphObject?.chart3dOption?.depth,
                viewDistance: 25,
              }
            : {},
        marginRight: graphObject?.type === "horizontal-bar" ? 30 : undefined,
        reflow: true,
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          fontSize: "1.6rem",
        },
      },
      title: {
        text: "",
        style: {
          fontWeight: "bold",
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      xAxis: {
        scrollbar: {
          enabled:
            graphObject?.single?.[0]?.x?.length > 10 &&
            graphObject?.metadata?.widget_id === "000-700-003"
              ? true
              : false,
        },
        min:
          graphObject?.single?.[0]?.x?.length > 10 &&
          graphObject?.metadata?.widget_id === "000-700-003"
            ? 0
            : undefined,
        max:
          graphObject?.single?.[0]?.x?.length > 10 &&
          graphObject?.metadata?.widget_id === "000-700-003"
            ? 9
            : undefined,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
        title: {
          style: {
            fontWeight: "bold",
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          text:
            graphObject?.metadata?.x_axis?.attribute &&
            graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
              graphObject?.metadata?.x_axis?.attribute
            )
              ? !x_date_part && !x_date_agg
                ? "Date"
                : !x_date_part && x_date_agg === "1h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "2h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "3h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "4h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "6h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "8h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "12h"
                ? "Hour of Day"
                : !x_date_part && x_date_agg === "ww"
                ? "Week of Year"
                : !x_date_part && x_date_agg === "M"
                ? "Month of Year"
                : !x_date_part && x_date_agg === "Y"
                ? "Years"
                : graphObject?.metadata?.x_axis?.date_part === null ||
                  graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "1h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "2h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "3h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "4h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "6h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "8h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "12h"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "M"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.metadata?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.metadata?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.metadata?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.metadata?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.metadata?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.metadata?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.metadata?.x_axis?.date_part
              : !graphObject?.metadata?.x_axis?.attribute &&
                (graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                  "RecordDate" ||
                  graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                    "CreatedDate")
              ? graphObject?.widgetGraphConfig?.x_axis?.date_part === null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part === "D"
                ? "Date"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "hh"
                ? "Hour of Day"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dd"
                ? "Day of Month"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dw"
                ? "Day of Week"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "mm"
                ? "Month of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "ww"
                ? "Week of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part === "qq"
                ? "Quarter of Year"
                : graphObject?.widgetGraphConfig?.x_axis?.date_part
              : graphObject?.xaxis,
          enabled: true,
        },
        // categories: single[0]?.x?.map(
        // 	(e) => e?.slice(0, 15) + (e?.length > 15 ? "..." : "")
        // ),
        categories:
          graphObject?.dashboard &&
          graphObject?.ticket_widget_id === "tickets_overview_1" &&
          ticketOverviewId === "000-000-004" &&
          // diffDay < 6 &&
          graphObject?.ticket_chart_type !== "W" &&
          graphObject?.ticket_chart_type !== "M" &&
          graphObject?.ticket_chart_type !== "Y" &&
          (graphObject?.ticket_status_duration === 60 ||
            graphObject?.ticket_status_duration === 120 ||
            graphObject?.ticket_status_duration === 180 ||
            graphObject?.ticket_status_duration === 240 ||
            graphObject?.ticket_status_duration === 360 ||
            graphObject?.ticket_status_duration === 480 ||
            graphObject?.ticket_status_duration === 720)
            ? graphObject?.single[0]?.x?.map((e) =>
                moment(e).format("D MMM H:mm")
              )
            : graphObject?.dashboard &&
              graphObject?.ticket_widget_id === "tickets_overview_1" &&
              ticketOverviewId === "000-000-004" &&
              // diffDay >= 6  &&
              graphObject?.ticket_chart_type !== "W" &&
              graphObject?.ticket_chart_type !== "M" &&
              graphObject?.ticket_chart_type !== "Y"
            ? graphObject?.single[0]?.x?.map((e) => moment(e).format("D MMM"))
            : graphObject?.dashboard &&
              graphObject?.ticket_widget_id === "tickets_overview_1" &&
              ticketOverviewId === "000-000-004" &&
              diffDay >= 6 &&
              graphObject?.ticket_chart_type === "M"
            ? graphObject?.single[0]?.x?.map((e) =>
                moment(e).format("MMM YYYY")
              )
            : graphObject?.dashboard &&
              graphObject?.ticket_widget_id === "tickets_overview_1" &&
              ticketOverviewId === "000-000-004" &&
              diffDay >= 6 &&
              (graphObject?.ticket_chart_type === "W" ||
                graphObject?.ticket_chart_type === "Y")
            ? graphObject?.single[0]?.x?.map((e) => e)
            : // : (graphObject?.metadata &&
            //     graphObject?.metadata?.x_axis?.attribute === "RecordDate") ||
            //   (graphObject?.xaxis === "Date" &&
            //     graphObject?.metadata?.x_axis?.date_part == null)
            // ? graphObject?.single[0]?.x?.map((e) =>
            //     moment(e).format("MMM DD YYYY")
            //   )
            graphObject?.metadata &&
              (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) ||
                graphObject?.xaxis === "Date") &&
              ((graphObject?.pdfDownloadStatus &&
                graphObject?.metadata?.x_axis?.date_part !== null &&
                graphObject?.metadata?.x_axis?.date_part !== "D") ||
                ((!graphObject?.pdfDownloadStatus ||
                  graphObject?.isScheduleReportOpen) &&
                  (graphObject?.metadata?.x_axis?.date_part == null ||
                    graphObject?.metadata?.x_axis?.date_part == "D"))) &&
              durationType
            ? durationType
            : graphObject?.metadata &&
              (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              ) ||
                graphObject?.xaxis === "Date") &&
              graphObject?.metadata?.x_axis?.date_part !== null &&
              graphObject?.metadata?.x_axis?.date_part !== "D"
            ? graphObject?.single[0]?.x?.map((e) => e)
            : graphObject?.xaxis === "Date" &&
              (graphObject?.widgetGraphConfig?.x_axis?.date_part == null ||
                graphObject?.widgetGraphConfig?.x_axis?.date_part == "D")
            ? graphObject?.single[0]?.x?.map((e) =>
                moment(e).format("MMM DD YYYY")
              )
            : graphObject?.xaxis === "Review Ratings" && xaxisDataList
            ? xaxisDataList
            : graphObject?.single[0]?.x,
        labels: {
          useHTML: true, // Enable HTML rendering
          formatter:
            graphObject?.type === "horizontal-bar" // formatting for horizontal bar
              ? function () {
                  return date_check || graphObject?.pdfDownloadStatus
                    ? this.value
                    : this.value.length > 12 &&
                      graphObject?.single[0]?.x?.length > 5
                    ? this.value?.slice(0, 12) +
                      (this.value?.length > 12 ? "..." : "")
                    : this.value;
                }
              : function () {
                  // formatting for all other cases
                  if (date_check || graphObject?.pdfDownloadStatus) {
                    return this.value; // No formatting needed in this case
                  }

                  const text = String(this.value);
                  const maxLengthPerLine = 24;

                  if (text?.length <= maxLengthPerLine) {
                    return `<div style="text-align: right;">${text}</div>`; // Single-line, right-aligned
                  }

                  // Find the last space before maxLengthPerLine for a natural word break
                  let firstLineEnd = text?.lastIndexOf(" ", maxLengthPerLine);
                  if (firstLineEnd === -1) firstLineEnd = maxLengthPerLine; // If no space found, force split

                  const firstLine = text?.slice(0, firstLineEnd);
                  let secondLine = text?.slice(firstLineEnd + 1);

                  // Truncate the second line if it exceeds maxLengthPerLine
                  if (secondLine?.length > maxLengthPerLine) {
                    secondLine =
                      secondLine?.slice(0, maxLengthPerLine - 3) + "...";
                  }

                  // Return wrapped text with right alignment using div and HTML
                  return `<div style="text-align: right;">${firstLine}<br>${secondLine}</div>`;
                },
          rotation:
            graphObject?.type === "horizontal-bar"
              ? 0
              : graphObject?.single[0]?.x?.length > 5
              ? -90
              : 0,
          //style: { fontWeight: "normal",color:"#869DAD" },
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          skew3d: graphObject?.chartShow3d,
        },
        // labels: {
        //   formatter: function () {
        //     return this.value.length > 11 &&
        //       graphObject?.single[0]?.x?.length > 5
        //       ? this.value?.slice(0, 11) +
        //           (this.value?.length > 11 ? "..." : "")
        //       : this.value;
        //   },
        //   rotation: graphObject?.single[0]?.x?.length > 5 ? 45 : 0,
        // },
      },
      yAxis: {
        min:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? 0.1
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? 0.1
            : flrSeriesExists
            ? 0
            : undefined, //aiLogScale:aiLogScale!==null? aiLogScale : log,
        minTickInterval: flrSeriesExists
          ? 60
          : graphObject?.scaleFromZeroToOne
          ? 1
          : undefined,
        type:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? "logarithmic"
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? "logarithmic"
            : "", //aiLogScale:aiLogScale!==null? aiLogScale : log,
        // min: 0,
        title: {
          text:
            updatedYAxisLabel?.length > 0
              ? updatedYAxisLabel
              : graphObject?.yaxis,
          style: {
            fontWeight: "bold",
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
        },
        endOnTick: true,
        labels: {
          //format: graphObject?.chartDataLabelInPercent ? "{text}%" : "{text}",
          formatter:
            flrSeriesExists && !graphObject?.chartDataLabelInPercent
              ? function () {
                  return SecondstoHumanReadable(this?.value, true);
                }
              : graphObject?.chartDataLabelInPercent
              ? function () {
                  return this.value + "%";
                }
              : function () {
                  return graphObject?.scaleFromZeroToOne &&
                    !Number?.isInteger(this?.value)
                    ? kFormatter(Math?.floor(this?.value))
                    : kFormatter(this?.value);
                },
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
          },
          skew3d: graphObject?.chartShow3d,
        },
        stackLabels: {
          enabled: graphObject?.chartDataLabelInPercent
            ? false
            : graphObject?.chartGlobalDataLabel
            ? true
            : graphObject?.chartGlobalDataLabel == undefined
            ? true
            : false,
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
            fontSize: isTicketOverview1 ? "1.2rem" : "0.9rem",
            fontFamily: `"Roboto", sans-serif`,
            fill: "#869DAD",
            fontWeight: "bold",
            textOutline: "none",
          },
          verticalAlign:
            graphObject?.chartShow3d && graphObject.type !== "lollipop"
              ? "top"
              : undefined, // Aligns the labels to the top of the stacks
          y:
            graphObject?.chartShow3d && graphObject.type !== "lollipop"
              ? -5
              : undefined, // Moves the labels down by 5 pixels
          // style: {
          // 	fontWeight: "bold",
          // 	//color: 'gray',
          // 	textOutline: "none",
          // },
          //format: percent ? "{total}%" : "{total}",
          formatter: flrSeriesExists
            ? function () {
                return this.total != 0
                  ? (this.total / 60).toFixed(0) + " mins"
                  : "";
              }
            : function () {
                return this.total != 0 ? kFormatter(this.total) : "";
              },
          // formatter: function () {
          //   return this.total != 0 ? kFormatter(this.total) : "";
          // },
        },
        allowDecimals: false,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },

      credits: {
        enabled: false,
      },
      // legend: {
      // 	reversed: true,
      // },
      tooltip: {
        enabled: true,
        useHTML: true,
        borderWidth: 0,
        shadow: false,
        backgroundColor: "transparent",
        // headerFormat:
        //   '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">{series.name}</span><span class="text-Darkgray d-block mb-1"> <span class="text-Darkgray mr-1"> X: </span> {point.key} </span><div class="d-flex align-items-center"> ',
        // // pointFormat: graphObject?.chartGlobalDataStacked
        // //   ? "Y :{point.y} | {point.percentage:.1f}%"
        // //   : "Y :{point.y}",
        // pointFormatter: function () {
        //   let clr = this?.color
        //     ? this?.color?.stops?.length
        //       ? this?.color?.stops?.[0]?.[1]
        //       : this?.color
        //     : this?.series?.color?.stops?.length
        //     ? this?.series?.color?.stops?.[0]?.[1]
        //     : this?.series?.color;
        //   if (flrSeriesExists && graphObject?.chartGlobalDataStacked) {
        //     return (
        //       '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style="background-color:' +
        //       clr +
        //       ';">' +
        //       SecondstoHumanReadable(this?.y) +
        //       (this?.percentage
        //         ? " | " +
        //           this?.percentage?.toFixed(1)?.replace(/\.0$/, "") +
        //           "%"
        //         : "") +
        //       "</span></div></div>"
        //     );
        //   } else if (flrSeriesExists) {
        //     return (
        //       '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style="background-color:' +
        //       clr +
        //       ';">' +
        //       SecondstoHumanReadable(this?.y) +
        //       "</span></div></div>"
        //     );
        //   } else if (graphObject?.chartGlobalDataStacked) {
        //     return (
        //       '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style="background-color:' +
        //       clr +
        //       ';">' +
        //       this?.y +
        //       (this?.percentage
        //         ? " | " +
        //           this?.percentage?.toFixed(1)?.replace(/\.0$/, "") +
        //           "%"
        //         : "") +
        //       "</span></div></div>"
        //     );
        //   } else {
        //     return (
        //       '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style=";background-color:' +
        //       clr +
        //       ';">' +
        //       this?.y +
        //       "</span></div></div>"
        //     );
        //   }
        // },
        formatter: function () {
          let clr = this?.color
            ? this?.color?.stops?.length
              ? this?.color?.stops?.[0]?.[1]
              : this?.color
            : this?.series?.color?.stops?.length
            ? this?.series?.color?.stops?.[0]?.[1]
            : this?.series?.color;
          let header =
            '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">' +
            getNameWithChannelIconIfExists(this?.series?.name) +
            '</span><span class="text-Darkgray d-block mb-1"> <span class="text-Darkgray mr-1"> X: </span> ' +
            (this?.key ? this?.key : this?.point?.name) +
            ' </span><div class="d-flex align-items-center"> ';
          if (flrSeriesExists && graphObject?.chartGlobalDataStacked) {
            return (
              header +
              '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style="background-color:' +
              clr +
              ';">' +
              SecondstoHumanReadable(this?.y) +
              (this?.percentage
                ? " | " +
                  this?.percentage?.toFixed(1)?.replace(/\.0$/, "") +
                  "%"
                : "") +
              "</span></div></div>"
            );
          } else if (flrSeriesExists) {
            return (
              header +
              '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style="background-color:' +
              clr +
              ';">' +
              SecondstoHumanReadable(this?.y) +
              "</span></div></div>"
            );
          } else if (graphObject?.chartGlobalDataStacked) {
            return (
              header +
              '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style="background-color:' +
              clr +
              ';">' +
              this?.y?.toLocaleString() +
              (this?.percentage
                ? " | " +
                  this?.percentage?.toFixed(1)?.replace(/\.0$/, "") +
                  "%"
                : "") +
              "</span></div></div>"
            );
          } else {
            return (
              header +
              '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style=";background-color:' +
              clr +
              ';">' +
              this?.y?.toLocaleString() +
              "</span></div></div>"
            );
          }
        },
        style: {
          fontSize: "1.4rem",
          fontFamily: '"Roboto", sans-serif',
          color: "#3b5175",
        },
      },
      plotOptions: {
        connectorColor: "#b1bdd0",
        // lollipop: {
        //   grouping: graphObject?.chartGlobalDataStacked
        //     ? true
        //     : graphObject?.chartGlobalDataStacked == undefined
        //     ? false
        //     : true,
        // },
        column: {
          grouping: graphObject?.chartGlobalDataStacked
            ? false
            : graphObject?.chartGlobalDataStacked == undefined
            ? false
            : true, // Always group for unstacked
          groupPadding:
            graphObject?.chartGlobalDataStacked === true ||
            graphObject?.chartGlobalDataStacked == undefined
              ? 0.1
              : 0.2, // More space for unstacked
          pointPadding:
            graphObject?.chartGlobalDataStacked === true ||
            graphObject?.chartGlobalDataStacked == undefined
              ? 0.05
              : 0.15, // More space for unstacked
        },
        series: {
          stacking:
            graphObject?.chartGlobalDataStacked &&
            graphObject?.chartDataLabelInPercent
              ? "percent"
              : !graphObject?.chartGlobalDataStacked &&
                graphObject?.chartDataLabelInPercent &&
                graphObject?.type === "lollipop"
              ? "percent"
              : graphObject?.chartGlobalDataStacked
              ? "normal"
              : graphObject?.type === "lollipop" &&
                !graphObject?.chartGlobalDataStacked
              ? "normal"
              : graphObject?.chartGlobalDataStacked == undefined
              ? "normal"
              : undefined,
          dataLabels: {
            enabled: graphObject?.chartGlobalDataLabel
              ? true
              : graphObject?.chartGlobalDataLabel == undefined
              ? true
              : false,
            //format:graphObject?.chartDataLabelInPercent?'{percentage:.0f}%':'{y}',
            formatter: function () {
              if (graphObject.chartShow3d && this.percentage < 1) {
                return "";
              }
              return this.y != 0
                ? flrSeriesExists && !graphObject?.chartDataLabelInPercent
                  ? (this.y / 60).toFixed(0) + " mins"
                  : graphObject?.chartDataLabelInPercent
                  ? Number.parseFloat(this.percentage)
                      .toFixed(1)
                      ?.replace(/\.0$/, "") + "%"
                  : kFormatter(this.y)
                : this.y == 0
                ? flrSeriesExists && !graphObject?.chartDataLabelInPercent
                  ? "0 mins"
                  : graphObject?.chartDataLabelInPercent
                  ? Number.parseFloat(this.percentage)
                      .toFixed(1)
                      ?.replace(/\.0$/, "") + "%"
                  : ""
                : "";
            },
            style: !graphObject?.chartGlobalDataStacked
              ? {
                  color: isTicketOverview1
                    ? "#ffffff"
                    : authParams?.theme_type === "1"
                    ? "#b1bdd0"
                    : "#3B5175",
                  fontSize: isTicketOverview1 ? "1.2rem" : "0.9rem",
                  fontFamily: `"Roboto", sans-serif`,
                  fill: "#869DAD",
                  fontWeight: "bold",
                  textOutline: isTicketOverview1 ? "1px contrast" : "none",
                }
              : graphObject?.type === "lollipop"
              ? {
                  color: dynamicHighchartTextColor(
                    authParams,
                    graphObject?.isDeepDiveView,
                    graphObject?.pdfDownloadStatus ||
                      graphObject?.pptDownloadStatus ||
                      graphObject?.isScheduleReportOpen,
                    graphObject?.isDeepDiveDashboardView
                  ),
                  fontSize: "0.9rem",
                  fontFamily: `"Roboto", sans-serif`,
                  fill: "#ffffff",
                  fontWeight: "normal",
                  textOutline: "none",
                }
              : {
                  color: "#ffffff",
                  //color: "#3b5175",
                  fontSize: "0.9rem",
                  fontFamily: `"Roboto", sans-serif`,
                  fill: "#ffffff",
                  fontWeight: "normal",
                  textOutline: "none",
                },
            animation: graphObject?.pdfDownloadStatus ? false : true,
            // : {
            //     defer: 2000,
            //   },
            crop: true,
            overflow: !graphObject?.chartGlobalDataStacked
              ? "justify"
              : "center",
            inside: !graphObject?.chartGlobalDataStacked ? undefined : true,
            rotation:
              graphObject?.type === "lollipop"
                ? null
                : graphObject?.type === "horizontal-bar"
                ? 0
                : graphObject?.chartDataLabelInPercent
                ? -90
                : 0,
            borderWidth: 0,
            padding: 0,
          },
          animation: graphObject?.pdfDownloadStatus ? false : true,
          // : {
          //     defer: 1000,
          //   },
          //pointPadding: 0.1,
          useHTML: true,
          groupPadding: 0.2,
          shadow: false,
          //pointWidth: undefined,
          //minPointWidth: 8,
          maxPointWidth: 60,
          showInLegend: graphObject?.metadata?.chart
            ? graphObject?.metadata?.chart?.chart_settings?.chart_property
                ?.show_legend
            : graphObject?.metadata?.chart_property?.show_legend,
          borderRadius: {
            radius: 10,
            scope: graphObject?.chartGlobalDataStacked
              ? "stack"
              : graphObject?.chartGlobalDataStacked === undefined
              ? "stack"
              : "point",
          },
          borderWidth: graphObject?.chartGlobalDataStacked
            ? 1
            : graphObject?.chartGlobalDataStacked == undefined
            ? 1
            : 0,
          borderColor: authParams?.theme_type === "1" ? "#1b2f50" : "#fff",
        },
      },
      series: yAxisdataRating ? yAxisdataRating : yAxisData[0],

      exporting: {
        filename: graphObject?.widget_name
          ? graphObject?.widget_name
          : graphObject?.type,
        enabled:
          graphObject?.pdfDownloadStatus ||
          graphObject?.pptDownloadStatus ||
          graphObject?.isScheduleReportOpen ||
          authParams?.screen_id
            ? false
            : true,
        chartOptions: {
          chart: {
            style: {
              fontSize: "16px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          xAxis: {
            title: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
            labels: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
          },
          yAxis: {
            title: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
            labels: {
              style: {
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: '"Roboto", sans-serif',
              },
            },
            stackLabels: {
              style: {
                fontSize: "10px", // Set your desired font size here
                fontWeight: "bold",
                fontFamily: '"Roboto", sans-serif',
              },
            },
          },
          tooltip: {
            style: {
              fontSize: "14px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          plotOptions: {
            series: {
              dataLabels: {
                style: {
                  fontSize: "10px",
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: "500",
                },
              },
            },
          },
          legend: {
            itemStyle: {
              fontSize: "13px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
        menuItemDefinitions: {
          // Custom definition
          xsl: {
            onclick: () => {
              const excel = new Excel();
              let multiData = yAxisData[0];
              let tempMultidata = [];
              let showPercentChart = graphObject.chartDataLabelInPercent;
              if (showPercentChart) {
                // storing in separate array for percent chart
                multiData.forEach((data) => {
                  tempMultidata.push(data);
                  const perCentKeys = {
                    ...data,
                    name: `${data.name} Percent`, // created new key with Percent as suffix
                  };
                  tempMultidata.push(perCentKeys);
                });
                let percentage = multiData.map((data) => {
                  return {
                    ...data,
                    percent: [],
                  };
                });
                for (let i = 0; i < multiData[0].data.length; i++) {
                  let total = multiData.reduce(
                    (acc, cur) => acc + cur.data[i],
                    0
                  );
                  multiData.forEach((item, index) => {
                    let percent =
                      ((item.data[i] / total) * 100).toFixed(1) + "%";
                    percentage[index].percent.push(percent);
                  });
                }
                let newArr = [];
                let percInd = 0;
                for (let i = 0; i < tempMultidata.length; i++) {
                  if (i % 2 == 0) {
                    newArr.push(tempMultidata[i]);
                  } else {
                    let data = percentage[percInd].percent;
                    tempMultidata[i].data = data;
                    newArr.push(tempMultidata[i]);
                    percInd++;
                  }
                }
                multiData = newArr;
                //to add percent column at the end of excel
                const percentItems = multiData.filter((item) =>
                  item.name.includes("Percent")
                );
                const nonPercentItems = multiData.filter(
                  (item) => !item.name.includes("Percent")
                );
                multiData = [...nonPercentItems, ...percentItems];
              }

              let columns = [
                {
                  title: graphObject?.xaxis,
                  dataIndex: graphObject?.xaxis,
                },
              ];
              let datasource = [];
              let lengthOfMultiData = multiData?.length;
              let lengthOfColumns = lengthOfMultiData + 1;
              for (let i = 0; i < lengthOfColumns; i++) {
                if (i == 0) {
                  graphObject?.single[0]?.x?.forEach((xElem, x_index) => {
                    datasource[x_index] = { [columns[i]?.title]: xElem };
                  });
                } else {
                  columns[i] = {
                    title: multiData[i - 1]?.name,
                    dataIndex: multiData[i - 1]?.name,
                  };
                  multiData[i - 1]?.data?.forEach((data, data_index) => {
                    datasource[data_index] = {
                      ...datasource[data_index],
                      [columns[i]?.title]: flrSeriesExists
                        ? SecondstoHumanReadable(data)
                        : isNaN(Number(data))
                        ? data?.toString().includes("NaN")
                          ? Number(0)
                          : data
                        : Number(data), // data
                    };
                  });
                }
              }
              excel
                .addSheet(showPercentChart ? "percent-chart" : "chart")
                .addColumns(columns)
                .addDataSource(datasource, {
                  str2Percent: true,
                })
                .saveAs(
                  `${
                    graphObject?.widget_name
                      ? graphObject?.widget_name
                      : graphObject?.type
                  }.xlsx`
                );
            },
            text: "Download excel sheet",
          },
        },
        buttons: {
          contextButton: {
            menuItems:
              graphObject?.xaxis && graphObject?.yaxis
                ? [
                    "downloadPNG",
                    "downloadJPEG",
                    "downloadSVG",
                    "separator",
                    "xsl",
                  ]
                : ["downloadPNG", "downloadJPEG", "downloadSVG"],
            buttonSpacing: 1,
            height: 20,
            symbolSize: 12,
            symbolStrokeWidth: 2,
            symbolX: 10.5,
            symbolY: 10.5,
            width: 20,
            x: -2,
            y: -12,
          },
        },
      },
      // series: [
      // 	{
      // 		name: "John",
      // 		data: yAxisData[0],
      // 	},
      // 	{
      // 		name: "Jane",
      // 		data: yAxisData[1],
      // 	},
      // ],
      legend: graphObject?.legend_position
        ? {
            reversed: graphObject?.chartGlobalDataStacked ? true : false, // This will reverse the order of the legend
            itemWidth:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? undefined
                : 200,
            layout:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "horizontal"
                : "vertical",
            align:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? "center"
                : graphObject?.legend_position,
            verticalAlign:
              graphObject?.legend_position == "top" ||
              graphObject?.legend_position == "bottom"
                ? graphObject?.legend_position
                : "middle",

            itemStyle: {
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
              fontFamily: `"Roboto", sans-serif`,
            },
            itemHoverStyle: {
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          }
        : {
            reversed: graphObject?.chartGlobalDataStacked ? true : false, // This will reverse the order of the legend
            itemWidth: undefined,
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            itemStyle: {
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
              fontFamily: `"Roboto", sans-serif`,
            },
            itemHoverStyle: {
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
            },
            useHTML: true,
            labelFormatter: function () {
              let formattedName = getNameWithChannelIconIfExists(this?.name);
              return formattedName;
            },
          },
    };
    if (authParams?.screen_id) {
      configObj.xAxis.title.style.fontSize = "1.4rem";
      configObj.xAxis.title.style.fontWeight = "500";
      configObj.xAxis.labels.style.fontSize = "1.4rem";
      configObj.xAxis.labels.style.fontWeight = "500";
      configObj.yAxis.title.style.fontSize = "1.4rem";
      configObj.yAxis.title.style.fontWeight = "500";
      configObj.yAxis.labels.style.fontSize = "1.4rem";
      configObj.yAxis.labels.style.fontWeight = "500";
      configObj.legend.itemStyle.fontSize = "1.4rem";
    }
    return configObj;
  }
};

// export const getScatter = (graphObject, authParams) => {
//   selectedColorArray = graphObject?.accentColors
//     ? graphObject?.accentColors
//     : globalChartColor;
//   let multiData = [];
//   graphObject?.single[0]?.data?.map((d) => {
//     multiData.push({ name: d?.name, data: d?.y });
//   });

//   const obj = {
//     chart: {
//       type: "scatter",
//       zoomType: "xy",
//       reflow: true,
//       height: graphObject?.style?.height,
//       style: {
//         fontFamily: '"Roboto", sans-serif',
//       },
//     },
//     title: {
//       text: "",
//     },
//     xAxis: {
//       title: {
//         enabled: true,
//         text: graphObject?.xaxis,
//         style: {
//           color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
//         },
//       },
//       startOnTick: true,
//       endOnTick: true,
//       showLastLabel: true,
//     },
//     yAxis: {
//       min: undefined,
//       title: {
//         text: graphObject?.yaxis,
//         style: {
//           color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
//         },
//       },
//     },
//     legend: graphObject?.legend_position
//       ? {
//           itemWidth:
//             graphObject?.legend_position == "top" ||
//             graphObject?.legend_position == "bottom"
//               ? undefined
//               : 200,
//           layout:
//             graphObject?.legend_position == "top" ||
//             graphObject?.legend_position == "bottom"
//               ? "horizontal"
//               : "vertical",
//           align:
//             graphObject?.legend_position == "top" ||
//             graphObject?.legend_position == "bottom"
//               ? "center"
//               : graphObject?.legend_position,
//           verticalAlign:
//             graphObject?.legend_position == "top" ||
//             graphObject?.legend_position == "bottom"
//               ? graphObject?.legend_position
//               : "middle",
//           itemStyle: {
//             fontFamily: '"Roboto", sans-serif',
//             color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
//           },
//           itemHoverStyle: {
//             color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
//           },
//           useHTML: true,
//           labelFormatter: function () {
//             let formattedName = getNameWithChannelIconIfExists(this?.name);
//             return formattedName;
//           },
//         }
//       : {
//           itemWidth: undefined,
//           enabled: true,
//           layout: "horizontal",
//           align: "center",
//           verticalAlign: "bottom",
//           itemStyle: {
//             fontFamily: '"Roboto", sans-serif',
//             color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
//           },
//           itemHoverStyle: {
//             color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
//           },
//           useHTML: true,
//           labelFormatter: function () {
//             let formattedName = getNameWithChannelIconIfExists(this?.name);
//             return formattedName;
//           },
//         },
//     plotOptions: {
//       scatter: {
//         marker: {
//           radius: 6,
//           states: {
//             hover: {
//               enabled: true,
//               lineColor: "rgb(100,100,100)",
//             },
//           },
//         },
//         states: {
//           hover: {
//             marker: {
//               enabled: false,
//             },
//           },
//         },
//         tooltip: {
//           headerFormat: "<b>{series.name}</b><br>",
//           pointFormat: "{point.x} cm, {point.y} kg",
//           style: {
//             fontFamily: '"Roboto", sans-serif',
//             color: "#3b5175",
//           },
//         },
//       },
//     },
//     credits: {
//       enabled: false,
//     },
//     series: multiData,
//   };
//   return obj;
// };
export const getScatter = (graphObject, authParams) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;

  // console.log("getScatter got called : ", graphObject, authParams);
  let ySeriesFromConfig = graphObject?.metadata?.y_series
    ? graphObject?.metadata?.y_series
    : Object?.entries(graphObject?.widgetGraphConfig?.y_axis);
  let flrSeriesExists = false;
  let useVariableSize = false;
  let graphConditionConfig = [
    ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
    ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
  ];
  // console.log("ySeriesFromConfig : ", ySeriesFromConfig);
  ySeriesFromConfig?.forEach((yseries, index) => {
    if (yseries?.attribute) {
      if (yseries?.variable_size) useVariableSize = true;
      if (yseries && graphConditionConfig?.includes(yseries?.attribute))
        flrSeriesExists = true;
    } else if (yseries && Array.isArray(yseries) && yseries?.[1]?.attribute) {
      if (yseries?.[1]?.variable_size) useVariableSize = true;
      if (yseries && graphConditionConfig?.includes(yseries[1]?.attribute))
        flrSeriesExists = true;
    }
  });
  let date_check = moment(
    graphObject?.single && graphObject?.single?.[0]?.x?.[0]
  ).isValid();

  let multiData = [];
  graphObject?.single?.[0]?.data?.map((d) => {
    multiData.push({ name: d?.name, data: d?.y });
  });
  let dataOfSeries = graphObject?.single?.[0]?.data
    ? graphObject?.single?.[0]
    : graphObject?.single;
  // console.log("graphObject?.single : ", graphObject?.single);
  // console.log("dataOfSeries : ", dataOfSeries);
  // let checkIsSingleSeries = dataOfSeries?.data?.[0]?.name ? false : true;
  let checkIsSingleSeries = false; // making false because here only many data
  let checkIsBreakDownAdded =
    dataOfSeries?.data?.[0]?.breakdown_dimension &&
    dataOfSeries?.data?.[0]?.breakdown_val
      ? true
      : false;

  // d?.y[0]?.name // for multiple
  // return dummyResponseForQuadrant;
  let xAxisAttributeObj = ySeriesFromConfig?.[0]?.attribute
    ? ySeriesFromConfig[0]
    : ySeriesFromConfig?.[0]?.[1]?.attribute
    ? ySeriesFromConfig?.[0]?.[1]
    : null;
  let xAxisPointName = graphObject?.metadata?.x_axis?.trace_name
    ? graphObject?.metadata?.x_axis?.trace_name
    : graphObject?.xaxis;
  let xAxisName = xAxisAttributeObj?.trace_name
    ? xAxisAttributeObj?.trace_name
    : graphObject?.yaxis;
  let yAxisName = ySeriesFromConfig?.[1]?.attribute
    ? ySeriesFromConfig[1]?.trace_name
      ? ySeriesFromConfig[1]?.trace_name
      : ySeriesFromConfig[1]?.attribute
    : ySeriesFromConfig?.[1]?.[1]?.attribute
    ? ySeriesFromConfig?.[1]?.[1]?.trace_name
      ? ySeriesFromConfig?.[1]?.[1]?.trace_name
      : ySeriesFromConfig?.[1]?.[1]?.attribute
    : null;

  let maxOfXCategories = 5;
  let maxOfYAxis = 0;
  let xAxisCategoriesValues = dataOfSeries?.x?.map((el, i) => {
    return Math.round(parseFloat(el) * 100) / 100;
  });
  maxOfXCategories = xAxisCategoriesValues
    ? Math.max(...xAxisCategoriesValues)
    : 5;
  // console.log(
  //   "xAxisCategoriesValues : ",
  //   xAxisCategoriesValues,
  //   "checkIsSingleSeries : ",
  //   checkIsSingleSeries,
  //   "xAxisName : ",
  //   xAxisName,
  //   "yAxisName : ",
  //   yAxisName,
  //   "checkIsBreakDownAdded : ",
  //   checkIsBreakDownAdded
  // );

  let showMedianEnabled = graphObject?.showAsMedian;
  let y_axes_settings = graphObject?.metadata?.y_axis_settings
    ? graphObject?.metadata?.y_axis_settings
    : graphObject?.metadata?.chart?.chart_settings?.y_axes_settings;

  let mediansOfSeries = graphObject?.showAsMedian
    ? dataOfSeries?.medians
      ? dataOfSeries?.medians
      : null
    : null;
  if (mediansOfSeries && y_axes_settings?.[0]?.median) {
    mediansOfSeries.x_median = y_axes_settings?.[0]?.median;
  }
  if (
    mediansOfSeries &&
    y_axes_settings?.[1]?.is_median_static &&
    y_axes_settings?.[1]?.median
  ) {
    mediansOfSeries.y_median = y_axes_settings?.[1]?.median;
  }
  let showPlotBandColors = false;
  let plottableData = [];
  let formattedDataBasedOnBreakdown = {};
  // Before data processing calculating data based on breakdown if exists
  if (checkIsBreakDownAdded) {
    if (Array.isArray(dataOfSeries?.data)) {
      dataOfSeries?.data?.forEach((series, index) => {
        const key = series?.breakdown_val;
        if (!formattedDataBasedOnBreakdown?.[key]) {
          formattedDataBasedOnBreakdown[key] = {
            name: key,
            color:
              series?.color && series?.color !== "#000000"
                ? series?.color
                : selectedColorArray?.[index],
            data: [],
          };
        }
        // Push the data point into the proper group
        formattedDataBasedOnBreakdown[key].data.push({
          name: series?.name,
          x: parseFloat(series?.x), // Convert string x to a number
          y: series?.y,
          // marker: { radius: series?.size }, // Use the size value as marker radius
          // Dynamic marker size based on data
          // If useVariableSize is true, use series.size,
          ...(useVariableSize
            ? {
                size: series?.size,
                marker: {
                  radius: series?.size, // Use size from data or default
                },
              }
            : {}),
        });
      });
    }
  }
  // console.log("useVariableSize : ", useVariableSize);
  // Data processing started here
  if (checkIsBreakDownAdded) {
    // Convert the object into an array for Highcharts
    const seriesValues = Object.values(formattedDataBasedOnBreakdown);
    if (Array.isArray(seriesValues) && seriesValues?.length) {
      seriesValues?.forEach((series, index) => {
        let pointsData = [];
        if (Array.isArray(series?.data) && series?.data?.length) {
          pointsData = series?.data?.map((point, pointIndex) => {
            return {
              x: Math.round(parseFloat(point?.x) * 100) / 100,
              y: point?.y,
              name: point?.name,
              // Dynamic marker size based on data
              // If useVariableSize is true, use point.size or marker_size, else default to 6
              ...(useVariableSize
                ? {
                    marker: {
                      radius: point?.size, // Use size from data or default
                    },
                  }
                : {}),
            };
          });
        }
        let seriesObject = {
          name: series?.name ? series?.name : "Scatter",
          color:
            series?.color && series?.color !== "#000000"
              ? series?.color
              : selectedColorArray?.[index],
          data: pointsData,
        };
        plottableData?.push(seriesObject);
      });
    }
  } else {
    if (Array.isArray(dataOfSeries?.data)) {
      dataOfSeries?.data?.forEach((series, index) => {
        let pointData = [
          {
            x: Math.round(parseFloat(series?.x) * 100) / 100,
            y: series?.y,
            // Dynamic marker size based on data
            // If useVariableSize is true, use point.size or marker_size, else default to 6
            ...(useVariableSize
              ? {
                  marker: {
                    radius: series?.size, // Use size from data or default
                  },
                }
              : {}),
          },
        ];
        // [
        //   [Math.round(parseFloat(series?.x) * 100) / 100, series?.y],
        // ];
        if (maxOfYAxis < series?.y) maxOfYAxis = series?.y;
        let seriesObject = {
          name: series?.name ? series?.name : "Scatter",
          color:
            series?.color && series?.color !== "#000000"
              ? series?.color
              : selectedColorArray?.[index],
          data: pointData,
        };
        plottableData?.push(seriesObject);
      });
    }
  }
  // console.log(
  //   "mediansOfSeries : ",
  //   mediansOfSeries,
  //   "plottableData : ",
  //   plottableData,
  //   "formattedDataBasedOnBreakdown : ",
  //   formattedDataBasedOnBreakdown
  // );
  const obj = {
    chart: {
      type: "scatter",
      zoomType: "xy",
      reflow: true,
      height: graphObject?.style?.height,
      style: {
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        fontSize: "1.6rem",
      },
      plotBorderWidth: 1,
      events: {
        // load: function () {
        //   const chart = this;
        //   // Get the primary yAxis
        //   const yAxis = chart.yAxis[0];
        //   // Retrieve the actual extremes (min, max) calculated by Highcharts
        //   const { max } = yAxis.getExtremes();
        //   console.log("max : ", max);
        //   maxOfYAxis = max;
        //   // After setting the data, call redraw
        //   chart.redraw(); // Force the chart to redraw
        // },
      },
    },
    title: {
      text: "",
    },
    xAxis: {
      title: {
        enabled: true,
        text: xAxisName,
        style: {
          fontFamily: '"Roboto", sans-serif',
          fontWeight: "bold",
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      startOnTick: true,
      endOnTick: false,
      showFirstLabel: true,
      showLastLabel: true,
      min:
        !showMedianEnabled && xAxisAttributeObj?.attribute === "nss_score"
          ? -maxOfXCategories
          : 0,
      max: maxOfXCategories + 0.5, // adding 0.5 for extra space
      tickInterval: 1,
      labels: {
        fontSize: "1.4rem",
        useHTML: true, // Enable HTML rendering
        formatter: function () {
          let labelValue = flrSeriesExists
            ? SecondstoHumanReadable(this?.value, true)
            : kFormatter(this.value);
          let reviewAttributes =
            graphObject?.graphConditionConfig?.["review-attributes"];
          return `<div style="text-align: right;">${
            Array.isArray(reviewAttributes) &&
            reviewAttributes?.includes(xAxisAttributeObj?.attribute)
              ? `${labelValue} ⭐`
              : labelValue
          }</div>`;
        },
        rotation: 0,
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),

          // authParams?.screen_id
          //   ? "#fff"
          //   : authParams?.theme_type === "1"
          //   ? "#b1bdd0"
          //   : "#3B5175",
        },
      },
      style: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
      },
      // categories: xAxisCategoriesValues,

      plotLines: [
        {
          width: 2,
          value:
            !showMedianEnabled && xAxisAttributeObj?.attribute === "nss_score"
              ? 0
              : mediansOfSeries?.x_median
              ? mediansOfSeries?.x_median
              : undefined, // x median
          color: "var(--locobuzz-border-color)",
          dashStyle:
            !showMedianEnabled && xAxisAttributeObj?.attribute === "nss_score"
              ? "solid"
              : "Dash", // Change to Dashed style
          // label: {
          //   text: "Median",
          //   align: "right",
          // },
        },
      ],
      plotBands: [
        {
          from: showPlotBandColors ? mediansOfSeries?.x_median : undefined,
          to: showPlotBandColors ? maxOfXCategories + 0.5 : undefined, // Adjust this value based on your data range
          color: "rgba(0, 255, 0, 0.2)", // Quadrant 1
          // label: {
          //   text: "Quadrant 1",
          //   align: "center",
          //   style: {
          //     color: "black",
          //   },
          // },
        },
        {
          from: showPlotBandColors ? 0 : undefined,
          to: showPlotBandColors ? mediansOfSeries?.x_median : undefined,
          color: "rgba(0, 0, 255, 0.2)", // Quadrant 2
          // label: {
          //   text: "Quadrant 2",
          //   align: "center",
          //   style: {
          //     color: "black",
          //   },
          // },
        },
      ],
    },
    yAxis: {
      // showFirstLabel: false,
      min:
        graphObject?.yAxisScale &&
        graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
          ? 0.1
          : graphObject?.aiLogScale && graphObject?.aiLogScale === true
          ? 0.1
          : flrSeriesExists
          ? 0
          : 1, //aiLogScale:aiLogScale!==null? aiLogScale : log,,
      minTickInterval: flrSeriesExists ? 60 : 1,
      type:
        graphObject?.yAxisScale &&
        graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
          ? "logarithmic"
          : graphObject?.aiLogScale && graphObject?.aiLogScale === true
          ? "logarithmic"
          : "", //aiLogScale:aiLogScale!==null? aiLogScale : log,
      crossing:
        !showMedianEnabled && xAxisAttributeObj?.attribute === "nss_score"
          ? 0
          : undefined,
      title: {
        text: yAxisName,
        enabled: true,
        ...(!showMedianEnabled && xAxisAttributeObj?.attribute === "nss_score"
          ? { x: -30 }
          : {}),
        style: {
          fontWeight: "bold",
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          // authParams?.screen_id
          //   ? "#fff"
          //   : authParams?.theme_type === "1"
          //   ? "#b1bdd0"
          //   : "#3B5175",
        },
      },
      style: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
      },
      labels: {
        ...(!showMedianEnabled && xAxisAttributeObj?.attribute === "nss_score"
          ? { x: -10 }
          : {}),
        fontSize: "1.4rem",
        formatter: flrSeriesExists
          ? function () {
              return SecondstoHumanReadable(this?.value, true);
            }
          : function () {
              return kFormatter(this.value);
            },

        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),

          // authParams?.screen_id
          //   ? "#fff"
          //   : authParams?.theme_type === "1"
          //   ? "#b1bdd0"
          //   : "#3B5175",
        },
      },
      allowDecimals: false,
      plotLines: [
        {
          width: 2,
          value: mediansOfSeries?.y_median
            ? mediansOfSeries?.y_median
            : undefined, // y median
          color: "var(--locobuzz-border-color)",
          dashStyle: "Dash", // Change to Dashed style
          // label: {
          //   text: "Median",
          //   align: "right",
          // },
        },
      ],
      plotBands: [
        {
          id: `${graphObject?.metadata?.widget_id}_dynamicPlotBandForYAxis_quadrant4`,
          from: showPlotBandColors ? mediansOfSeries?.y_median : undefined,
          to: showPlotBandColors ? maxOfYAxis : undefined, // Adjust this value based on your data range
          color: "rgba(255, 0, 0, 0.2)", // Quadrant 4
          // label: {
          //     text: 'Quadrant 4',
          //     align: 'center',
          //     style: {
          //         color: 'black'
          //     }
          // }
        },
        {
          id: `${graphObject?.metadata?.widget_id}_dynamicPlotBandForYAxis_quadrant3`,
          from: showPlotBandColors ? 0 : undefined,
          to: showPlotBandColors ? mediansOfSeries?.y_median : undefined,
          color: "rgba(255, 255, 0, 0.2)", // Quadrant 3
          // label: {
          //     text: 'Quadrant 3',
          //     align: 'center',
          //     style: {
          //         color: 'black'
          //     }
          // }
        },
      ],
    },
    legend: graphObject?.legend_position
      ? {
          enabled: true,
          itemWidth:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? undefined
              : 200,
          layout:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? "horizontal"
              : "vertical",
          align:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? "center"
              : graphObject?.legend_position,
          verticalAlign:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? graphObject?.legend_position
              : "middle",
          itemStyle: {
            fontFamily: '"Roboto", sans-serif',
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          itemHoverStyle: {
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          useHTML: true,
          labelFormatter: function () {
            let formattedName = getNameWithChannelIconIfExists(this?.name);
            return formattedName;
          },
        }
      : {
          itemWidth: undefined,
          enabled: true,
          layout: "horizontal",
          align: "center",
          verticalAlign: "bottom",
          itemStyle: {
            fontFamily: '"Roboto", sans-serif',
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          itemHoverStyle: {
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          useHTML: true,
          labelFormatter: function () {
            let formattedName = getNameWithChannelIconIfExists(this?.name);
            return formattedName;
          },
        },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderWidth: 0,
      shadow: false,
      backgroundColor: "transparent",
      formatter: function () {
        let clr = this?.color
          ? this?.color?.stops?.length
            ? this?.color?.stops?.[0]?.[1]
            : this?.color
          : this?.series?.color?.stops?.length
          ? this?.series?.color?.stops?.[0]?.[1]
          : this?.series?.color;
        let reviewAttributes =
          graphObject?.graphConditionConfig?.["review-attributes"];
        let xValue = this?.x ? this?.x : this?.point?.category;
        let xAxisPointValue = this?.point?.name ? this?.point?.name : this?.key;
        xValue =
          Array.isArray(reviewAttributes) &&
          reviewAttributes?.includes(xAxisAttributeObj?.attribute)
            ? `${xValue} ⭐`
            : xValue;
        if (checkIsSingleSeries) {
          return (
            "<div class='tooltip_chart fontSize-16 d-flex align-items-center'><span class='text-Darkgray font-weight600 mr-1'> " +
            getNameWithChannelIconIfExists(xValue) +
            "</span>" +
            '<span class="stackbar__tooltip" style="background-color:' +
            clr +
            ';">' +
            (flrSeriesExists
              ? SecondstoHumanReadable(this?.y)
              : this?.y?.toLocaleString()) +
            "</span></div>"
          );
        }
        let xAxisPointHtml = checkIsBreakDownAdded
          ? '<span class="text-Darkgray d-block mb-1"> <span class="text-Darkgray mr-1"> ' +
            xAxisPointName +
            ": </span> " +
            xAxisPointValue +
            " </span>"
          : "";
        let header =
          '<div class="tooltip_chart"><span class="text-Darkgray fontSize-16 font-weight600 d-block mb-1">' +
          getNameWithChannelIconIfExists(this?.series?.name) +
          "</span>" +
          xAxisPointHtml +
          '<span class="text-Darkgray d-block mb-1"> <span class="text-Darkgray mr-1"> ' +
          xAxisName +
          ": </span> " +
          xValue +
          ' </span><div class="d-flex align-items-center"> ';
        if (flrSeriesExists) {
          return (
            header +
            '<span class="text-Darkgray mr-1"> ' +
            yAxisName +
            ': </span><span class="stackbar__tooltip" style="background-color:' +
            clr +
            ';">' +
            SecondstoHumanReadable(this?.y) +
            "</span></div></div>"
          );
        } else {
          return (
            header +
            '<span class="text-Darkgray mr-1"> ' +
            yAxisName +
            ': </span><span class="stackbar__tooltip" style=";background-color:' +
            clr +
            ';">' +
            this?.y?.toLocaleString() +
            "</span></div></div>"
          );
        }
      },

      style: {
        fontSize: "1.4rem",
        fontFamily: '"Roboto", sans-serif',
        color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
      },
    },
    plotOptions: {
      scatter: {
        marker: {
          enabled: true,
          radius: 8,
          states: {
            hover: {
              enabled: true,
              lineColor: "rgb(100,100,100)",
            },
          },
          symbol: "circle",
        },
        // states: {
        //   hover: {
        //     marker: {
        //       enabled: false, // Change to true if you want markers to show on hover
        //     },
        //   },
        // },
        // tooltip: {
        //   headerFormat: "<b>{series.name}</b><br>",
        //   pointFormat: "{point.x} cm, {point.y} kg",
        //   style: {
        //     fontFamily: '"Roboto", sans-serif',
        //     color: "#3b5175",
        //   },
        // },
        dataLabels: {
          enabled: graphObject?.chartGlobalDataLabel ? true : false,
          style: {
            fontWeight: "500",
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
            fontSize: "1rem",
            fontFamily: '"Roboto", sans-serif',
          },
          formatter: flrSeriesExists
            ? function () {
                return (this.y / 60).toFixed(0) + " mins";
              }
            : function () {
                return kFormatter(this.y);
              },
        },
        showInLegend: graphObject?.metadata?.chart
          ? graphObject?.metadata?.chart?.chart_settings?.chart_property
              ?.show_legend
          : graphObject?.metadata?.chart_property?.show_legend,
      },
    },
    exporting: {
      filename: graphObject?.widget_name
        ? graphObject?.widget_name
        : graphObject?.type,
      chartOptions: {
        chart: {
          style: {
            fontSize: "16px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        tooltip: {
          style: {
            fontSize: "14px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        plotOptions: {
          scatter: {
            dataLabels: {
              style: {
                fontSize: "10px",
                fontFamily: '"Roboto", sans-serif',
                fontWeight: "500",
              },
            },
          },
          series: {
            dataLabels: {
              style: {
                fontSize: "10px",
                fontFamily: '"Roboto", sans-serif',
                fontWeight: "500",
              },
            },
          },
        },
        legend: {
          itemStyle: {
            fontSize: "13px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
      },
      enabled:
        graphObject?.pdfDownloadStatus ||
        graphObject?.pptDownloadStatus ||
        graphObject?.isScheduleReportOpen ||
        authParams?.screen_id
          ? false
          : true,
      menuItemDefinitions: {
        // Custom definition
        xsl: {
          onclick: () => {
            const excel = new Excel();
            let xAxisColumnName = graphObject?.xaxis;
            let ySeriesFirst = xAxisName;
            let ySeriesSecond = yAxisName;

            let widgetData = graphObject?.metadata;
            let breakdownAttr;
            // This is for calculating the breakdown name
            if (checkIsBreakDownAdded) {
              breakdownAttr =
                widgetData?.y_axes && Array.isArray(widgetData?.y_axes)
                  ? widgetData?.y_axes?.[0]?.attribute
                    ? widgetData?.y_axes?.[0]
                    : null
                  : widgetData?.breakdown_by &&
                    Array.isArray(widgetData?.breakdown_by)
                  ? widgetData?.breakdown_by?.[0]?.attribute
                    ? widgetData?.breakdown_by?.[0]
                    : null
                  : null;
            }
            let breakdownColumnName = breakdownAttr?.trace_name || "Breakdown";
            let columns = checkIsBreakDownAdded
              ? [
                  { title: breakdownColumnName, dataIndex: "breakdown" },
                  { title: xAxisColumnName, dataIndex: "x_axis" },
                  { title: ySeriesFirst, dataIndex: "y-series-1" },
                  { title: ySeriesSecond, dataIndex: "y-series-2" },
                ]
              : [
                  { title: xAxisColumnName, dataIndex: "x_axis" },
                  { title: ySeriesFirst, dataIndex: "y-series-1" },
                  { title: ySeriesSecond, dataIndex: "y-series-2" },
                ];
            const dataSource = [];
            if (Array.isArray(plottableData)) {
              plottableData?.forEach((series) => {
                let xAxisDataValue = checkIsBreakDownAdded ? "" : series?.name;
                let breakdownDataValue = checkIsBreakDownAdded
                  ? series?.name
                  : "";
                series?.data &&
                  Array.isArray(series?.data) &&
                  series?.data?.forEach((point) => {
                    let ySeriesFirstValue = isNaN(Number(point?.x))
                      ? point?.x?.toString().includes("NaN")
                        ? Number(0)
                        : point?.x
                      : Number(point?.x);
                    let ySeriesSecondValue = isNaN(Number(point?.y))
                      ? point?.y?.toString().includes("NaN")
                        ? Number(0)
                        : point?.y
                      : Number(point?.y);
                    if (checkIsBreakDownAdded) {
                      xAxisDataValue = point?.name;
                      dataSource.push({
                        breakdown: breakdownDataValue,
                        x_axis: xAxisDataValue,
                        "y-series-1": ySeriesFirstValue,
                        "y-series-2": ySeriesSecondValue,
                      });
                    } else {
                      dataSource.push({
                        x_axis: xAxisDataValue,
                        "y-series-1": ySeriesFirstValue,
                        "y-series-2": ySeriesSecondValue,
                      });
                    }
                  });
              });
            }
            excel
              .addSheet("test")
              .addColumns(columns)
              .addDataSource(dataSource, {
                str2Percent: true,
              })
              .saveAs(
                `${
                  graphObject?.widget_name
                    ? graphObject?.widget_name
                    : graphObject?.type
                }.xlsx`
              );
          },
          text: "Download excel sheet",
        },
      },
      buttons: {
        contextButton: {
          menuItems:
            graphObject?.xaxis && graphObject?.yaxis
              ? [
                  "downloadPNG",
                  "downloadJPEG",
                  "downloadSVG",
                  "separator",
                  "xsl",
                ]
              : ["downloadPNG", "downloadJPEG", "downloadSVG"],
          buttonSpacing: 1,
          height: 20,
          symbolSize: 12,
          symbolStrokeWidth: 2,
          symbolX: 10.5,
          symbolY: 10.5,
          width: 20,
          x: -2,
          y: -12,
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: plottableData?.length ? plottableData : [],
  };
  // console.log("getScatter return obj : ", obj);
  return obj;
};
export const getQuadrant = (graphObject, authParams) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;

  // console.log("getQuadrant got called : ", graphObject, authParams);
  let ySeriesFromConfig = graphObject?.metadata?.y_series
    ? graphObject?.metadata?.y_series
    : Object?.entries(graphObject?.widgetGraphConfig?.y_axis);
  let flrSeriesExists = false;
  let graphConditionConfig = [
    ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
    ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
  ];

  ySeriesFromConfig?.forEach((yseries, index) => {
    if (yseries?.attribute) {
      if (yseries && graphConditionConfig?.includes(yseries?.attribute))
        flrSeriesExists = true;
    } else if (yseries && graphConditionConfig?.includes(yseries[1]?.attribute))
      flrSeriesExists = true;
  });
  let date_check = moment(
    graphObject?.single && graphObject?.single?.[0]?.x?.[0]
  ).isValid();

  let multiData = [];
  graphObject?.single?.[0]?.data?.map((d) => {
    multiData.push({ name: d?.name, data: d?.y });
  });
  let dataOfSeries = graphObject?.single?.[0]?.data
    ? graphObject?.single?.[0]
    : graphObject?.single;
  // console.log("graphObject?.single : ", graphObject?.single);
  // console.log("dataOfSeries : ", dataOfSeries);
  let checkIsSingleSeries = dataOfSeries?.data?.[0]?.name ? false : true;
  // d?.y[0]?.name // for multiple
  // return dummyResponseForQuadrant;

  let xAxisCategoriesValues = [];
  if (graphObject?.xaxis === "Review Ratings") {
    xAxisCategoriesValues = dataOfSeries?.x?.map((el, i) => {
      return `${el} ⭐`;
    });
    // xAxisCategoriesValues = dataOfSeries?.x;
  } else {
    xAxisCategoriesValues = dataOfSeries?.x;
  }
  // console.log(
  //   "xAxisCategoriesValues : ",
  //   xAxisCategoriesValues,
  //   "checkIsSingleSeries : ",
  //   checkIsSingleSeries
  // );
  let mediansOfSeries = graphObject?.showAsMedian
    ? dataOfSeries?.medians
      ? dataOfSeries?.medians
      : null
    : null;
  let plottableData = [];
  if (checkIsSingleSeries) {
    let pointData = Array.isArray(dataOfSeries?.data)
      ? dataOfSeries?.data?.map((d) => {
          return [d?.x, d?.y];
        })
      : [];
    let seriesObject = {
      name: dataOfSeries?.name ? dataOfSeries?.name : "Scatter",
      color:
        dataOfSeries?.color && dataOfSeries?.color !== "#000000"
          ? dataOfSeries?.color
          : selectedColorArray?.[0],
      data: pointData,
    };
    plottableData?.push(seriesObject);
  } else if (
    Array.isArray(dataOfSeries?.data) &&
    dataOfSeries?.data?.[0]?.name
  ) {
    dataOfSeries?.data?.forEach((series, index) => {
      let pointData = Array.isArray(series?.y)
        ? series?.y?.map((yValue, yIndex) => {
            return [dataOfSeries?.x?.[yIndex], yValue];
          })
        : [];
      let seriesObject = {
        name: series?.name ? series?.name : "Scatter",
        color:
          series?.color && series?.color !== "#000000"
            ? series?.color
            : selectedColorArray?.[index],
        data: pointData,
      };
      plottableData?.push(seriesObject);
    });
  }
  // console.log(
  //   "mediansOfSeries : ",
  //   mediansOfSeries,
  //   "plottableData : ",
  //   plottableData
  // );
  const obj = {
    chart: {
      type: "scatter",
      zoomType: "xy",
      reflow: true,
      height: graphObject?.style?.height,
      style: {
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        fontSize: "1.6rem",
      },
      plotBorderWidth: 1,
    },
    title: {
      text: "",
    },
    xAxis: {
      title: {
        enabled: true,
        text: graphObject?.xaxis,
        style: {
          fontFamily: '"Roboto", sans-serif',
          fontWeight: "bold",
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
        },
      },
      startOnTick: true,
      endOnTick: true,
      showLastLabel: true,
      // min: 0,
      // max: 10,
      // tickInterval: 1,
      labels: {
        useHTML: true, // Enable HTML rendering
        formatter: function () {
          if (date_check || graphObject?.pdfDownloadStatus) {
            return this.value; // No formatting needed in this case
          }

          const text = this.value;
          const maxLengthPerLine = 24;

          if (text.length <= maxLengthPerLine) {
            return `<div style="text-align: right;">${
              graphObject?.xaxis === "Review Ratings" ? `${text} ⭐` : text
            }</div>`; // Single-line, right-aligned
          }

          // Find the last space before maxLengthPerLine for a natural word break
          let firstLineEnd = text.lastIndexOf(" ", maxLengthPerLine);
          if (firstLineEnd === -1) firstLineEnd = maxLengthPerLine; // If no space found, force split

          const firstLine = text.slice(0, firstLineEnd);
          let secondLine = text.slice(firstLineEnd + 1);

          // Truncate the second line if it exceeds maxLengthPerLine
          if (secondLine.length > maxLengthPerLine) {
            secondLine = secondLine.slice(0, maxLengthPerLine - 3) + "...";
          }

          // Return wrapped text with right alignment using div and HTML
          return `<div style="text-align: right;">${firstLine}<br>${secondLine} ${
            graphObject?.xaxis === "Review Ratings" ? ` ⭐` : ""
          }</div>`;
        },
        rotation: graphObject?.single?.[0]?.x?.length > 5 ? -90 : 0,
        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),

          // authParams?.screen_id
          //   ? "#fff"
          //   : authParams?.theme_type === "1"
          //   ? "#b1bdd0"
          //   : "#3B5175",
        },
      },
      style: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
      },
      categories: xAxisCategoriesValues,

      plotLines: [
        {
          color: "red",
          width: 2,
          value: mediansOfSeries?.x_median
            ? mediansOfSeries?.x_median - 1
            : undefined, // x median
        },
      ],
    },
    yAxis: {
      min:
        graphObject?.yAxisScale &&
        graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
          ? 0.1
          : graphObject?.aiLogScale && graphObject?.aiLogScale === true
          ? 0.1
          : flrSeriesExists
          ? 0
          : undefined, //aiLogScale:aiLogScale!==null? aiLogScale : log,,
      minTickInterval: flrSeriesExists ? 60 : undefined,
      type:
        graphObject?.yAxisScale &&
        graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
          ? "logarithmic"
          : graphObject?.aiLogScale && graphObject?.aiLogScale === true
          ? "logarithmic"
          : "", //aiLogScale:aiLogScale!==null? aiLogScale : log,
      title: {
        text: graphObject?.yaxis,
        enabled: true,
        style: {
          fontWeight: "bold",
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          // authParams?.screen_id
          //   ? "#fff"
          //   : authParams?.theme_type === "1"
          //   ? "#b1bdd0"
          //   : "#3B5175",
        },
      },
      style: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
      },
      labels: {
        formatter: flrSeriesExists
          ? function () {
              return SecondstoHumanReadable(this?.value, true);
            }
          : function () {
              return kFormatter(this.value);
            },

        style: {
          fontFamily: '"Roboto", sans-serif',
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),

          // authParams?.screen_id
          //   ? "#fff"
          //   : authParams?.theme_type === "1"
          //   ? "#b1bdd0"
          //   : "#3B5175",
        },
      },
      allowDecimals: false,
      plotLines: [
        {
          color: "red",
          width: 2,
          value: mediansOfSeries?.y_median
            ? mediansOfSeries?.y_median
            : undefined, // y median
        },
      ],
    },
    legend: graphObject?.legend_position
      ? {
          enabled: plottableData?.length === 1 ? false : true,
          itemWidth:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? undefined
              : 200,
          layout:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? "horizontal"
              : "vertical",
          align:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? "center"
              : graphObject?.legend_position,
          verticalAlign:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? graphObject?.legend_position
              : "middle",
          itemStyle: {
            fontFamily: '"Roboto", sans-serif',
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          itemHoverStyle: {
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          useHTML: true,
          labelFormatter: function () {
            let formattedName = getNameWithChannelIconIfExists(this?.name);
            return formattedName;
          },
        }
      : {
          itemWidth: undefined,
          enabled: plottableData?.length === 1 ? false : true,
          layout: "horizontal",
          align: "center",
          verticalAlign: "bottom",
          itemStyle: {
            fontFamily: '"Roboto", sans-serif',
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          itemHoverStyle: {
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          useHTML: true,
          labelFormatter: function () {
            let formattedName = getNameWithChannelIconIfExists(this?.name);
            return formattedName;
          },
        },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderWidth: 0,
      shadow: false,
      backgroundColor: "transparent",
      formatter: function () {
        let clr = this?.color
          ? this?.color?.stops?.length
            ? this?.color?.stops?.[0]?.[1]
            : this?.color
          : this?.series?.color?.stops?.length
          ? this?.series?.color?.stops?.[0]?.[1]
          : this?.series?.color;
        if (checkIsSingleSeries) {
          return (
            "<div class='tooltip_chart fontSize-14 d-flex align-items-center'><span class='text-Darkgray font-weight600 mr-1'> " +
            getNameWithChannelIconIfExists(
              this?.x
                ? this?.x
                : this?.point?.category
                ? this?.point?.category
                : this?.key
                ? this?.key
                : this?.point?.name
            ) +
            "</span>" +
            '<span class="stackbar__tooltip" style="background-color:' +
            clr +
            ';">' +
            (flrSeriesExists
              ? SecondstoHumanReadable(this?.y)
              : this?.y?.toLocaleString()) +
            "</span></div>"
          );
        }

        let header =
          '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">' +
          getNameWithChannelIconIfExists(this?.series?.name) +
          '</span><span class="text-Darkgray d-block mb-1"> <span class="text-Darkgray mr-1"> X: </span> ' +
          (this?.x
            ? this?.x
            : this?.point?.category
            ? this?.point?.category
            : this?.key
            ? this?.key
            : this?.point?.name) +
          ' </span><div class="d-flex align-items-center"> ';
        if (flrSeriesExists) {
          return (
            header +
            '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style="background-color:' +
            clr +
            ';">' +
            SecondstoHumanReadable(this?.y) +
            "</span></div></div>"
          );
        } else {
          return (
            header +
            '<span class="text-Darkgray mr-1"> Y : </span><span class="stackbar__tooltip" style=";background-color:' +
            clr +
            ';">' +
            this?.y?.toLocaleString() +
            "</span></div></div>"
          );
        }
      },

      style: {
        fontSize: "1.4rem",
        fontFamily: '"Roboto", sans-serif',
        color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
      },
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 6,
          states: {
            hover: {
              enabled: true,
              lineColor: "rgb(100,100,100)",
            },
          },
        },
        states: {
          hover: {
            marker: {
              enabled: false,
            },
          },
        },
        // tooltip: {
        //   headerFormat: "<b>{series.name}</b><br>",
        //   pointFormat: "{point.x} cm, {point.y} kg",
        //   style: {
        //     fontFamily: '"Roboto", sans-serif',
        //     color: "#3b5175",
        //   },
        // },
        dataLabels: {
          enabled: graphObject?.chartGlobalDataLabel ? true : false,
          style: {
            fontWeight: "500",
            color: authParams?.theme_type === "1" ? "#b1bdd0" : "#3B5175",
            fontSize: "1rem",
            fontFamily: '"Roboto", sans-serif',
          },
          formatter: flrSeriesExists
            ? function () {
                return (this.y / 60).toFixed(0) + " mins";
              }
            : function () {
                return kFormatter(this.y);
              },
        },
        showInLegend: graphObject?.metadata?.chart
          ? graphObject?.metadata?.chart?.chart_settings?.chart_property
              ?.show_legend
          : graphObject?.metadata?.chart_property?.show_legend,
      },
    },
    exporting: {
      enabled: false,
      allowHTML: true,
    },
    credits: {
      enabled: false,
    },
    series: plottableData?.length ? plottableData : [],
  };
  return obj;
};

export const getCombination = (graphObject, authParams) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  const xaxisDataList = [];
  graphObject?.single?.x_axis?.map((el, i) => {
    xaxisDataList.push(`${el} ⭐`);
  });

  // Commented this because of logic updated
  // let arr = [];
  // arr = graphObject?.metadata?.y_series
  //   ? graphObject?.metadata?.y_series
  //   : Object.keys(graphObject?.widgetGraphConfig?.y_axis).map(
  //       (key) => graphObject?.widgetGraphConfig?.y_axis[key]
  //     );
  // let filteredArray = arr?.filter((d) => d.attribute);
  // let updatedXAxisLabel=[];
  // let updatedYAxisLabel = [];
  // filteredArray?.map((d, i) => {
  //   if (i >= 1) {
  //     if (i >= 2) {
  //       updatedYAxisLabel = updatedYAxisLabel + ", " + d.trace_name;
  //     } else {
  //       updatedYAxisLabel = updatedYAxisLabel + d.trace_name;
  //     }
  //   }else if(i===0){
  //     updatedXAxisLabel = d.trace_name;
  //   }
  // });

  let previewBenchmark = graphObject?.metadata?.comp_brand_list;
  let chartSettings = graphObject?.metadata?.payload?.chart?.chart_settings
    ? graphObject?.metadata?.payload?.chart?.chart_settings
    : graphObject?.metadata?.chart?.chart_settings
    ? graphObject?.metadata?.chart?.chart_settings
    : graphObject?.metadata?.chart_settings
    ? graphObject?.metadata?.chart_settings
    : null;
  let benchMarkTraceIsEnabled =
    chartSettings?.benchmark_line && chartSettings?.group_line_agg
      ? true
      : false;
  let y_axes_settings = graphObject?.metadata?.y_axis_settings
    ? graphObject?.metadata?.y_axis_settings
    : graphObject?.metadata?.chart?.chart_settings?.y_axes_settings;
  let graph_type = y_axes_settings?.filter((a) => a.show_as);
  let graph_y_axis_map_at = y_axes_settings;

  // currently disbaled this functionality
  // let sameShowAsExists=true;
  // let typeOfG1="bar";
  // graph_type?.forEach((g)=>{
  //   if(g?.show_as!==typeOfG1){
  //     sameShowAsExists=false;
  //   }
  // })
  // if(sameShowAsExists){
  //   updatedXAxisLabel=updatedXAxisLabel + ", " + updatedYAxisLabel;
  //   updatedYAxisLabel="";
  // }

  // let y_axis = graphObject?.metadata?.y_axis
  //   ? graphObject?.metadata?.y_axis
  //   : graphObject?.metadata?.y_series;
  // let meta_y_series_array = [];
  // meta_y_series_array = graphObject?.metadata?.y_series?.map((d) => {
  //   return graphObject?.attribute[d.attribute];
  // });
  let ySeriesFromConfig = graphObject?.metadata?.y_series
    ? graphObject?.metadata?.y_series
    : graphObject?.widgetGraphConfig?.y_axis?.y_series_1?.attribute
    ? Object?.entries(graphObject?.widgetGraphConfig?.y_axis)
    : [];
  // let y_series_array = [];
  // y_series_array =
  //   graphObject?.All_WM_attributes &&
  //   Object.keys(graphObject?.widgetGraphConfig?.y_axis).map(
  //     (key) =>
  //       graphObject?.All_WM_attributes[
  //         graphObject?.widgetGraphConfig?.y_axis[key]?.attribute
  //       ]
  //   );
  let s_data = [];
  let stackeBarData = [];
  let stackeBarDataExists = false;
  let smallestMin = 0;
  let greatestMax = 0;
  let flrSeriesExists = false;
  let allSeriesIsFLR = true;
  let latchAllAxisInSingle = false;
  let graphConditionConfig = [
    ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
    ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
  ];
  let seriesColorsFlag = false;
  graphObject?.single?.y_axis?.forEach((d, i) => {
    if (ySeriesFromConfig?.length > i) {
      if (
        !(
          (ySeriesFromConfig[i]?.attribute &&
            graphConditionConfig?.includes(ySeriesFromConfig[i]?.attribute)) ||
          graphConditionConfig?.includes(ySeriesFromConfig[i][1]?.attribute)
        )
      ) {
        allSeriesIsFLR = false;
      }
    }
  });
  let j = 0;
  let noOfIterationInColors = 0;
  let lengthOfColors = selectedColorArray?.length;
  graphObject?.single?.y_axis?.map((d, i) => {
    stackeBarData = [];
    if (d?.y[0]?.name) {
      stackeBarData = d.y?.map((el, ind) => {
        let dataColor;
        if (
          el?.color !== undefined &&
          el?.color !== "#000000" &&
          el?.color !== null
        ) {
          dataColor = el?.color;
        } else {
          if (j >= lengthOfColors) {
            noOfIterationInColors = parseInt(j / lengthOfColors);
            let rem = parseInt(j % lengthOfColors);
            dataColor =
              noOfIterationInColors > 0
                ? newColorShadeInHex6(
                    selectedColorArray[rem],
                    parseInt(noOfIterationInColors * 5)
                  )
                : selectedColorArray[j];
          } else {
            dataColor = selectedColorArray[j];
          }
          j++;
        }
        return {
          name: el?.name,
          data: el?.y,
          stacking: !graphObject?.chartGlobalDataStacked ? undefined : "normal",
          type:
            graph_type &&
            !graphObject?.competitorBenchmark &&
            !previewBenchmark &&
            !benchMarkTraceIsEnabled
              ? graph_type[i]?.show_as === "bar" || d?.chart_type === "bar"
                ? "column"
                : d?.chart_type
                ? d?.chart_type
                : graph_type[i]?.show_as
              : d?.chart_type === "bar"
              ? "column"
              : d.chart_type === "horizontal-bar"
              ? "bar"
              : d?.chart_type,
          color: dataColor,
          attribute: d?.attribute,
          data_source: d?.data_source,
          deep_dive_info: d?.deep_dive_info ? d?.deep_dive_info : null,
          isBenchmark: d?.isBenchmark ? true : false,
          yAxis:
            (graph_y_axis_map_at?.length > 0 &&
              graph_y_axis_map_at?.[i]?.y_axis_map_at === 1) ||
            (!graph_y_axis_map_at &&
              !ySeriesFromConfig?.length &&
              d?.y_axis_map_at === 1)
              ? 1
              : 0,
          parentName: d?.name,
          parentNameUsed: ind === 0 ? true : false,
          dataLabels: {
            style: {
              color: graphObject?.chartGlobalDataStacked
                ? "#ffffff"
                : authParams?.theme_type === "1"
                ? "#b1bdd0"
                : "#3B5175",
            },
          },
        };
      });
    }

    let seriesData = [];
    seriesColorsFlag = false;
    if (d?.colors != undefined && d?.colors?.length > 0) {
      seriesColorsFlag = true;
      seriesData = d?.y?.map((yElem, i) => {
        return {
          y: yElem,
          color:
            d?.colors != undefined
              ? d?.colors[i] != "#000000"
                ? d?.colors[i]
                : undefined
              : undefined,
        };
      });
    }
    if (ySeriesFromConfig?.length > i) {
      if (
        ySeriesFromConfig[i]?.attribute &&
        graphConditionConfig?.includes(ySeriesFromConfig[i]?.attribute)
      ) {
        flrSeriesExists = true;
      } else if (
        graphConditionConfig?.includes(ySeriesFromConfig[i][1]?.attribute)
      ) {
        flrSeriesExists = true;
      }
    }

    latchAllAxisInSingle =
      graphObject?.yAxisMode != 2 &&
      !flrSeriesExists &&
      graphObject?.yAxisScale &&
      ((graphObject?.yAxisScale?.[0]?.y_axis_scale !== "log" &&
        graphObject?.yAxisScale?.[1]?.y_axis_scale !== "log") ||
        (graphObject?.yAxisScale?.[0]?.y_axis_scale === "log" &&
          graphObject?.yAxisScale?.[1]?.y_axis_scale === "log"))
        ? true
        : false;
    if (!d?.y[0]?.name) {
      let dataColor;
      if (
        d?.color !== undefined &&
        d?.color !== "#000000" &&
        d?.color !== null
      ) {
        dataColor = d?.color;
      } else {
        if (j >= lengthOfColors) {
          noOfIterationInColors = parseInt(j / lengthOfColors);
          let rem = parseInt(j % lengthOfColors);
          dataColor =
            noOfIterationInColors > 0
              ? newColorShadeInHex6(
                  selectedColorArray[rem],
                  parseInt(noOfIterationInColors * 5)
                )
              : selectedColorArray[j];
        } else {
          dataColor = selectedColorArray[j];
        }
        j++;
      }
      let lineWidth;
      lineWidth = graphObject?.metadata?.chart?.chart_settings?.y_axes_settings[
        i
      ]?.lineWidth
        ? graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[i]
            ?.lineWidth
        : graphObject?.widgetGraphConfig?.y_axis_settings[i]?.lineWidth
        ? graphObject?.widgetGraphConfig?.y_axis_settings?.[i]?.lineWidth
        : 3;

      let lineStyle;
      lineStyle = graphObject?.metadata?.chart?.chart_settings?.y_axes_settings[
        i
      ]?.lineStyle
        ? graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[i]
            ?.lineStyle
        : graphObject?.widgetGraphConfig?.y_axis_settings[i]?.lineStyle
        ? graphObject?.widgetGraphConfig?.y_axis_settings?.[i]?.lineStyle
        : "Solid";

      s_data.push({
        //  name: meta_y_series_array ? meta_y_series_array[i] : y_series_array[i],
        lineWidth: lineWidth,
        dashStyle: lineStyle,
        name: d?.name,
        type:
          graph_type?.length > 0 &&
          !graphObject?.competitorBenchmark &&
          !previewBenchmark &&
          !benchMarkTraceIsEnabled
            ? graph_type[i]?.show_as === "bar"
              ? "column"
              : graph_type[i]?.show_as
            : d.chart_type === "bar"
            ? "column"
            : d.chart_type === "horizontal-bar"
            ? "bar"
            : d.chart_type,
        stacking: undefined,
        data: seriesColorsFlag ? seriesData : d?.y,
        color: seriesColorsFlag ? undefined : dataColor,
        attribute: d?.attribute,
        data_source: d?.data_source,
        deep_dive_info: d?.deep_dive_info ? d?.deep_dive_info : null,
        isBenchmark: d?.isBenchmark ? true : false,
        // yAxis: i < 2 && i + 1
        yAxis:
          (ySeriesFromConfig?.length > i &&
            ySeriesFromConfig?.[i]?.attribute &&
            graphConditionConfig?.includes(
              ySeriesFromConfig?.[i]?.attribute
            )) ||
          (ySeriesFromConfig?.length > i &&
            graphConditionConfig?.includes(
              ySeriesFromConfig?.[i]?.[1]?.attribute
            )) ||
          (ySeriesFromConfig?.length && i >= ySeriesFromConfig?.length) ||
          (i > 0 &&
            ((ySeriesFromConfig?.[i - 1]?.attribute &&
              graphConditionConfig?.includes(
                ySeriesFromConfig?.[i - 1]?.attribute
              )) ||
              graphConditionConfig?.includes(
                ySeriesFromConfig?.[i - 1]?.[1]?.attribute
              )) &&
            !ySeriesFromConfig?.[i]?.attribute &&
            ySeriesFromConfig?.length > i &&
            !ySeriesFromConfig?.[i]?.[1]?.attribute)
            ? graphObject?.yAxisMode != 2
              ? 0
              : !allSeriesIsFLR
              ? 0
              : graph_y_axis_map_at?.length > 0 && i === 0
              ? graph_y_axis_map_at?.[i]?.y_axis_map_at === 1
                ? 1
                : 0
              : graph_y_axis_map_at?.length > 0 &&
                graph_y_axis_map_at?.[i]?.y_axis_map_at === 0
              ? 0
              : 1
            : latchAllAxisInSingle && !flrSeriesExists
            ? 0
            : graph_y_axis_map_at?.length > 0 && i === 0
            ? graph_y_axis_map_at?.[i]?.y_axis_map_at === 1
              ? 1
              : 0
            : graph_y_axis_map_at?.length > 0 &&
              graph_y_axis_map_at?.[i]?.y_axis_map_at === 0
            ? 0
            : !graph_y_axis_map_at &&
              !ySeriesFromConfig?.length &&
              (d?.y_axis_map_at === 0 || d?.y_axis_map_at === 1)
            ? d?.y_axis_map_at
            : 1,
      });
    }

    if (d?.min < smallestMin) {
      smallestMin = d?.min;
    }
    if (d?.max > greatestMax) {
      greatestMax = d?.max;
    }
    if (stackeBarData?.length > 0) {
      try {
        if (graphObject?.chartGlobalDataStacked) {
          if (stackeBarData && stackeBarData?.length > 0) {
            // stackeBarData = structuredClone(stackeBarData)?.reverse();
            stackeBarData = stackeBarData?.slice()?.reverse();
            stackeBarDataExists = true;
          }
        }
      } catch (error) {
        console.error("Error While convert dataset to reverse");
      }
      s_data = [...stackeBarData, ...s_data];
    }
  });

  s_data = s_data?.filter((d) => d?.name);
  //if (s_data?.length > 0) s_data[0].yAxis = 0;

  let updatedXAxisLabel = "";
  let updatedYAxisLabel = "";
  //change related to make line & area to spline & areaspline
  s_data =
    s_data &&
    s_data.map((g, i) => {
      let lineStyle;
      let lineWidth;
      if (
        (graphObject?.widgetGraphConfig?.breakdown_by &&
          graphObject?.widgetGraphConfig?.breakdown_by?.length >= 1 &&
          graphObject?.widgetGraphConfig?.breakdown_by?.[0]?.attribute ===
            g?.attribute) ||
        (graphObject?.metadata?.y_axes &&
          graphObject?.metadata?.y_axes?.length >= 1 &&
          graphObject?.metadata?.y_axes?.[0]?.attribute === g?.attribute)
      ) {
        lineWidth = graphObject?.metadata?.chart?.chart_settings
          ?.y_axes_settings?.[0]?.lineWidth
          ? graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[0]
              ?.lineWidth
          : graphObject?.widgetGraphConfig?.y_axis_settings?.[0]?.lineWidth
          ? graphObject?.widgetGraphConfig?.y_axis_settings?.[0]?.lineWidth
          : 3;

        lineStyle = graphObject?.metadata?.chart?.chart_settings
          ?.y_axes_settings?.[0]?.lineStyle
          ? graphObject?.metadata?.chart?.chart_settings?.y_axes_settings?.[0]
              ?.lineStyle
          : graphObject?.widgetGraphConfig?.y_axis_settings?.[0]?.lineStyle
          ? graphObject?.widgetGraphConfig?.y_axis_settings?.[0]?.lineStyle
          : "Solid";
        g.dashStyle = lineStyle;
        g.lineWidth = lineWidth;
      }
      g.dashStyle = g?.type === "line" ? g?.dashStyle : "Solid";
      g.lineWidth = g?.type === "line" ? g?.lineWidth : 3;
      // g.stacking =
      //   g?.type === "line" || g?.type === "area"
      //     ? undefined
      //     : !graphObject?.chartGlobalDataStacked
      //     ? undefined
      //     : "normal";
      g.type =
        g?.type === "line"
          ? "spline"
          : g?.type === "area"
          ? "areaspline"
          : g?.type;
      //For Update color and shadows
      // if (g?.type === "spline") {
      //   g.shadow = {
      //     color:
      //       g?.color != undefined && g?.color != "#000000"
      //         ? `${g?.color}`
      //         : `${globalChartColor[i]}`,
      //     width: 10,
      //     opacity: 1,
      //     offsetX: 0,
      //     offsetY: 0,
      //   };
      // }
      if (g?.isBenchmark) {
        g.dashStyle = "ShortDash";
        g.color = "#A0A0A0";
        g.marker = {
          enabled: false,
          symbol: "circle",
          fillColor: "#FFFFFF",
          lineWidth: 3,
          lineColor: undefined, // inherit from series
        };
      }
      if (g?.type === "column") {
        if (g?.color) {
          g.color = graphObject?.chartGradientColor
            ? {
                linearGradient: {
                  x1: 0,
                  x2: 0,
                  y1: 0,
                  y2: 1,
                },
                stops:
                  g?.color != undefined && g?.color != "#000000"
                    ? [
                        [0, g?.color],
                        [1, `${g?.color}80`],
                      ]
                    : [
                        [0, selectedColorArray[j]],
                        [1, `${selectedColorArray[j++]}80`],
                      ],
              }
            : g?.color !== undefined &&
              g?.color !== "#000000" &&
              g?.color !== null
            ? g?.color
            : selectedColorArray[j++];
        } else if (g?.data?.[0]?.color) {
          g.data = g?.data?.map((s, ind) => {
            s.color = graphObject?.chartGradientColor
              ? {
                  linearGradient: {
                    x1: 0,
                    x2: 0,
                    y1: 0,
                    y2: 1,
                  },
                  stops:
                    s?.color != undefined && s?.color != "#000000"
                      ? [
                          [0, s?.color],
                          [1, `${s?.color}80`],
                        ]
                      : [
                          [0, selectedColorArray[j]],
                          [1, `${selectedColorArray[j++]}80`],
                        ],
                }
              : s?.color !== undefined &&
                s?.color !== "#000000" &&
                s?.color !== null
              ? s?.color
              : selectedColorArray[j++];
            return s;
          });
        }
      }
      if (g?.type === "areaspline") {
        g.fillColor = {
          linearGradient: [0, 0, 0, 300],
          stops: [
            [
              0,
              g?.color != undefined && g?.color != "#000000"
                ? `${g?.color}99`
                : `${selectedColorArray[j]}99`,
            ],
            [
              1,
              g?.color != undefined && g?.color != "#000000"
                ? `${g?.color}1a`
                : `${selectedColorArray[j]}1a`,
            ],
          ],
        };
        g.color =
          g?.color != undefined && g?.color != "#000000"
            ? `${g?.color}99`
            : `${selectedColorArray[j++]}99`;
      }
      // In last i'm checking yAxis for Label update
      if (g?.yAxis === 0) {
        if (g?.parentName && g?.parentNameUsed) {
          if (updatedXAxisLabel === "") {
            updatedXAxisLabel = updatedXAxisLabel + g?.parentName;
          } else {
            updatedXAxisLabel = updatedXAxisLabel + ", " + g?.parentName;
          }
        } else if (!g?.parentName) {
          if (updatedXAxisLabel === "") {
            updatedXAxisLabel = updatedXAxisLabel + g?.name;
          } else {
            updatedXAxisLabel = updatedXAxisLabel + ", " + g?.name;
          }
        }
      } else if (g?.yAxis === 1) {
        if (g?.parentName && g?.parentNameUsed) {
          if (updatedYAxisLabel === "") {
            updatedYAxisLabel = updatedYAxisLabel + g?.parentName;
          } else {
            updatedYAxisLabel = updatedYAxisLabel + ", " + g?.parentName;
          }
        } else if (!g?.parentName) {
          if (updatedYAxisLabel === "") {
            updatedYAxisLabel = updatedYAxisLabel + g?.name;
          } else {
            updatedYAxisLabel = updatedYAxisLabel + ", " + g?.name;
          }
        }
      }
      return g;
    });
  let date_check = moment(graphObject?.single?.x_axis?.[0]).isValid();

  const durationType =
    (getWindowLocationPath()?.toLowerCase() === "widgetmaker"?.toLowerCase() ||
      getWindowLocationPath()?.toLowerCase() === "edit-widget"?.toLowerCase() ||
      graphObject?.dashboard ||
      graphObject?.screen ||
      graphObject?.panes?.[graphObject?.index]?.is_widget_maker_open_obj
        ?.open_flag) &&
    handleDateFormat(
      graphObject?.panes,
      graphObject?.index,
      graphObject?.section_index,
      graphObject?.widget_index,
      graphObject?.metadata,
      graphObject?.metadata?.x_axis?.attribute,
      graphObject?.xaxis,
      graphObject?.metadata?.x_axis?.date_part,
      graphObject?.single?.x_axis,
      graphObject?.section,
      graphObject?.template_widget,
      graphObject?.shareTemplateData,
      graphObject?.pdfDownloadStatus,
      graphObject?.isScheduleReportOpen,
      graphObject?.screen,
      graphObject?.template_details,
      graphObject?.isDeepDive,
      graphObject?.dateAggregationOfThisGraph,
      graphObject?.deepdiveObject,
      false, // graphObject?.isDeepDive,
      null, //graphObject?.dateAggregationOfThisGraph,
      null, //graphObject?.deepdiveObject,
      null, //graphObject?.yAxesDateAttr,
      graphObject?.isDeepDiveDashboardView,
      graphObject?.deepdiveDashboardData,
      graphObject?.deepdiveDashboardTemplateData
    );

  let x_date_part =
    getWindowLocationPath()?.toLowerCase() === "CommandCenter"?.toLowerCase() &&
    graphObject?.isDeepDive &&
    graphObject?.dateAggregationOfThisGraph &&
    graphObject?.deepdiveObject?.start_date
      ? null
      : graphObject?.metadata?.x_axis?.date_part;
  let x_date_agg =
    getWindowLocationPath()?.toLowerCase() === "CommandCenter"?.toLowerCase() &&
    graphObject?.isDeepDive &&
    graphObject?.dateAggregationOfThisGraph &&
    graphObject?.deepdiveObject?.start_date
      ? graphObject?.dateAggregationOfThisGraph
      : graphObject?.metadata?.x_axis?.date_aggregation;

  // to bind xAxisLabel and YAxisLabel in case of YAxisMode === 1
  if (latchAllAxisInSingle && updatedYAxisLabel?.length) {
    updatedXAxisLabel = updatedXAxisLabel + ", " + updatedYAxisLabel;
    updatedYAxisLabel = "";
  }
  //to check all series lied on same axis based on yAxis
  let allSeriesMappedAtSameAxis = true;
  let axisOfG1 = null;
  s_data?.forEach((g) => {
    if (axisOfG1 === null) {
      axisOfG1 = g.yAxis;
    }
    if (axisOfG1 !== null && axisOfG1 !== g.yAxis) {
      allSeriesMappedAtSameAxis = false;
    }
  });
  const obj = {
    chart: {
      height: graphObject?.style?.height,
      zoomType: "xy",
      zooming: {
        mouseWheel: false,
      },
      reflow: true,
      style: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        fontSize: "1.6rem",
      },
    },
    title: {
      text: "",
      style: {
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
      },
    },
    xAxis: {
      title: {
        style: {
          fontWeight: "bold",
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
        },
        text:
          getWindowLocationPath()?.toLowerCase() ===
            "CommandCenter"?.toLowerCase() &&
          graphObject?.isDeepDive &&
          graphObject?.dateAggregationOfThisGraph &&
          graphObject?.deepdiveObject?.start_date
            ? !x_date_part && !x_date_agg
              ? "Date"
              : !x_date_part && x_date_agg === "1h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "2h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "3h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "4h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "6h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "8h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "12h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "ww"
              ? "Week of Year"
              : !x_date_part && x_date_agg === "M"
              ? "Month of Year"
              : !x_date_part && x_date_agg === "Y"
              ? "Years"
              : x_date_part === null || x_date_part === "D"
              ? "Date"
              : x_date_part === "1h"
              ? "Hour of Day"
              : x_date_part === "2h"
              ? "Hour of Day"
              : x_date_part === "3h"
              ? "Hour of Day"
              : x_date_part === "4h"
              ? "Hour of Day"
              : x_date_part === "6h"
              ? "Hour of Day"
              : x_date_part === "8h"
              ? "Hour of Day"
              : x_date_part === "12h"
              ? "Hour of Day"
              : x_date_part === "M"
              ? "Month of Year"
              : x_date_part === "D"
              ? "Date"
              : x_date_part === "hh"
              ? "Hour of Day"
              : x_date_part === "dd"
              ? "Day of Month"
              : x_date_part === "dw"
              ? "Day of Week"
              : x_date_part === "mm"
              ? "Month of Year"
              : x_date_part === "ww"
              ? "Week of Year"
              : x_date_part === "qq"
              ? "Quarter of Year"
              : x_date_part
            : graphObject?.metadata?.x_axis?.attribute &&
              graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
                graphObject?.metadata?.x_axis?.attribute
              )
            ? !x_date_part && !x_date_agg
              ? "Date"
              : !x_date_part && x_date_agg === "1h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "2h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "3h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "4h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "6h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "8h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "12h"
              ? "Hour of Day"
              : !x_date_part && x_date_agg === "ww"
              ? "Week of Year"
              : !x_date_part && x_date_agg === "M"
              ? "Month of Year"
              : !x_date_part && x_date_agg === "Y"
              ? "Years"
              : graphObject?.metadata?.x_axis?.date_part === null ||
                graphObject?.metadata?.x_axis?.date_part === "D"
              ? "Date"
              : graphObject?.metadata?.x_axis?.date_part === "1h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "2h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "3h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "4h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "6h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "8h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "12h"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "M"
              ? "Month of Year"
              : graphObject?.metadata?.x_axis?.date_part === "D"
              ? "Date"
              : graphObject?.metadata?.x_axis?.date_part === "hh"
              ? "Hour of Day"
              : graphObject?.metadata?.x_axis?.date_part === "dd"
              ? "Day of Month"
              : graphObject?.metadata?.x_axis?.date_part === "dw"
              ? "Day of Week"
              : graphObject?.metadata?.x_axis?.date_part === "mm"
              ? "Month of Year"
              : graphObject?.metadata?.x_axis?.date_part === "ww"
              ? "Week of Year"
              : graphObject?.metadata?.x_axis?.date_part === "qq"
              ? "Quarter of Year"
              : graphObject?.metadata?.x_axis?.date_part
            : !graphObject?.metadata?.x_axis?.attribute &&
              (graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                "RecordDate" ||
                graphObject?.widgetGraphConfig?.x_axis?.attribute ===
                  "CreatedDate")
            ? graphObject?.widgetGraphConfig?.x_axis?.date_part === null ||
              graphObject?.widgetGraphConfig?.x_axis?.date_part === "D"
              ? "Date"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "hh"
              ? "Hour of Day"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dd"
              ? "Day of Month"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "dw"
              ? "Day of Week"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "mm"
              ? "Month of Year"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "ww"
              ? "Week of Year"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part === "qq"
              ? "Quarter of Year"
              : graphObject?.widgetGraphConfig?.x_axis?.date_part
            : graphObject?.xaxis,
        enabled: true,
      },
      categories:
        graphObject?.template_id === "agent_activity_001" &&
        graphObject?.trends_type === "time"
          ? graphObject?.single?.x_axis?.map((e) =>
              moment(e).format("MMM DD YYYY HH:mm")
            )
          : graphObject?.metadata &&
            (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
              graphObject?.metadata?.x_axis?.attribute
            ) ||
              graphObject?.xaxis === "Date") &&
            ((graphObject?.pdfDownloadStatus &&
              graphObject?.metadata?.x_axis?.date_part !== null &&
              graphObject?.metadata?.x_axis?.date_part !== "D") ||
              ((!graphObject?.pdfDownloadStatus ||
                graphObject?.isScheduleReportOpen) &&
                (graphObject?.metadata?.x_axis?.date_part == null ||
                  graphObject?.metadata?.x_axis?.date_part == "D"))) &&
            durationType
          ? durationType
          : getWindowLocationPath()?.toLowerCase() ===
              "CommandCenter"?.toLowerCase() &&
            graphObject?.isDeepDive &&
            graphObject?.dateAggregationOfThisGraph &&
            graphObject?.deepdiveObject?.start_date
          ? durationType
          : graphObject?.metadata &&
            (graphObject?.graphConditionConfig?.[`date-attributes`]?.includes(
              graphObject?.metadata?.x_axis?.attribute
            ) ||
              graphObject?.xaxis === "Date") &&
            graphObject?.metadata?.x_axis?.date_part !== null &&
            graphObject?.metadata?.x_axis?.date_part !== "D"
          ? graphObject?.single?.x_axis?.map((e) => e)
          : // (graphObject?.metadata &&
          //   graphObject?.metadata?.x_axis?.attribute === "RecordDate") ||
          // (graphObject?.xaxis === "Date" &&
          //   graphObject?.metadata?.x_axis?.date_part == null)
          //   ? graphObject?.single?.x_axis?.map((e) =>
          //       moment(e).format("MMM DD YYYY")
          //     )
          graphObject?.xaxis === "Date" &&
            (graphObject?.widgetGraphConfig?.x_axis?.date_part == null ||
              graphObject?.widgetGraphConfig?.x_axis?.date_part == "D")
          ? graphObject?.single?.x_axis?.map((e) =>
              moment(e).format("MMM DD YYYY")
            )
          : graphObject?.xaxis === "Review Ratings" && xaxisDataList
          ? xaxisDataList
          : graphObject?.single?.x_axis,

      // categories: graphObject?.single?.x_axis,
      crosshair: true,
      labels: {
        useHTML: true, // Enable HTML rendering
        formatter: function () {
          if (date_check || graphObject?.pdfDownloadStatus) {
            return this.value; // No formatting needed in this case
          }

          const text = String(this.value);
          const maxLengthPerLine = 24;

          if (text?.length <= maxLengthPerLine) {
            return `<div style="text-align: right;">${text}</div>`; // Single-line, right-aligned
          }

          // Find the last space before maxLengthPerLine for a natural word break
          let firstLineEnd = text?.lastIndexOf(" ", maxLengthPerLine);
          if (firstLineEnd === -1) firstLineEnd = maxLengthPerLine; // If no space found, force split

          const firstLine = text?.slice(0, firstLineEnd);
          let secondLine = text?.slice(firstLineEnd + 1);

          // Truncate the second line if it exceeds maxLengthPerLine
          if (secondLine?.length > maxLengthPerLine) {
            secondLine = secondLine?.slice(0, maxLengthPerLine - 3) + "...";
          }

          // Return wrapped text with right alignment using div and HTML
          return `<div style="text-align: right;">${firstLine}<br>${secondLine}</div>`;
        },
        rotation: -90,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ),
          //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
        },
      },
    },
    // xAxis: [
    //   {
    //     categories: graphObject?.single?.x_axis,
    //     crosshair: true,
    //     labels: {
    //       formatter: function () {
    //         return date_check || graphObject?.pdfDownloadStatus
    //           ? this.value
    //           : this.value.length > 11 &&
    //             graphObject?.single?.x_axis?.length > 5
    //           ? this.value?.slice(0, 11) +
    //             (this.value?.length > 11 ? "..." : "")
    //           : this.value;
    //       },
    //       rotation: graphObject?.single?.x_axis?.length > 5 ? -45 : 0,
    //       //style: { fontWeight: "normal",color:"#869DAD" },
    //     },
    //     // labels: {
    //     //   formatter: function () {
    //     //     return this.value.length > 11 &&
    //     //       graphObject?.single?.x_axis?.length > 5
    //     //       ? this.value?.slice(0, 11) +
    //     //           (this.value?.length > 11 ? "..." : "")
    //     //       : this.value;
    //     //   },
    //     //   rotation: graphObject?.single?.x_axis?.length > 5 ? -45 : 0,
    //     //   //style: { fontWeight: "normal",color:"#869DAD" },
    //     // },
    //   },
    //   {
    //     title: {
    //       text: graphObject?.xaxis,
    //     },
    //   },
    // ],
    yAxis: [
      {
        min:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? 0.1
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? 0.1
            : graphObject?.yAxisMode != 2 && latchAllAxisInSingle
            ? smallestMin
            : flrSeriesExists
            ? 0
            : undefined, //aiLogScale:aiLogScale!==null? aiLogScale : log,
        // Primary yAxis
        max:
          graphObject?.yAxisMode != 2 && latchAllAxisInSingle
            ? greatestMax
            : undefined,
        type:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? "logarithmic"
            : "",
        title: {
          useHTML: false,
          text:
            latchAllAxisInSingle &&
            !flrSeriesExists &&
            updatedXAxisLabel?.length > 0
              ? updatedXAxisLabel
              : updatedXAxisLabel?.length > 0
              ? updatedXAxisLabel
              : allSeriesMappedAtSameAxis
              ? ""
              : graphObject?.yaxis,
          //text: graphObject?.yaxis,
          style: {
            fontWeight: "bold",
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
            //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
        },
        labels: {
          formatter:
            (ySeriesFromConfig &&
              ySeriesFromConfig?.[0]?.attribute &&
              graphConditionConfig?.includes(
                ySeriesFromConfig?.[0]?.attribute
              )) ||
            (ySeriesFromConfig &&
              graphConditionConfig?.includes(
                ySeriesFromConfig?.[0]?.[1]?.attribute
              ))
              ? function () {
                  return SecondstoHumanReadable(this?.value, true);
                }
              : function () {
                  return kFormatter(this.value);
                },
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), // authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
        },
        allowDecimals: false,
        //endOnTick: false,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
        },
        minTickInterval:
          (ySeriesFromConfig &&
            ySeriesFromConfig?.[0]?.attribute &&
            graphConditionConfig?.includes(
              ySeriesFromConfig?.[0]?.attribute
            )) ||
          (ySeriesFromConfig &&
            graphConditionConfig?.includes(
              ySeriesFromConfig?.[0]?.[1]?.attribute
            ))
            ? 60
            : undefined,
      },
      {
        // Secondary yAxis
        min:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[0]?.y_axis_scale === "log"
            ? 0.1
            : graphObject?.aiLogScale && graphObject?.aiLogScale === true
            ? 0.1
            : allSeriesIsFLR && graphObject?.yAxisMode == 2
            ? 0
            : undefined, //aiLogScale:aiLogScale!==null? aiLogScale : log,
        type:
          graphObject?.yAxisScale &&
          graphObject?.yAxisScale?.[1]?.y_axis_scale === "log"
            ? "logarithmic"
            : "",
        title: {
          useHTML: false,
          text:
            updatedYAxisLabel?.length > 0
              ? updatedYAxisLabel
              : (latchAllAxisInSingle && !flrSeriesExists) ||
                allSeriesMappedAtSameAxis
              ? ""
              : graphObject?.y_series2,
          style: {
            fontWeight: "bold",
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
        },
        labels: {
          formatter:
            allSeriesIsFLR &&
            ((ySeriesFromConfig &&
              ySeriesFromConfig?.[1]?.attribute &&
              graphConditionConfig?.includes(
                ySeriesFromConfig?.[1]?.attribute
              )) ||
              (ySeriesFromConfig &&
                graphConditionConfig?.includes(
                  ySeriesFromConfig?.[1]?.[1]?.attribute
                )))
              ? function () {
                  return SecondstoHumanReadable(this?.value, true);
                }
              : function () {
                  return kFormatter(this.value);
                },
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          enabled:
            graphObject?.yAxisMode == 2 ||
            flrSeriesExists ||
            !latchAllAxisInSingle
              ? true
              : false,
        },
        linkedTo: latchAllAxisInSingle ? 0 : undefined,
        opposite: true,
        allowDecimals: false,
        //endOnTick: false,
        style: {
          color: dynamicHighchartTextColor(
            authParams,
            graphObject?.isDeepDiveView,
            graphObject?.pdfDownloadStatus ||
              graphObject?.pptDownloadStatus ||
              graphObject?.isScheduleReportOpen,
            graphObject?.isDeepDiveDashboardView
          ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
        },
        minTickInterval:
          allSeriesIsFLR && graphObject?.yAxisMode == 2 ? 60 : undefined,
      },
    ],
    tooltip: {
      shared: true,
      enabled: true,
      useHTML: true,
      shadow: {
        color: "#0000001A",
      },
      className: "custom_background",
      // headerFormat:"<span style='font-size: 1rem'>{point.x} </span><br/>",
      // pointFormatter: function () {
      //   return '<span style="color:'+this.color+'">●</span>' + this.series.name + ' : <b>' + this.y + '</b><br/>';
      // }
      formatter:
        (ySeriesFromConfig &&
          ySeriesFromConfig?.[0]?.attribute &&
          graphConditionConfig?.includes(ySeriesFromConfig?.[0]?.attribute)) ||
        (ySeriesFromConfig &&
          graphConditionConfig?.includes(
            ySeriesFromConfig?.[0]?.[1]?.attribute
          ))
          ? function () {
              let xPointValue =
                this?.category ||
                this?.point?.category ||
                this?.key ||
                this?.point?.key ||
                this?.x ||
                this?.point?.x;
              let tooltipForCom =
                "<div class='tooltip_chart--dark'><span class='font-weight600 fontSize-14 text-Darkgray d-block mb-1'>" +
                xPointValue +
                "</span>";
              this.points?.forEach((point) => {
                let yPointValue =
                  (ySeriesFromConfig?.length > point?.series?.index &&
                    ySeriesFromConfig[point?.series?.index]?.attribute &&
                    graphConditionConfig?.includes(
                      ySeriesFromConfig[point?.series?.index]?.attribute
                    )) ||
                  (ySeriesFromConfig?.length > point?.series?.index &&
                    graphConditionConfig?.includes(
                      ySeriesFromConfig[point?.series?.index][1]?.attribute
                    )) ||
                  point?.series?.index >= ySeriesFromConfig?.length ||
                  (point?.series?.index > 0 &&
                    ((ySeriesFromConfig[point?.series?.index - 1]?.attribute &&
                      graphConditionConfig?.includes(
                        ySeriesFromConfig[point?.series?.index - 1]?.attribute
                      )) ||
                      graphConditionConfig?.includes(
                        ySeriesFromConfig[point?.series?.index - 1][1]
                          ?.attribute
                      )) &&
                    !ySeriesFromConfig[point?.series?.index]?.attribute &&
                    ySeriesFromConfig?.length > point?.series?.index &&
                    !ySeriesFromConfig[point?.series?.index][1]?.attribute)
                    ? SecondstoHumanReadable(point?.y)
                    : point?.y?.toLocaleString();
                let clr = point?.color
                  ? point?.color?.stops?.length
                    ? point?.color?.stops?.[0]?.[1]
                    : point?.color
                  : point?.series?.color?.stops?.length
                  ? point?.series?.color?.stops?.[0]?.[1]
                  : point?.series?.color;
                tooltipForCom =
                  tooltipForCom +
                  "<div class='d-flex align-items-center mb-1'><span class='text-Darkgray fontSize-12 mr-1'>" +
                  getNameWithChannelIconIfExists(point?.series?.name) +
                  "</span>" +
                  '<span class="stackbar__tooltip" style=";background-color:' +
                  clr +
                  ';">' +
                  yPointValue +
                  "</span></div>";
              });
              tooltipForCom = tooltipForCom + "</div>";
              return tooltipForCom;
            }
          : function () {
              let xPointValue =
                this?.category ||
                this?.point?.category ||
                this?.key ||
                this?.point?.key ||
                this?.x ||
                this?.point?.x;
              let tooltipForCom =
                "<div class='tooltip_chart--dark'><span class='font-weight600 fontSize-14 text-Darkgray d-block mb-1'>" +
                xPointValue +
                "</span>";
              this.points?.forEach((point) => {
                let yPointValue = point?.y?.toLocaleString();
                let clr = point?.color
                  ? point?.color?.stops?.length
                    ? point?.color?.stops?.[0]?.[1]
                    : point?.color
                  : point?.series?.color?.stops?.length
                  ? point?.series?.color?.stops?.[0]?.[1]
                  : point?.series?.color;
                tooltipForCom =
                  tooltipForCom +
                  "<div class='d-flex align-items-center mb-1'><span class='text-Darkgray fontSize-12 mr-1'>" +
                  getNameWithChannelIconIfExists(point?.series?.name) +
                  "</span>" +
                  '<span class="stackbar__tooltip" style=";background-color:' +
                  clr +
                  ';">' +
                  yPointValue +
                  "</span></div>";
              });
              tooltipForCom = tooltipForCom + "</div>";
              return tooltipForCom;
            },
    },
    legend: graphObject?.legend_position
      ? {
          reversed:
            stackeBarDataExists && graphObject?.chartGlobalDataStacked
              ? true
              : false, // This will reverse the order of the legend
          itemWidth:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? undefined
              : 200,
          layout:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? "horizontal"
              : "vertical",
          align:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? "center"
              : graphObject?.legend_position,
          verticalAlign:
            graphObject?.legend_position == "top" ||
            graphObject?.legend_position == "bottom"
              ? graphObject?.legend_position
              : "middle",
          itemStyle: {
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            fill: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          itemHoverStyle: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            fill: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          useHTML: true,
          labelFormatter: function () {
            let formattedName = getNameWithChannelIconIfExists(this?.name);
            return formattedName;
          },
        }
      : {
          reversed:
            stackeBarDataExists && graphObject?.chartGlobalDataStacked
              ? true
              : false, // This will reverse the order of the legend
          itemWidth: undefined,
          enabled: true,
          layout: "horizontal",
          align: "center",
          verticalAlign: "bottom",
          itemStyle: {
            fontFamily: '"Roboto", sans-serif',
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            fill: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), // authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          itemHoverStyle: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), // authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            fill: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
          },
          useHTML: true,
          labelFormatter: function () {
            let formattedName = getNameWithChannelIconIfExists(this?.name);
            return formattedName;
          },
        },
    credits: {
      enabled: false,
    },
    series: s_data,
    exporting: {
      chartOptions: {
        chart: {
          width: 800,
          height: 600,
        },
      },
      filename: graphObject?.widget_name
        ? graphObject?.widget_name
        : graphObject?.type,
      enabled:
        graphObject?.pdfDownloadStatus ||
        graphObject?.pptDownloadStatus ||
        graphObject?.isScheduleReportOpen ||
        authParams?.screen_id
          ? false
          : true,
      menuItemDefinitions: {
        // Custom definition
        xsl: {
          onclick: () => {
            const excel = new Excel();
            let multiData = s_data;
            let columns = [
              {
                title: graphObject?.xaxis,
                dataIndex: graphObject?.xaxis,
              },
            ];
            let datasource = [];
            let lengthOfMultiData = multiData?.length;
            let lengthOfColumns = lengthOfMultiData + 1;
            for (let i = 0; i < lengthOfColumns; i++) {
              if (i == 0) {
                graphObject?.single?.x_axis?.forEach((xElem, x_index) => {
                  datasource[x_index] = { [columns[i]?.title]: xElem };
                });
              } else {
                if (multiData[i - 1]?.name?.includes("|$|")) {
                  let ProfileName = multiData[i - 1]?.name
                    ?.split("|$|")?.[1]
                    ?.trim();
                  let channelName = multiData[i - 1]?.name
                    ?.split("|$|")?.[0]
                    ?.trim();
                  columns[i] = {
                    title: channelName + " | " + ProfileName,
                    dataIndex: multiData[i - 1]?.name,
                  };
                } else if (multiData[i - 1]?.name?.includes("|G|")) {
                  let genderName = multiData[i - 1]?.name
                    ?.split("|G|")?.[1]
                    ?.trim();

                  columns[i] = {
                    title: genderName,
                    dataIndex: multiData[i - 1]?.name,
                  };
                } else if (multiData[i - 1]?.name?.includes("|C|")) {
                  let countryName = multiData[i - 1]?.name
                    ?.split("|C|")?.[1]
                    ?.trim();

                  columns[i] = {
                    title: countryName,
                    dataIndex: multiData[i - 1]?.name,
                  };
                } else {
                  columns[i] = {
                    title: multiData[i - 1]?.name,
                    dataIndex: multiData[i - 1]?.name,
                  };
                }
                multiData[i - 1]?.data?.forEach((data, data_index) => {
                  datasource[data_index] = {
                    ...datasource[data_index],
                    [columns[i]?.dataIndex]: data?.y
                      ? graphConditionConfig?.includes(
                          multiData[i - 1]?.attribute
                        )
                        ? SecondstoHumanReadable(data?.y)
                        : isNaN(Number(data?.y))
                        ? data?.y?.toString().includes("NaN")
                          ? Number(0)
                          : data?.y
                        : Number(data?.y) // data?.y?.toLocaleString()
                      : graphConditionConfig?.includes(
                          multiData[i - 1]?.attribute
                        )
                      ? SecondstoHumanReadable(data)
                      : isNaN(Number(data))
                      ? data?.toString().includes("NaN")
                        ? Number(0)
                        : data
                      : Number(data), //
                  };
                });
              }
            }
            excel
              .addSheet("test")
              .addColumns(columns)
              .addDataSource(datasource, {
                str2Percent: true,
              })
              .saveAs(
                `${
                  graphObject?.widget_name
                    ? graphObject?.widget_name
                    : graphObject?.type
                }.xlsx`
              );
          },
          text: "Download excel sheet",
        },
      },
      buttons: {
        contextButton: {
          menuItems:
            graphObject?.xaxis && graphObject?.yaxis
              ? [
                  "downloadPNG",
                  "downloadJPEG",
                  "downloadSVG",
                  "separator",
                  "xsl",
                ]
              : ["downloadPNG", "downloadJPEG", "downloadSVG"],
          buttonSpacing: 1,
          height: 20,
          symbolSize: 12,
          symbolStrokeWidth: 2,
          symbolX: 10.5,
          symbolY: 10.5,
          width: 20,
          x: -2,
          y: -12,
        },
      },
      chartOptions: {
        chart: {
          style: {
            fontSize: "16px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        xAxis: {
          title: {
            style: {
              fontSize: "14px",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          labels: {
            style: {
              fontSize: "14px",
              fontWeight: "500",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
        yAxis: {
          title: {
            style: {
              fontSize: "14px",
              fontWeight: "500",
              fontFamily: '"Roboto", sans-serif',
            },
          },
          labels: {
            style: {
              fontSize: "14px",
              fontWeight: "500",
              fontFamily: '"Roboto", sans-serif',
            },
          },
        },
        tooltip: {
          style: {
            fontSize: "14px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        plotOptions: {
          series: {
            dataLabels: {
              style: {
                fontSize: "10px",
                fontFamily: '"Roboto", sans-serif',
                fontWeight: "500",
              },
            },
          },
        },
        legend: {
          itemStyle: {
            fontSize: "13px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
      },
    },
    plotOptions: {
      series: {
        lineWidth: 3,
        marker: {
          enabled:
            graphObject?.chartMarkers === false
              ? graphObject?.chartMarkers
              : true,
          //radius: graphObject?.chartMarkers === false ? 0 : 4,
          symbol: "circle",
          fillColor: "#FFFFFF",
          lineWidth: 3,
          lineColor: undefined, // inherit from series
        },
        stacking: "normal",
        dataLabels: {
          enabled: graphObject?.chartGlobalDataLabel ? true : false,
          style: {
            fontWeight: "500",
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ), //authParams?.theme_type === "1" ? "#b1bdd0" : "#3b5175",
            fontSize: "1rem",
            fontFamily: '"Roboto", sans-serif',
            textOutline: "none",
          },
          formatter: function () {
            let index = this?.series?.index;
            let yDataLabel =
              (ySeriesFromConfig?.length > index &&
                ySeriesFromConfig[index]?.attribute &&
                graphConditionConfig?.includes(
                  ySeriesFromConfig[index]?.attribute
                )) ||
              (ySeriesFromConfig?.length > index &&
                graphConditionConfig?.includes(
                  ySeriesFromConfig[index][1]?.attribute
                )) ||
              (flrSeriesExists && index >= ySeriesFromConfig?.length) ||
              (flrSeriesExists &&
                !ySeriesFromConfig[index]?.attribute &&
                !ySeriesFromConfig[index][1]?.attribute)
                ? (this?.y / 60)?.toFixed(1)?.replace(/\.0$/, "") + " mins"
                : this.y != 0
                ? kFormatter(this.y)
                : "";
            return yDataLabel;
          },
        },
        showInLegend: graphObject?.metadata?.chart
          ? graphObject?.metadata?.chart?.chart_settings?.chart_property
              ?.show_legend
          : graphObject?.metadata?.chart_property?.show_legend,
        // borderRadius: {
        //   radius: 10,
        //   scope: graphObject?.chartGlobalDataStacked ? "stack" : "point",
        // },
        borderRadius: {
          radius: 10,
          scope: graphObject?.chartGlobalDataStacked
            ? "stack"
            : graphObject?.chartGlobalDataStacked === undefined
            ? "stack"
            : "point",
        },
        borderWidth: 1,
        borderColor: authParams?.theme_type === "1" ? "#1b2f50" : "#fff",
      },
    },
    // series: stackeBarData
    // 	? [
    // 			...stackeBarData,
    // 			{
    // 				name: y_series2,
    // 				type:
    // 					yAxisChartType === "horizontal-bar"
    // 						? "bar"
    // 						: yAxisChartType === "bar"
    // 						? "column"
    // 						: yAxisChartType,
    // 				data: single?.y_axis?.line?.y,
    // 				// data: yData && yData[0]?.data[0].y,
    // 			},
    // 	  ]
    // 	: [
    // 			{
    // 				type:
    // 					xAxisChartType === "horizontal-bar"
    // 						? "bar"
    // 						: xAxisChartType === "bar"
    // 						? "column"
    // 						: xAxisChartType,

    // 				yAxis: 1,
    // 				name: yaxis,
    // 				data: single?.y_axis?.bar?.y,
    // 			},
    // 			// xAxisData[0],
    // 			{
    // 				name: y_series2,
    // 				type:
    // 					yAxisChartType === "horizontal-bar"
    // 						? "bar"
    // 						: yAxisChartType === "bar"
    // 						? "column"
    // 						: yAxisChartType,
    // 				data: single?.y_axis?.line?.y,
    // 				// data: yData && yData[0]?.data[0].y,
    // 			},
    // 	  ],
  };
  return obj;
};
// let easeOutBounce = function (pos) {
//     if ((pos) < (1 / 2.75)) {
//         return (7.5625 * pos * pos);
//     }
//     if (pos < (2 / 2.75)) {
//         return (7.5625 * (pos -= (1.5 / 2.75)) * pos + 0.75);
//     }
//     if (pos < (2.5 / 2.75)) {
//         return (7.5625 * (pos -= (2.25 / 2.75)) * pos + 0.9375);
//     }
//     return (7.5625 * (pos -= (2.625 / 2.75)) * pos + 0.984375);
// };

// Math.easeOutBounce = easeOutBounce;
export const getWordcloud = (graphObject, Highcharts, authParams) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  let differentialWordcloudData = [];
  let wordcloudData = [];
  let j = 0;
  let noOfIterationInColors = 0;
  let lengthOfColors = selectedColorArray?.length;
  let wordcloud_color = graphObject?.metadata?.chart?.chart_settings
    ?.wordcloud_color
    ? graphObject?.metadata?.chart?.chart_settings?.wordcloud_color
    : graphObject?.widgetGraphConfig?.chart_settings?.wordcloud_color
    ? graphObject?.widgetGraphConfig?.chart_settings?.wordcloud_color
    : "0";

  graphObject?.single && graphObject?.single?.[0]?.name != undefined
    ? graphObject?.single?.forEach((differentialData) => {
        let dataColor;
        if (
          differentialData?.color !== undefined &&
          differentialData?.color !== "#000000" &&
          differentialData?.color !== null &&
          wordcloud_color === "0"
        ) {
          dataColor = differentialData?.color;
        } else {
          dataColor = getDataColor(wordcloud_color, differentialData, j);
          j++;
        }
        differentialData?.data?.forEach((data, i) => {
          if (data?.text?.length) {
            differentialWordcloudData.push({
              // name: data?.text,
              tooltip_name: data?.text,
              name:
                data?.text?.length > 47
                  ? data?.text?.substring(0, 47) + "..."
                  : data?.text,
              weight: data?.value,
              color: dataColor,
              percentage: data?.percent,
              SentimentType: data?.SentimentType ? data?.SentimentType : null,
            });
          }
        });
      })
    : graphObject?.single?.map((data, i) => {
        if (data?.text?.length) {
          let dataColor;
          if (
            data?.color !== undefined &&
            data?.color !== "#000000" &&
            data?.color !== null &&
            wordcloud_color === "0"
          ) {
            dataColor = data?.color;
          } else {
            dataColor = getDataColor(wordcloud_color, data, j);
            j++;
          }
          wordcloudData?.push({
            // name: data?.text,
            tooltip_name: data?.text,
            name:
              data?.text?.length > 47
                ? data?.text?.substring(0, 47) + "..."
                : data?.text,
            weight: data?.value,
            color: dataColor,
            percentage: data?.percent,
            SentimentType: data?.SentimentType ? data?.SentimentType : null,
          });
        }
      });
  let shapeType = ["default", "random", "square", "spiral", "oval"];
  let selectedShapeType = setHighchartWordCloudShape(shapeType[0], Highcharts);
  const obj = {
    chart: {
      type: "wordcloud",
      reflow: true,
      height:
        differentialWordcloudData?.length > 0
          ? Number(String(graphObject?.style?.height)?.replace("px", "")) - 50
          : graphObject?.style?.height,
      // marginRight: 0,
      // marginLeft: 0,
      // marginBottom: 0,
      style: {
        fontFamily: graphObject?.fontFamily ? graphObject?.fontFamily : "Arial",
        // fontFamily: '"Rubik", serif',
        //textTransform: "uppercase",
      },
    },
    // accessibility: {
    // 	screenReaderSection: {
    // 		beforeChartFormat: '<h5>{chartTitle}</h5>' +
    // 			'<div>{chartSubtitle}</div>' +
    // 			'<div>{chartLongdesc}</div>' +
    // 			'<div>{viewTableButton}</div>'
    // 	}
    // },
    credits: {
      enabled: false,
    },
    series: [
      selectedShapeType === "spiral"
        ? {
            data:
              graphObject?.single && graphObject?.single[0]?.name
                ? differentialWordcloudData
                : wordcloudData,
            rotation: {
              from: 0,
              to: 0,
            },
            minFontSize: 7,
            style: {
              fontFamily: graphObject?.fontFamily
                ? graphObject?.fontFamily
                : "Arial",
              // fontFamily: '"Rubik", serif',
              fontWeight: "700",
              //textTransform: "uppercase",
            },
            spiral: "archimedean",
            placementStrategy: "center",
          }
        : selectedShapeType === "random"
        ? {
            data:
              graphObject?.single && graphObject?.single[0]?.name
                ? differentialWordcloudData
                : wordcloudData,
            rotation: {
              from: 0,
              to: 0,
            },
            minFontSize: 7,
            style: {
              fontFamily: graphObject?.fontFamily
                ? graphObject?.fontFamily
                : "Arial",
              // fontFamily: '"Rubik", serif',
              fontWeight: "700",
              //textTransform: "uppercase",
            },
            spiral: "rectangular",
            placementStrategy: "random",
          }
        : selectedShapeType === "square"
        ? {
            data:
              graphObject?.single && graphObject?.single[0]?.name
                ? differentialWordcloudData
                : wordcloudData,
            rotation: {
              from: 0,
              to: 0,
            },
            minFontSize: 7,
            style: {
              fontFamily: graphObject?.fontFamily
                ? graphObject?.fontFamily
                : "Arial",
              // ffontFamily: '"Rubik", serif',
              fontWeight: "700",
              //textTransform: "uppercase",
            },
            spiral: "square",
            placementStrategy: "center",
          }
        : selectedShapeType === "oval"
        ? {
            data:
              graphObject?.single && graphObject?.single[0]?.name
                ? differentialWordcloudData
                : wordcloudData,
            rotation: {
              from: 0,
              to: 0,
            },
            minFontSize: 7,
            style: {
              fontFamily: graphObject?.fontFamily
                ? graphObject?.fontFamily
                : "Arial",
              // fontFamily: '"Rubik", serif',
              fontWeight: "700",
              //textTransform: "uppercase",
            },
            spiral: "oval",
            placementStrategy: "center",
          }
        : {
            data:
              graphObject?.single && graphObject?.single[0]?.name != undefined
                ? differentialWordcloudData
                : wordcloudData,
            rotation: {
              from: 0,
              to: 0,
            },
            minFontSize: 7,
            style: {
              fontFamily: graphObject?.fontFamily
                ? graphObject?.fontFamily
                : "Arial",
              // fontFamily: '"Rubik", serif',
              fontWeight: "700",
              //textTransform: "uppercase",
            },
            spiral: "rectangular",
            placementStrategy: "center",
          },
    ],
    exporting: {
      filename: graphObject?.widget_name
        ? graphObject?.widget_name
        : graphObject?.type,
      enabled:
        graphObject?.pdfDownloadStatus ||
        graphObject?.pptDownloadStatus ||
        graphObject?.isScheduleReportOpen ||
        authParams?.screen_id
          ? false
          : true,
      menuItemDefinitions: {
        // Custom definition
        xsl: {
          onclick: () => {
            const excel = new Excel();
            let columns = [
              {
                title: graphObject?.xaxis,
                dataIndex: graphObject?.xaxis,
              },
              {
                title: graphObject?.yaxis,
                dataIndex: graphObject?.yaxis,
              },
            ];

            let datasource = graphObject?.single?.map((el, i) => {
              return {
                [columns[0]?.title]: el?.text,
                [columns[1]?.title]:
                  el?.value?.toLocaleString() + " | " + el?.percent + "%",
              };
            });
            excel
              .addSheet("test")
              .addColumns(columns)
              .addDataSource(datasource, {
                str2Percent: true,
              })
              .saveAs(
                `${
                  graphObject?.widget_name
                    ? graphObject?.widget_name
                    : graphObject?.type
                }.xlsx`
              );
          },
          text: "Download excel sheet",
        },
      },
      buttons: {
        contextButton: {
          menuItems:
            graphObject?.xaxis && graphObject?.yaxis
              ? [
                  "downloadPNG",
                  "downloadJPEG",
                  "downloadSVG",
                  "separator",
                  "xsl",
                ]
              : ["downloadPNG", "downloadJPEG", "downloadSVG"],
          buttonSpacing: 1,
          height: 20,
          symbolSize: 12,
          symbolStrokeWidth: 2,
          symbolX: 10.5,
          symbolY: 10.5,
          width: 20,
          x: -2,
          y: -12,
        },
      },
      chartOptions: {
        chart: {
          style: {
            fontSize: "16px",
            fontFamily: graphObject?.fontFamily
              ? graphObject?.fontFamily
              : "Arial",
          },
        },
        tooltip: {
          style: {
            fontSize: "14px",
            fontFamily: graphObject?.fontFamily
              ? graphObject?.fontFamily
              : "Arial",
            fontWeight: "700",
            textTransform: "uppercase",
          },
        },
        legend: {
          itemStyle: {
            fontSize: "13px",
            fontFamily: graphObject?.fontFamily
              ? graphObject?.fontFamily
              : "Arial",
          },
        },
        plotOptions: {
          series: {
            dataLabels: {
              style: {
                fontSize: "10px",
                fontFamily: graphObject?.fontFamily
                  ? graphObject?.fontFamily
                  : "Arial",
                fontWeight: "700",
              },
            },
          },
        },
      },
    },
    title: {
      text: graphObject?.title,
    },
    subtitle: {
      text: graphObject?.subtitle,
    },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      shadow: false,
      backgroundColor: "transparent",
      headerFormat:
        '<div class="tooltip_chart"><span class="text-Darkgray fontSize-14 font-weight600 d-block mb-1">{point.tooltip_name}</span><div class="d-flex align-items-center">',
      pointFormatter: function () {
        let clr = this?.color
          ? this?.color?.stops?.length
            ? this?.color?.stops?.[0]?.[1]
            : this?.color
          : this?.series?.color?.stops?.length
          ? this?.series?.color?.stops?.[0]?.[1]
          : this?.series?.color;
        return (
          '<span class="stackbar__tooltip" style="background-color:' +
          clr +
          ';">' +
          this?.weight?.toLocaleString() +
          (this?.percentage
            ? " | " + this?.percentage?.toFixed(1)?.replace(/\.0$/, "") + "%"
            : "") +
          "</span></div></div>"
        );
      },
      // pointFormatter: function (point) {

      //   return (
      //     "" +this.weight+
      //     " | " + (graphObject?.single && graphObject?.single[0]?.name
      //     ? differentialWordcloudData[this.index]?.percentage
      //     : wordcloudData[this.index]?.percentage) +
      //     "%"
      //   );
      // },
      style: {
        fontSize: "1.4rem",
        fontFamily: "Arial",
        // fontFamily: '"Rubik", serif',
        fontWeight: "700",
        textTransform: "uppercase",
      },
    },
    plotOptions: {
      wordcloud: {
        animation: graphObject?.pdfDownloadStatus ? false : true,
        // animation: {
        //   defer: 0,
        //   duration: 1000,
        // },
      },
    },
  };
  obj["wordCloudLegend"] =
    graphObject?.single && graphObject?.single[0]?.name ? true : false;
  return obj;
};

// Organization Chart
export const getOrganization = (graphObject, Highcharts) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  const obj = {
    chart: {
      height: 650,
      inverted: true,
      marginLeft: 20,
      marginRight: 20,
    },
    title: {
      text: null,
    },
    series: [
      {
        type: "organization",
        name: null,
        keys: ["from", "to"],
        linkRadius: 50,
        linkLineWidth: 3,
        linkColor: "#A4B9FF",
        data: graphObject?.single?.data?.data,
        nodes: graphObject?.single?.data?.nodes,

        colorByPoint: false,
        color: "#007ad0",
        dataLabels: {
          color: "white",
        },
        borderColor: "#fff",
        borderRadius: "20px",
        nodeWidth: 120,
        nodePadding: 80,
        dataLabels: {
          nodeFormatter: function () {
            let percentCount = this?.point?.percent
              ? this?.point?.percent
              : null;
            let imgPath = this?.point?.checked ? checkMark : closeMark;
            // console.log("this", this?.point);
            return (
              '<div style="border: 1px solid ' +
              this?.point?.border_color +
              "; background:" +
              (isArray(this.point.color)
                ? "transparent linear-gradient(97deg, " +
                  this.point.color?.[0] +
                  " 0%, " +
                  this.point.color?.[1] +
                  " 100%) 0% 0% no-repeat padding-box"
                : this.point.color) +
              ';" class="organization_node_container text-center"><div class="wm-chart-card-title mb-1 line-height-normal"><span style="color:' +
              this?.point?.name_color +
              ';" class="text-ellipsis d-inline-block wm-chart-card-title">' +
              (this.point.name === "AI Perceived Action"
                ? "<img style='margin: 5px; width:19px; height: 25px;' src='" +
                  aiStars +
                  "' /> "
                : "") +
              this?.point.name +
              '</span></div><div class="d-flex align-items-baseline justify-content-center"><span  style="color:' +
              this?.point?.value_color +
              ';" class="wm-chart-card-value mr-1 no--wrap">' +
              this?.point?.value?.toLocaleString() +
              "</span></div>" +
              (this?.point?.percent
                ? " <div class='organization_node_percent_container'><img style='margin-right:5px; width:12px; height: 12px;' src='" +
                  imgPath +
                  "' /> " +
                  "<p style='margin: 1px; font-weight: 900; color: " +
                  (this?.point?.checked ? "#5ED142" : "#DC1E1E") +
                  "'> " +
                  percentCount +
                  "%" +
                  "</p>" +
                  " </div>"
                : "") +
              "</div>"
            );
          },
        },
      },
    ],
    tooltip: {
      outside: true,
      formatter: function () {
        // console.log("this?.point data =>", this?.point);
        return this?.point?.fromNode?.name
          ? "<div> From: " +
              this?.point?.fromNode?.name +
              "<br>To: " +
              this?.point?.toNode?.name +
              "</div>"
          : "<div>" +
              this?.point?.name +
              "<br>" +
              this?.point?.value?.toLocaleString() +
              "</div>";
      },
    },
    exporting: {
      enabled: false,
      allowHTML: true,
      sourceWidth: 800,
      sourceHeight: 600,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      // stickyTracking: false,
      // organization: {
      //   linkColor: "green",
      // },
    },
  };

  // console.log("obj", obj);
  return obj;
};

const getDataColor = (wordcloud_color, data, j) => {
  let dataColor;
  let lengthOfColors = selectedColorArray?.length;
  let noOfIterationInColors = 0;

  if (wordcloud_color === "0") {
    if (j >= lengthOfColors) {
      noOfIterationInColors = parseInt(j / lengthOfColors);
      let rem = parseInt(j % lengthOfColors);
      dataColor =
        noOfIterationInColors > 0
          ? newColorShadeInHex6(
              selectedColorArray[rem],
              parseInt(noOfIterationInColors * 5)
            )
          : selectedColorArray[j];
    } else {
      dataColor = selectedColorArray[j];
    }
  } else if (wordcloud_color === "1") {
    if (data?.aspect_sentiment) {
      dataColor =
        data?.aspect_sentiment === "Positive"
          ? "#1c9f00"
          : data?.aspect_sentiment === "Negative"
          ? "#dc1e1e"
          : data?.aspect_sentiment === "Neutral"
          ? "#deaa0c"
          : data?.aspect_sentiment === "Others"
          ? "#6060FF"
          : data?.color;
    } else {
      dataColor =
        data?.SentimentType === "Positive"
          ? "#1c9f00"
          : data?.SentimentType === "Negative"
          ? "#dc1e1e"
          : data?.SentimentType === "Neutral"
          ? "#deaa0c"
          : data?.SentimentType === "Others"
          ? "#6060FF"
          : data?.color;
    }
  } else {
    dataColor = wordcloud_color;
  }
  return dataColor;
};

export const getTreeMap = (graphObject, authParams) => {
  selectedColorArray = graphObject?.accentColors
    ? graphObject?.accentColors
    : globalChartColor;
  // console.log("getTreeMap got called : ", graphObject, authParams);
  // Get time-related attributes for special formatting
  let flrSeriesExists = false;
  let isFirstSeriesIsFlr = false;
  let graphConditionConfig = [
    ...(graphObject?.graphConditionConfig?.["ticket-time-label-attrs"] ?? []),
    ...(graphObject?.graphConditionConfig?.["time-label-attrs"] ?? []),
  ];

  // Get y-series configuration
  let ySeriesFromConfig = graphObject?.metadata?.y_series
    ? graphObject?.metadata?.y_series
    : Object?.entries(graphObject?.widgetGraphConfig?.y_axis);

  // Check if time-related attributes are used
  ySeriesFromConfig?.forEach((yseries, index) => {
    if (yseries?.attribute) {
      if (yseries && graphConditionConfig?.includes(yseries?.attribute)) {
        if (index === 0) {
          isFirstSeriesIsFlr = true;
        }
        flrSeriesExists = true;
      }
    } else if (
      yseries &&
      graphConditionConfig?.includes(yseries[1]?.attribute)
    ) {
      if (index === 0) {
        isFirstSeriesIsFlr = true;
      }
      flrSeriesExists = true;
    }
  });

  // Get color settings from chart configuration
  const useColorGradient =
    graphObject?.chartGradientColor ||
    graphObject?.metadata?.chart?.chart_settings?.gradient_color ||
    graphObject?.widgetGraphConfig?.chart_settings?.gradient_color ||
    false;

  const colorMode =
    graphObject?.metadata?.chart?.chart_settings?.treemap_color_mode ||
    graphObject?.widgetGraphConfig?.chart_settings?.treemap_color_mode ||
    "value"; // 'value' or 'sentiment'

  // Get label visibility settings
  const showDataLabels = true; //Making by true  graphObject?.chartGlobalDataLabel ? true : false;

  const showDetailsOnCard = graphObject?.chartShowDetailsOnCard
    ? graphObject?.chartShowDetailsOnCard
    : false;

  const treeMapSingleData =
    graphObject?.single && Array.isArray(graphObject?.single)
      ? graphObject?.single?.[0]
      : [];

  // Process data for treemap
  let treemapData = [];
  let j = 0;
  const lengthOfColors = selectedColorArray?.length;
  // Process hierarchical data
  if (treeMapSingleData && Array.isArray(treeMapSingleData)) {
    if (treeMapSingleData?.some((item) => item?.parent)) {
      // Identify parents and children properly for hierarchical data
      const parentIds = new Set();
      const itemsById = new Map();

      // First pass: identify all parent IDs and build a lookup map
      treeMapSingleData.forEach((item) => {
        itemsById.set(item.id, item);
        if (item.parent) {
          parentIds.add(item.parent);
        }
      });

      // Create parent and child nodes separately
      const parentNodes = [];
      const childrenData = [];

      treeMapSingleData.forEach((item) => {
        // If this item has no parent but is referenced as a parent by other items
        if (!item.parent && parentIds.has(item.id)) {
          let itemColor = useColorGradient
            ? undefined
            : item.color && item.color !== "#000000"
            ? item.color
            : selectedColorArray[j++ % lengthOfColors];

          // Enhanced color assignment for better contrast and visibility
          // Use point background and border colors instead of dataLabel backgrounds for reliability
          let optimizedBackground = getOptimalTreemapBackground(
            itemColor,
            true
          );
          let optimizedDataLabelBackground = getOptimalTreemapBackground(
            itemColor,
            true,
            true // Use dataLabel background optimization
          );
          let optimizedTextColor = getOptimalTextColor(optimizedBackground);
          let optimizedBorderColor = getOptimalBorderColor(itemColor, true);
          let lighterColor = newColorShadeInHex6(itemColor, 80);
          // This is a top-level parent
          let itemObj = {
            id: item.id,
            name: item.name,
            color: optimizedBackground, // Use optimized background as point color
            value: item.value || 0,
            percentage: item.percentage,
            percent: item.percent,
            share: item.share,
            isParent: true,
            custom: {
              percentage: item.percentage,
              percent: item.percent,
              share: item.share,
              value: item.value,
              y_series: item.y_series || [],
              originalColor: itemColor, // Store original color for reference
              textColor: optimizedTextColor, // Store optimal text color
            },
            dataLabels: {
              enabled: showDataLabels,
              headers: true, // This is important for proper header styling
              align: "center",
              verticalAlign: "top",
              useHTML: true,
              inside: true,
              className: "treemap-data-label treemap-parent-label",
              shape: "callout",
              borderWidth: 1,
              borderColor: useColorGradient ? undefined : optimizedBorderColor,
              backgroundColor: itemColor
                ? optimizedDataLabelBackground
                : undefined, // Use a lighter color for better contrast
              // backgroundColor: itemColor ? lighterColor : undefined,
              // allowOverlap: false,
              style: {
                fontWeight: "bold",
                fontSize: "0.9em",
                textTransform: "uppercase",
                textOutline: "none",
                textShadow: "none",
              },
              padding: 0,
            },
            borderWidth: 2,
            groupPadding: 2,
            borderRadius: 6,
            borderColor:
              useColorGradient &&
              graphObject?.chartStyles?.dataLabelColor &&
              graphObject?.chartStyles?.dataLabelColor !== "#000000"
                ? graphObject?.chartStyles?.dataLabelColor
                : dynamicHighchartTextColor(
                    authParams,
                    graphObject?.isDeepDiveView,
                    graphObject?.pdfDownloadStatus ||
                      graphObject?.pptDownloadStatus ||
                      graphObject?.isScheduleReportOpen,
                    graphObject?.isDeepDiveDashboardView
                  ),

            colorByPoint: useColorGradient ? true : false, // Disable to use our custom colors
            // levelIsConstant: false,
          };
          // Only add colorValue if useColorGradient is true
          if (useColorGradient) {
            itemObj.colorValue = item.value || 0;
          }
          parentNodes.push(itemObj);
        } else if (item.parent) {
          // This is a child node
          let itemColor = useColorGradient
            ? undefined
            : item.color && item.color !== "#000000"
            ? item.color
            : selectedColorArray[j++ % lengthOfColors];

          // Enhanced color assignment for child nodes
          // Use point background and border colors instead of dataLabel backgrounds for reliability
          let optimizedBackground = getOptimalTreemapBackground(
            itemColor,
            false
          );
          let optimizedTextColor = getOptimalTextColor(optimizedBackground);
          let optimizedBorderColor = getOptimalBorderColor(itemColor, false);

          let itemObj = {
            id: item.id,
            name: item.name,
            parent: item.parent,
            value: item.value || 0,
            color: optimizedBackground, // Use optimized background as point color
            percentage: item.percentage,
            percent: item.percent,
            share: item.share,
            custom: {
              percentage: item.percentage,
              percent: item.percent,
              share: item.share,
              y_series: item.y_series || [],
              originalColor: itemColor, // Store original color for reference
              textColor: optimizedTextColor, // Store optimal text color
            },
            dataLabels: {
              enabled: showDataLabels,
              useHTML: true,
              inside: true,
              // allowOverlap: false,
              // inside: true,
              align: "center",
              verticalAlign: "middle",
              padding: 0,
              className: "treemap-data-label treemap-child-label",
              style: {
                fontWeight: "normal",
                fontSize: "0.8em",
                textOutline: "none",
                textShadow: "none",
              },
            },
            borderWidth: 1,
            // groupPadding: 1,
            borderRadius: 4,
            borderColor:
              useColorGradient &&
              graphObject?.chartStyles?.dataLabelColor &&
              graphObject?.chartStyles?.dataLabelColor !== "#000000"
                ? graphObject?.chartStyles?.dataLabelColor
                : optimizedBorderColor,
          };
          // Only add colorValue if useColorGradient is true
          if (useColorGradient) {
            itemObj.colorValue = item.value || 0;
          }
          childrenData.push(itemObj);
        } else {
          // Items that are neither parents nor children (orphans)
          // Should be rare, but handle just in case
          let itemColor = useColorGradient
            ? undefined
            : item.color && item.color !== "#000000"
            ? item.color
            : selectedColorArray[j++ % lengthOfColors];

          // Enhanced color assignment for orphan nodes (treat as parent)
          // Use point background and border colors instead of dataLabel backgrounds for reliability
          let optimizedBackground = getOptimalTreemapBackground(
            itemColor,
            true
          );
          let optimizedDataLabelBackground = getOptimalTreemapBackground(
            itemColor,
            true,
            true // Use dataLabel background optimization
          );
          let optimizedTextColor = getOptimalTextColor(optimizedBackground);
          let optimizedBorderColor = getOptimalBorderColor(itemColor, true);
          let lighterColor = newColorShadeInHex6(itemColor, 80);
          let itemObj = {
            id: item.id,
            name: item.name,
            color: optimizedBackground, // Use optimized background as point color
            value: item.value || 0,
            percentage: item.percentage,
            percent: item.percent,
            share: item.share,
            isParent: true, // Also mark orphans as parent nodes
            custom: {
              percentage: item.percentage,
              percent: item.percent,
              share: item.share,
              y_series: item.y_series || [],
              originalColor: itemColor, // Store original color for reference
              textColor: optimizedTextColor, // Store optimal text color
            },
            dataLabels: {
              enabled: showDataLabels,
              headers: true, // This is important for proper header styling
              align: "center",
              verticalAlign: "top",
              useHTML: true,
              inside: true,
              className: "treemap-data-label treemap-parent-label",
              shape: "callout",
              borderWidth: 1,
              borderColor: useColorGradient ? undefined : optimizedBorderColor,
              backgroundColor: itemColor
                ? optimizedDataLabelBackground
                : undefined, // Use a lighter color for better contrast
              // allowOverlap: false,
              style: {
                fontWeight: "bold",
                fontSize: "0.9em",
                textTransform: "uppercase",
                textOutline: "none",
                textShadow: "none",
              },
              padding: 0,
            },
            borderWidth: 2,
            groupPadding: 2,
            borderRadius: 6,
            borderColor:
              useColorGradient &&
              graphObject?.chartStyles?.dataLabelColor &&
              graphObject?.chartStyles?.dataLabelColor !== "#000000"
                ? graphObject?.chartStyles?.dataLabelColor
                : dynamicHighchartTextColor(
                    authParams,
                    graphObject?.isDeepDiveView,
                    graphObject?.pdfDownloadStatus ||
                      graphObject?.pptDownloadStatus ||
                      graphObject?.isScheduleReportOpen,
                    graphObject?.isDeepDiveDashboardView
                  ),
            colorByPoint: useColorGradient ? true : false, // Disable to use our custom colors
            levelIsConstant: false,
          };
          // Only add colorValue if useColorGradient is true
          if (useColorGradient) {
            itemObj.colorValue = item.value || 0;
          }
          parentNodes.push(itemObj);
        }
      });
      // Ensure all parents referenced by children actually exist
      const referencedParentIds = new Set(
        childrenData.map((item) => item.parent)
      );
      const existingParentIds = new Set(parentNodes.map((item) => item.id));

      // Create missing parents if needed
      referencedParentIds.forEach((parentId) => {
        if (!existingParentIds.has(parentId)) {
          // console.log(`Creating missing parent node: ${parentId}`);
          let itemColor = useColorGradient
            ? undefined
            : selectedColorArray[j++ % lengthOfColors];

          // Enhanced color assignment for missing parents
          // Use point background and border colors instead of dataLabel backgrounds for reliability
          let optimizedBackground = getOptimalTreemapBackground(
            itemColor,
            true
          );
          let optimizedDataLabelBackground = getOptimalTreemapBackground(
            itemColor,
            true,
            true // Use dataLabel background optimization
          );
          let optimizedTextColor = getOptimalTextColor(optimizedBackground);
          let optimizedBorderColor = getOptimalBorderColor(itemColor, true);
          let lighterColor = newColorShadeInHex6(itemColor, 80);

          let parentItem = {
            id: parentId,
            name: `Category ${parentId}`,
            color: optimizedBackground, // Use optimized background as point color
            value: 0, // Value will be derived from children
            isParent: true,
            custom: {
              originalColor: itemColor, // Store original color for reference
              textColor: optimizedTextColor, // Store optimal text color
            },
            dataLabels: {
              enabled: showDataLabels,
              headers: true,
              align: "center",
              verticalAlign: "top",
              useHTML: true,
              inside: true,
              className: "treemap-data-label treemap-parent-label",
              shape: "callout",
              borderWidth: 1,
              borderColor: useColorGradient ? undefined : optimizedBorderColor,
              backgroundColor: itemColor
                ? optimizedDataLabelBackground
                : undefined, // Use a lighter color for better contrast
              style: {
                fontWeight: "bold",
                fontSize: "0.9em",
                textTransform: "uppercase",
                textOutline: "none",
                textShadow: "none",
              },
              padding: 0,
            },
            borderWidth: 2,
            groupPadding: 2,
            borderRadius: 6,
            borderColor:
              useColorGradient &&
              graphObject?.chartStyles?.dataLabelColor &&
              graphObject?.chartStyles?.dataLabelColor !== "#000000"
                ? graphObject?.chartStyles?.dataLabelColor
                : dynamicHighchartTextColor(
                    authParams,
                    graphObject?.isDeepDiveView,
                    graphObject?.pdfDownloadStatus ||
                      graphObject?.pptDownloadStatus ||
                      graphObject?.isScheduleReportOpen,
                    graphObject?.isDeepDiveDashboardView
                  ),

            colorByPoint: useColorGradient ? true : false, // Disable to use our custom colors
          };
          // Only add colorValue if useColorGradient is true
          if (useColorGradient) {
            parentItem.colorValue = 0;
          }
          parentNodes.push(parentItem);
        }
      });

      // Combine all data for the treemap
      treemapData = [...parentNodes, ...childrenData];
    } else {
      // Handle flat (non-hierarchical) data by creating a proper structure for TreeMap
      // First check if we have data to work with
      if (treeMapSingleData && treeMapSingleData?.length > 0) {
        // // Create a virtual root node (invisible)
        // const rootNode = {
        //   id: "root",
        //   name: "All Data", // This will be the root level
        //   value: null, // Value will be derived from sum of children
        //   color: "transparent", // Make root invisible
        //   dataLabels: {
        //     enabled: false, // Hide root node label
        //   },
        // };

        // Create child nodes with proper parent reference
        const children = treeMapSingleData?.map((el, i) => {
          const value = el?.value || el?.y || 0;
          let itemColor = useColorGradient
            ? undefined
            : el.color && el.color !== "#000000"
            ? el.color
            : selectedColorArray[j++ % lengthOfColors];

          // Enhanced color assignment for flat data (treat as child nodes)
          // Use point background and border colors instead of dataLabel backgrounds for reliability
          let optimizedBackground = getOptimalTreemapBackground(
            itemColor,
            false
          );
          let optimizedTextColor = getOptimalTextColor(optimizedBackground);
          let optimizedBorderColor = getOptimalBorderColor(itemColor, false);

          let itemObj = {
            id: `item-${i}`,
            name: el?.name || `Item ${i + 1}`,
            // parent: "root", // Link to virtual root // No parent reference, making this a top-level node
            value: value,
            color: optimizedBackground, // Use optimized background as point color
            percentage: el?.percentage,
            percent: el?.percent,
            share: el?.share,
            // Add custom metadata for tooltip
            custom: {
              percentage: el?.percentage,
              percent: el?.percent,
              share: el?.share,
              y_series: el?.y_series || [],
              originalColor: itemColor, // Store original color for reference
              textColor: optimizedTextColor, // Store optimal text color
            },
            dataLabels: {
              enabled: showDataLabels,
              useHTML: true,
              inside: true,
              // allowOverlap: false,
              inside: true,
              align: "center",
              verticalAlign: "middle",
              padding: 0,
              className: "treemap-data-label treemap-flat-label",
              style: {
                fontWeight: "normal",
                fontSize: "0.8em",
                textOutline: "none",
                textShadow: "none",
              },
            },
            borderWidth: 1,
            borderRadius: 4,
            borderColor: optimizedBorderColor,
          };
          // Only add colorValue if useColorGradient is true
          if (useColorGradient) {
            itemObj.colorValue = value;
          }
          return itemObj;
        });

        // Combine root and children
        treemapData = [...children];
      } else {
        // Fallback for empty data
        treemapData = [];
      }
    }
  }
  // console.log("treemapData: ", treemapData);
  // Build enhanced dynamic tooltip formatter
  const tooltipFormatter = (
    graphConditionConfig,
    isFirstSeriesIsFlr,
    showDetailsOnCard
  ) =>
    function () {
      // Skip tooltip for virtual root node
      // console.log("this : ", this);
      if (this.point.id === "root") {
        return false;
      }

      // Get color from point or series
      let pointColor = this?.color
        ? this?.color?.stops?.length
          ? this?.color?.stops?.[0]?.[1]
          : this?.custom?.originalColor || this?.color
        : this?.series?.color?.stops?.length
        ? this?.series?.color?.stops?.[0]?.[1]
        : this?.custom?.originalColor || this?.series?.color;

      let pillColor = this?.dataLabel?.text?.styles?.color || "#000000";
      // Header: Node name and (optional) level/category
      const nodeName = this.point.name || "—";
      const parentNode =
        this.point.parent && this.point.parent !== "root"
          ? this.series.points.find((p) => p.id === this.point.parent)?.name ||
            this.point.parent
          : null;

      // Aggregate summary (example: total volume, percentage)
      const totalVolume =
        this.point.value !== undefined
          ? isFirstSeriesIsFlr
            ? SecondstoHumanReadable(this.point.value)
            : this?.point?.value?.toLocaleString()
          : "—";
      const percent =
        this?.point?.percent !== undefined && this?.point?.percent !== null
          ? `${this?.point?.percent?.toFixed(2)}%`
          : this?.point?.custom?.percent !== undefined
          ? `${this?.point?.custom?.percent?.toFixed(2)}%`
          : null;
      const shareOfVoice =
        this?.point?.share !== undefined && this?.point?.share !== null
          ? `${this?.point?.share?.toFixed(2)}%`
          : this?.point?.custom?.share !== undefined
          ? `${this?.point?.custom?.share?.toFixed(2)}%`
          : null;

      // Format percent change
      const percentChangeData = formatPercentChange(
        this?.point?.percent !== undefined
          ? this?.point?.percent
          : this?.point?.custom?.percent
      );
      // Details (y_series)
      let details = "";
      let ySeriesPresent = false;
      if (
        Array.isArray(this?.point?.custom?.y_series) &&
        this.point.custom.y_series.length > 0
      ) {
        ySeriesPresent = true;
        details = this.point.custom.y_series
          .map((ys) => {
            // Format each y_series entry
            let formattedValue = ys.value;
            if (graphConditionConfig?.includes(ys?.attribute)) {
              formattedValue = SecondstoHumanReadable(ys.value);
            } else {
              formattedValue = ys?.value?.toLocaleString();
            }
            // Add share if available in y_series
            let shareStr =
              ys?.share !== undefined && ys?.share !== null
                ? ` | ${ys?.share?.toFixed(2)}%`
                : "";

            // Format percent change for y_series
            const ySeriesPercentChange = formatPercentChange(ys?.percent);
            let percentChangeStr = "";
            if (ySeriesPercentChange) {
              percentChangeStr = `<span class='${ySeriesPercentChange.className}' style='font-size:0.8em;font-weight:600;'>
                ${ySeriesPercentChange.icon} ${ySeriesPercentChange.value}
              </span>`;
            }

            return (
              `<div class='hc-treemap-tooltip-detail-row'>` +
              `<span class='hc-treemap-tooltip-detail-key'>${getNameWithChannelIconIfExists(
                ys?.trace_name
              )}:</span>` +
              `<span class='hc-treemap-tooltip-detail-value'>${formattedValue}${shareStr}${percentChangeStr}</span>` +
              `</div>`
            );
          })
          .join("");
      }
      const hexToRgba = (inputColor, alpha) => {
        const regex =
          /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i;
        if (regex.test(inputColor)) {
          // It's already in rgb/rgba format
          return inputColor.replace("rgb", "rgba").replace(")", `, ${"0.6"})`);
        } else {
          const r = parseInt(inputColor.slice(1, 3), 16);
          const g = parseInt(inputColor.slice(3, 5), 16);
          const b = parseInt(inputColor.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
      };

      // Compose card: Node name and pill in same row, parent below, details scrollable
      return `
      <div class='hc-treemap-tooltip-card'>
        <div class='hc-treemap-tooltip-header-row'>
          <span class="hc-treemap-tooltip-title">${getNameWithChannelIconIfExists(
            ySeriesPresent && parentNode ? parentNode : nodeName
          )}</span>
          ${
            ySeriesPresent
              ? ""
              : `<span class="hc-treemap-tooltip-pill" style="background:${hexToRgba(
                  pointColor,
                  0.25
                )}; color:${pillColor};">${totalVolume}${
                  shareOfVoice ? ` | ${shareOfVoice}` : ""
                }</span>
                  ${
                    percentChangeData
                      ? `<div class='hc-treemap-tooltip-summary'>
                <span class='${percentChangeData.className}' style='font-weight: 600;'>
                  ${percentChangeData.icon} ${percentChangeData.value}
                </span>
              </div>`
                      : ""
                  }
                `
          }
          
        
        </div>
        ${
          ySeriesPresent
            ? `<div class='hc-treemap-tooltip-separator'></div>
        ${
          parentNode
            ? `<div class='hc-treemap-tooltip-parent'>${getNameWithChannelIconIfExists(
                ySeriesPresent && parentNode ? nodeName : parentNode
              )}</div>`
            : ""
        }
        
        <div class='hc-treemap-tooltip-section-title'>Details:</div>
        <div class="hc-treemap-tooltip-details-scroll">
        ${
          details ||
          '<div class="hc-treemap-tooltip-no-details">No details</div>'
        }`
            : ""
        }
        
        </div>
      </div>
    `;
    };

  // Configure color axis if gradient is enabled
  let colorAxis = null; // Set to null to disable colorAxis
  if (useColorGradient) {
    if (colorMode === "sentiment") {
      // For sentiment, use red-yellow-green gradient
      colorAxis = {
        minColor: "#FF5252", // Red for negative
        maxColor: "#4CAF50", // Green for positive
        stops: [
          [0, "#FF5252"], // Red (negative)
          [0.5, "#FFEB3B"], // Yellow (neutral)
          [1, "#4CAF50"], // Green (positive)
        ],
      };
    } else {
      // // For value, use consistent brand color gradient
      // const baseColor = selectedColorArray[0];
      // colorAxis = {
      //   minColor: calculateNewShadeOfColor(baseColor, 40), // Lighter
      //   maxColor: calculateNewShadeOfColor(baseColor, -40), // Darker
      // };
      colorAxis = {
        min: 0,
        maxColor:
          graphObject?.chartStyles?.endColor &&
          graphObject?.chartStyles?.endColor !== "#000000"
            ? graphObject?.chartStyles?.endColor
            : "#185edf",
        minColor:
          graphObject?.chartStyles?.startColor &&
          graphObject?.chartStyles?.startColor !== "#000000"
            ? graphObject?.chartStyles?.startColor
            : "#fa1919",
        allowDecimals: false,
        width: "70%", // Increase the color bar width
        labels: {
          style: {
            color: dynamicHighchartTextColor(
              authParams,
              graphObject?.isDeepDiveView,
              graphObject?.pdfDownloadStatus ||
                graphObject?.pptDownloadStatus ||
                graphObject?.isScheduleReportOpen,
              graphObject?.isDeepDiveDashboardView
            ),
            whiteSpace: "normal", // Allow wrapping
            overflow: "visible", // Prevent ellipsis
            textOverflow: "unset", // Prevent ellipsis
            fontSize: "0.8em",
            textAlign: "center",
          },
          reserveSpace: true, // Ensure space is reserved for labels
        },
      };
    }
  }

  // Configure final Highcharts options
  const obj = {
    chart: {
      type: "treemap",
      spacingTop: 20, // Padding at the top of the chart
      spacingRight: 20, // Padding at the right of the chart
      spacingBottom: 20, // Padding at the bottom of the chart
      spacingLeft: 20, // Padding at the left of the chart
      height: graphObject?.style?.height,
      // plotBorderWidth: 1,
      // plotBorderColor: dynamicHighchartTextColor(
      //   authParams,
      //   graphObject?.isDeepDiveView,
      //   graphObject?.pdfDownloadStatus ||
      //     graphObject?.pptDownloadStatus ||
      //     graphObject?.isScheduleReportOpen,
      //   graphObject?.isDeepDiveDashboardView
      // ),
      reflow: true,
      style: {
        fontFamily: '"Roboto", sans-serif',
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        fontSize: "1.6rem",
      },
      events: {
        load: function () {
          try {
            let chart = this;
            setTimeout(() => {
              requestAnimationFrame(function () {
                let container = chart?.renderTo;
                // Use current container dimensions to update the chart size
                chart?.setSize(null, container?.clientHeight, false);
                chart?.reflow();
              });
            }, 500);
          } catch (e) {
            console.error("Error in load event of treemap", e);
          }
        },
      },
    },
    title: {
      text: null,
      style: {
        color: dynamicHighchartTextColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
      },
    },
    series: [
      {
        type: "treemap",
        layoutAlgorithm: "squarified",
        animation: true,
        animationLimit: 1000,
        minPointSize: 60, // Minimum size in pixels for any node (try 10-20)
        allowDrillToNode: true,
        interactByLeaf: false,
        allowTraversingTree: true,
        alternateStartingDirection: true,
        allowPointSelect: true,
        cursor: "pointer",
        nodeSizeBy: "leaf",
        borderColor: dynamicHighchartOutlineColor(
          authParams,
          graphObject?.isDeepDiveView,
          graphObject?.pdfDownloadStatus ||
            graphObject?.pptDownloadStatus ||
            graphObject?.isScheduleReportOpen,
          graphObject?.isDeepDiveDashboardView
        ),
        data: treemapData,
        name: graphObject?.xaxis,
        dataLabels: {
          enabled: showDataLabels,
          // allowOverlap: true,
          useHTML: true,
          inside: true,
          align: "center",
          verticalAlign: "middle",
          style: {
            fontSize: "0.9em",
            textOutline: "none",
            color:
              useColorGradient &&
              graphObject?.chartStyles?.dataLabelColor &&
              graphObject?.chartStyles?.dataLabelColor !== "#000000"
                ? graphObject?.chartStyles?.dataLabelColor
                : dynamicHighchartTextColor(
                    authParams,
                    graphObject?.isDeepDiveView,
                    graphObject?.pdfDownloadStatus ||
                      graphObject?.pptDownloadStatus ||
                      graphObject?.isScheduleReportOpen,
                    graphObject?.isDeepDiveDashboardView
                  ),
          },
          formatter: function () {
            // Skip labels for root node
            if (!this.point || this.point.id === "root") {
              return null;
            }

            // Always show name and value for better readability
            let valueText = isFirstSeriesIsFlr
              ? (this.point.value / 60).toFixed(0) + " mins"
              : kFormatter(this.point.value || 0);
            // Add share of voice if available
            let shareOfVoiceText = null;
            if (typeof this?.point?.share === "number") {
              shareOfVoiceText = `${this?.point?.share?.toFixed(2)}%`;
            } else if (
              this?.point?.custom &&
              typeof this?.point?.custom?.share === "number"
            ) {
              shareOfVoiceText = `${this?.point?.custom?.share?.toFixed(2)}%`;
            }

            // Format percent change for data labels
            const percentChangeData = formatPercentChange(
              this?.point?.percent !== undefined
                ? this?.point?.percent
                : this?.point?.custom?.percent
            );
            // Basic label without y_series details
            let label = `<span style='font-weight:bold;'>${this?.point?.name}</span>`;
            let detailsLabelHtml = "";
            if (
              showDetailsOnCard &&
              Array.isArray(this?.point?.custom?.y_series) &&
              this?.point?.custom?.y_series.length > 0
            ) {
              // Get area to adjust how many details we show
              const area =
                this?.point?.shapeArgs?.width * this?.point?.shapeArgs?.height;
              const maxItems = area > 10000 ? 3 : area > 5000 ? 2 : 1;

              // Format each y_series item
              const detailsHtml = this?.point?.custom?.y_series
                .slice(0, maxItems) // Limit number of items based on area
                .map((ys) => {
                  // Format value according to type
                  let formattedValue = graphConditionConfig?.includes(
                    ys?.attribute
                  )
                    ? (ys.value / 60).toFixed(0) + " mins"
                    : kFormatter(ys.value || 0);
                  // Add share if available in y_series
                  let shareStr =
                    ys?.share !== undefined && ys?.share !== null
                      ? ` | ${ys?.share?.toFixed(2)}%`
                      : "";

                  // Format percent change for y_series in data labels
                  const ySeriesPercentChange = formatPercentChange(ys?.percent);
                  let percentChangeStr = "";
                  if (ySeriesPercentChange) {
                    percentChangeStr = `<span class='${ySeriesPercentChange.className}' style='font-size:0.85em;font-weight:700;'>
                      ${ySeriesPercentChange.icon} ${ySeriesPercentChange.value}
                    </span>`;
                  }

                  return `<div style="font-size:0.8em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
            ${
              ys.trace_name || ys.attribute
            }: ${formattedValue}${shareStr}${percentChangeStr}
          </div>`;
                })
                .join("");

              // Add details to label with a separator
              if (detailsHtml) {
                const borderColor = dynamicHighchartOutlineColor(
                  authParams,
                  graphObject?.isDeepDiveView,
                  graphObject?.pdfDownloadStatus ||
                    graphObject?.pptDownloadStatus ||
                    graphObject?.isScheduleReportOpen,
                  graphObject?.isDeepDiveDashboardView
                );
                detailsLabelHtml += `<hr style="margin:3px 0;border-top:1px solid ${
                  borderColor || "var(--locobuzz-border-color)"
                };">
                  ${detailsHtml}`;
              }
            }
            if (detailsLabelHtml && detailsLabelHtml?.length > 0) {
              label += detailsLabelHtml;
            } else {
              label = label + "<br>";
              label +=
                "<span style='font-size:0.8em;font-weight:normal;'>" +
                valueText;
              if (shareOfVoiceText) {
                label += ` <span>| ${shareOfVoiceText}</span>`;
              }
              label += "</span>";

              // Add percent change if available
              if (percentChangeData) {
                label += `<br><span class='${percentChangeData.className}' style='font-size:0.85em;font-weight:700;padding:2px 6px;border-radius:8px;'>
                  ${percentChangeData.icon} ${percentChangeData.value}
                </span>`;
              }
            }
            return label;
          },
        },
        states: {
          hover: {
            brightness: 0.1, // Slightly darken on hover
            opacity: 0.15,
          },
        },
        breadcrumbs: {
          buttonTheme: {
            style: {
              color: dynamicHighchartTextColor(
                authParams,
                graphObject?.isDeepDiveView,
                graphObject?.pdfDownloadStatus ||
                  graphObject?.pptDownloadStatus ||
                  graphObject?.isScheduleReportOpen,
                graphObject?.isDeepDiveDashboardView
              ),
              fontSize: "1.4rem",
              fontFamily: '"Roboto", sans-serif',
            },
            states: {
              hover: {
                fill: "var(--locobuzz-secondary-bg-color)",
                style: {
                  color: dynamicHighchartTextColor(
                    authParams,
                    graphObject?.isDeepDiveView,
                    graphObject?.pdfDownloadStatus ||
                      graphObject?.pptDownloadStatus ||
                      graphObject?.isScheduleReportOpen,
                    graphObject?.isDeepDiveDashboardView
                  ),
                },
              },
              select: {
                fill: "var(--locobuzz-secondary-bg-color)",
                style: {
                  color: dynamicHighchartTextColor(
                    authParams,
                    graphObject?.isDeepDiveView,
                    graphObject?.pdfDownloadStatus ||
                      graphObject?.pptDownloadStatus ||
                      graphObject?.isScheduleReportOpen,
                    graphObject?.isDeepDiveDashboardView
                  ),
                  fontWeight: "500",
                },
              },
            },
          },
          position: {
            align: "left",
          },
          floating: false,
          style: {
            fontSize: "1.3rem",
          },
        },
        // groupPadding: 1, // Set margin between parent nodes
        // levels: [
        //   {
        //     // Root level
        //     level: 0,
        //     layoutAlgorithm: "squarified",
        //     dataLabels: {
        //       enabled: false, // Hide root node labels
        //     },
        //     borderWidth: 0,
        //   },
        //   {
        //     // Level 1 (top-level categories)
        //     level: 1,
        //     layoutAlgorithm: "squarified",
        //     borderWidth: 3,
        //     borderColor: dynamicHighchartOutlineColor(
        //       authParams,
        //       graphObject?.isDeepDiveView,
        //       graphObject?.pdfDownloadStatus ||
        //         graphObject?.pptDownloadStatus ||
        //         graphObject?.isScheduleReportOpen,
        //       graphObject?.isDeepDiveDashboardView
        //     ),
        //     // dataLabels: {
        //     //   enabled: showDataLabels,
        //     //   useHTML: true,
        //     //   allowOverlap: false,
        //     //   headers: true,
        //     //   align: "center",
        //     //   verticalAlign: "top",
        //     //   y: 10, // push label to top
        //     //   style: {
        //     //     fontSize: "1.6rem",
        //     //     fontWeight: "bold",
        //     //   },
        //     // },
        //     colorByPoint: true, // Set to true if you want each parent to have its own color
        //     levelIsConstant: false,
        //   },
        //   {
        //     // Level 2 (mid-level categories)
        //     level: 2,
        //     layoutAlgorithm: "squarified",
        //     borderWidth: 2,
        //     borderColor: dynamicHighchartOutlineColor(
        //       authParams,
        //       graphObject?.isDeepDiveView,
        //       graphObject?.pdfDownloadStatus ||
        //         graphObject?.pptDownloadStatus ||
        //         graphObject?.isScheduleReportOpen,
        //       graphObject?.isDeepDiveDashboardView
        //     ),
        //   },
        //   {
        //     // Level 3 (leaf nodes)
        //     level: 3,
        //     layoutAlgorithm: "squarified",
        //     borderWidth: 1,
        //   },
        //   {
        //     // Add a catch-all level for drill downs
        //     level: 4,
        //     layoutAlgorithm: "squarified",
        //     borderWidth: 1,
        //   },
        // ],
        accessibility: {
          exposeAsGroupOnly: true,
        },
      },
    ],
    // Global dataLabels settings (series-specific settings take precedence)
    // dataLabels: {
    //   enabled: showDataLabels,
    //   useHTML: true,
    //   align: "center",
    //   verticalAlign: "middle",
    //   format: "{point.name}",
    //   style: {
    //     textOutline: "none",
    //     fontSize: "0.8em",
    //     fontWeight: "normal",
    //     color:
    //       graphObject?.chartStyles?.dataLabelColor &&
    //       graphObject?.chartStyles?.dataLabelColor !== "#000000"
    //         ? graphObject?.chartStyles?.dataLabelColor
    //         : dynamicHighchartTextColor(
    //             authParams,
    //             graphObject?.isDeepDiveView,
    //             graphObject?.pdfDownloadStatus ||
    //               graphObject?.pptDownloadStatus ||
    //               graphObject?.isScheduleReportOpen,
    //             graphObject?.isDeepDiveDashboardView
    //           ),
    //   },
    // },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderWidth: 0,
      shadow: false,
      followPointer: true,
      outside: true,
      backgroundColor: "transparent",
      formatter: tooltipFormatter(
        graphConditionConfig,
        isFirstSeriesIsFlr,
        showDetailsOnCard
      ),
      style: {
        zIndex: 10000, // Set a higher z-index for the tooltip
      },
    },
    credits: { enabled: false },
    exporting: {
      chartOptions: {
        chart: {
          width: 800,
          height: 600,
        },
      },
      filename: graphObject?.widget_name
        ? graphObject?.widget_name
        : graphObject?.type,
      enabled:
        graphObject?.pdfDownloadStatus ||
        graphObject?.pptDownloadStatus ||
        graphObject?.isScheduleReportOpen ||
        authParams?.screen_id
          ? false
          : true,
      menuItemDefinitions: {
        xsl: {
          onclick: () => {
            const excel = new Excel();
            let columns = [
              { title: "Name", dataIndex: "name" },
              { title: "Value", dataIndex: "value" },
              { title: "Category", dataIndex: "parent" },
              { title: "Share of Voice (%)", dataIndex: "share" },
              { title: "Percent Change (%)", dataIndex: "percent" },
            ];

            // Add columns for custom attributes if they exist
            const hasCustomAttrs = treemapData.some((item) => item.custom);
            if (hasCustomAttrs) {
              if (
                treemapData.some((item) => item.custom?.share !== undefined)
              ) {
                columns.push({
                  title: "Share of Voice (%)",
                  dataIndex: "share",
                });
              }
              if (
                treemapData.some((item) => item.custom?.percent !== undefined)
              ) {
                columns.push({
                  title: "Percent Change (%)",
                  dataIndex: "percent",
                });
              }
            }

            // Create data source for Excel export
            let datasource = treemapData
              // Filter out virtual root node in case of flat data
              .filter((item) => item.id !== "root")
              .map((item) => {
                let row = {
                  name: item.name,
                  value: item.value,
                  // For flat data with virtual root node, use "Root" as parent label
                  parent:
                    item.parent === "root" ? "Root" : item.parent || "Root",
                };

                // Add share of voice if available
                if (item.share !== undefined) {
                  row.share = item.share;
                }

                // Add percent change if available
                if (item.percent !== undefined) {
                  row.percent = item.percent;
                }

                // Add custom attributes
                if (item.custom) {
                  if (item.custom.share !== undefined) {
                    row.share = item.custom.share;
                  }
                  if (item.custom.percent !== undefined) {
                    row.percent = item.custom.percent;
                  }
                }
                return row;
              });

            excel
              .addSheet("TreeMap Data")
              .addColumns(columns)
              .addDataSource(datasource, {
                str2Percent: true,
              })
              .saveAs(
                `${
                  graphObject?.widget_name
                    ? graphObject?.widget_name
                    : "treemap"
                }.xlsx`
              );
          },
          text: "Download excel sheet",
        },
      },
      buttons: {
        contextButton: {
          menuItems: [
            "downloadPNG",
            "downloadJPEG",
            "downloadSVG",
            // "separator",
            // "xsl",
          ],
          buttonSpacing: 1,
          height: 20,
          symbolSize: 12,
          symbolStrokeWidth: 2,
          symbolX: 10.5,
          symbolY: 10.5,
          width: 20,
          x: 20,
          y: -12,
        },
      },
      chartOptions: {
        chart: {
          width: 800,
          height: 600,
          style: {
            fontSize: "10px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        tooltip: {
          style: {
            fontSize: "10px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
        plotOptions: {
          treemap: {
            dataLabels: {
              enabled: showDataLabels,
              useHTML: true,
              inside: true,
              allowOverlap: false, // Also prevent overlap in export
              style: {
                fontSize: "10px",
                fontFamily: '"Roboto", sans-serif',
                fontWeight: "bold",
                color: "#ffffff",
              },
            },
          },
        },
        legend: {
          itemStyle: {
            fontSize: "13px",
            fontFamily: '"Roboto", sans-serif',
          },
        },
      },
    },
  };
  // Only add colorAxis if useColorGradient is true and colorAxis is defined
  if (useColorGradient && colorAxis) {
    obj.colorAxis = colorAxis;
  }
  // console.log("getTreeMap return obj : ", obj);
  return obj;
};
